import { AfterViewInit, Component, effect, ElementRef, HostListener, inject, Injector, OnDestroy, OnInit, TemplateRef, untracked, ViewChild } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { DndDropEvent } from 'ngx-drag-drop';
import Konva from 'konva';
import { tinykeys } from 'tinykeys';
import moment from 'moment';
import * as jsts from 'jsts/dist/jsts';
import { BehaviorSubject, from, lastValueFrom, map, Observable, Subject, Subscription, timer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// models
import { Alignment, AlignmentTypes } from '../models/alignment';
import { Block } from '../models/block';
import { BoxElement } from '../models/elements/boxElement';
import { CanvasFlipMode } from '../../core/models/canvasMode';
import { ComponentCanDeactivate } from '../../core/models/canDeactivate';
import { CopyingData } from '../../core/models/copyingData';
import { CopyingNotification } from '../../core/models/copyingNotification';
import { DwUserActivityNames } from '../../core/models/dwUserActivityNames';
import { DwUserActivityTypes } from '../../core/models/dwUsersActivityTypes';
import { Element } from '../../equipment/models/elements/element';
import { EMPTY_PRODUCT_IMAGE_PATH, ProductsService } from '../../core/services/products.service';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { GroupByCategory } from './components/layout-rules/models/group-base';
import { Figure } from '../../equipment/models/figures/figure';
import { FunctionalType } from '../../core/models/blockedFunctionalType';
import { GoldenShelf } from '../../core/models/goldenShelf';
import { HangBarElement } from '../models/elements/hangBarElement';
import { HighlightOptionsValue, HighlightProductsService } from '../../core/services/highlight-products.service';
import { HistoryInstance } from '../models/history-instance';
import { IAutoLayoutParams, IReportParams } from './components/autolayout/auto-layout-panel.component';
import { ICanPutProductsResult } from '../models/canPutProductsResult';
import { IChatUser, LayoutService } from '../../core/services/layout.service';
import { IColumnType, ITableData, ITableRowClickData } from '../../core/models/table';
import { IConfirmModalAnswer } from '../../core/modals/confirm-modal/models/confirmModalAnswer';
import { IConfirmModalData } from '../../core/modals/confirm-modal/models/confirmModalData';
import { IInfoModalData } from '../../core/modals/info-modal/models/infoModalData';
import { CanvasUpdateRequest, ILabelsState, NORMALIZED_ELEMENTS, PlanogramActivity } from '../services/planogram-activity.service';
import { ILayoutConfigFormParams } from './components/layout-config/layout-config.component';
import { elementsGenerator } from '../../equipment/models/elements/elementGenerator';
import { EquipmentBase } from '../models/equipment-base';
import { Extension, ExtensionType, RailsParams } from '../../equipment/models/elements/extension';
import { IEquipment, IEquipmentConfigurations } from '../../equipment/models/models';
import { IOnboardingModalData } from '../../core/models/onBoardingModalData';
import { ICompressionParams, IProductCard, IProductConf } from '../../product/models/models';
import { IPlanogram } from '../models/planogram';
import { IPlanogramData, IPlanogramPlanData } from '../models/planogramData';
import { IProductPacking } from '../../product/models/product-card';
import { IProductsTableItem } from './models/productsTableItem';
import { IStandConversationDone, IStandConversationImage, IStandConversationMessage, PlanogramsService, StandConf } from '../../core/services/planograms.service';
import { konvaSettings } from '../../equipment/models/konvaSettings';
import { Label } from '../../plan/models/label';
import { LayoutRule } from './components/layout-rules/models/layoutRule';
import { MeasurementSystem } from '../../core/models/measurementSystem';
import { IExecuteEvent } from '../../core/components/multi-execution/models/executeEvent';
import { MixpanelEvents } from '../../core/models/mixpanelEvents';
import { PlanogramViewBehavior } from '../models/planogramViewBehavior';
import { PlanogramViewBehaviorFactory } from '../models/planogramViewBehaviorFactory';
import { PlatformApplications } from '../../core/models/platformApplications';
import { Point } from '../../core/models/point';
import { POSTextMaterial } from '../models/POSMaterials/POSTextMaterial';
import { POSMaterial } from '../models/POSMaterials/POSMaterial';
import { Product } from '../models/product';
import { CompressionType, ProductErrors, ProductGroup } from '../models/product-group';
import { PersonalSettings, SearchAlgorithmType } from '../../core/models/personalSettings';
import { ProductGroupRotation } from '../models/product-group-rotation';
import { Range } from '../../core/models/range';
import { Rectangle } from '../../equipment/models/figures/rectangle';
import { RuleLevel } from './components/layout-rules/models/rule-level';
import { ServiceExtensions } from '../../core/models/serviceExtensions';
import { ServiceFeatures } from '../../core/models/serviceFeatures';
import { ShelfElement } from '../models/elements/shelfElement';
import { Side } from '../../core/models/side';
import { SocketGroup, SocketService } from '../../core/services/socket.service';
import { SortParams } from './components/brand-info-panel/brand-info-panel.component';
import { Wall } from '../../plan/models/wall';
import { IUserSave, UsersActivityData } from '../../core/models/usersActivityData';
import { UnitConverter } from '../../core/utils/unitConverter';
import { MeasurementUnitPipe } from '../../core/pipes/measurement-unit.pipe';
import { MeasurementUnitTranslatePipe } from '../../core/pipes/measurement-unit-translate.pipe';
import { Bounding } from '../../core/models/bounding';
import { DefaultProductParams } from '../../core/models/defaultProductParams';
import { POSImageMaterial, POSMaterialImageSaveObject } from '../models/POSMaterials/POSImageMaterial';

// components
import { ConfirmModalComponent } from '../../core/modals/confirm-modal/confirm-modal.component';
import { CopyPlanogramModalComponent } from '../modals/copy-planogram-modal/copy-planogram-modal.component';
import { ExcelToLayoutComponent } from './modals/excel-to-layout/excel-to-layout.component';
import { EquipmentCreateNameModalComponent } from '../../equipment/modals/equipment-create-name-modal/equipment-create-name-modal.component';
import { EquipmentModelComponent } from '../../core/components/equipment-model/equipment-model.component';
import { InfoModalComponent } from '../../core/modals/info-modal/info-modal.component';
import { LayoutHistoryComponent } from '../../core/components/layout-history/layout-history.component';
import { OnboardingModalComponent } from '../../core/modals/onboarding-modal/onboarding-modal.component';
import { PhSimpleTableComponent } from '../../core/components/ph-simple-table/ph-simple-table.component';
import { ProductsAddExcelModalComponent } from './modals/products-add-excel-modal/products-add-excel-modal.component';

// services
import { AddProductsService } from '../../core/services/add-products.service';
import { BlockedFunctionalService } from '../../core/services/blocked-functional.service';
import { CanvasDrawerService } from '../../core/services/canvas-drawer.service';
import { DragOutsideService } from '../../core/services/drag-outside.service';
import { DwUsersActivityService } from '../../core/services/dw-users-activity.service';
import { EquipmentService } from '../../core/services/equipment.service';
import { FiltersService } from '../../core/services/filters.service';
import { LayoutRulesService } from '../../core/services/layout-rules.service';
import { MixpanelService } from '../../core/services/mixpanel.service';
import { PlanogramLayoutService } from '../services/planogram-layout.service';
import { PlanogramTooltipService } from '../services/planogram-tooltip.service';
import { PlansService } from '../../core/services/plans.service';
import { ProductCardService } from '../../core/services/product-card.service';
import { RulesService } from './components/layout-rules/layout-rules.service';
import { SessionService } from '../../core/services/session.service';
import { SidePanelService } from '../../core/services/side-panel.service';
import { SnackBarService } from '../../core/services/snack-bar.service';
import { Utils } from '../../core/services/utils.service';
import { POSMaterialsService } from '../services/pos-materials.service';
import { POSImageMaterialConfigData } from './components/pos-image-material-config/pos-image-material-config.component';
import { GlobalSettings } from '../../core/models/globalSettings';
import { IPlanogramBounds } from './models/planogramBounds';
import { NotificationsService } from '../../core/services/notifications.service';
import { DetailedElementWidgetOverlayService } from '../services/detailed-element-widget-overlay.service';
import { PlanogramModes } from '../services/planogram-modes.service';
import { HoleElement } from '../models/elements/holeElement';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HookElement } from '../models/elements/hookElement';
import { PalletElement } from '../models/elements/palletElement';

const canvasOffset: number = konvaSettings.canvasOffset,
	Coordinate = jsts.geom.Coordinate,
	GeometryFactory = jsts.geom.GeometryFactory,
	geometryFactory = new GeometryFactory(),
	EPS_3 = 1e-3,
	choices = [0, 1],
	RENDER_DEBOUNCE_TIME = 1e3,
	LEFT_PANEL_DEFAULT_WIDTH_PX: number = 550,
	PRODUCTS_TABLE_PAGE_SIZE: number = 30,
	BACK_WALLS_LIST: Array<EquipmentElements> = [
		EquipmentElements.BackWall,
		EquipmentElements.CentralPillar,
		EquipmentElements.HookWall,
		EquipmentElements.PerforatedWall,
		EquipmentElements.Showcase
	],
	DRAGGABLE_ELEMENTS: { front: Array<EquipmentElements>, top: Array<EquipmentElements> } = {
		front: [
			//EquipmentElements.CentralPillar,
			EquipmentElements.Shelf,
			EquipmentElements.AngleShelf,
			EquipmentElements.HangBar,
			EquipmentElements.HookPanel,
			EquipmentElements.Hook,
			EquipmentElements.Perforation
		],
		top: [
			//EquipmentElements.CentralPillar,
			EquipmentElements.Box,
			EquipmentElements.SlicedBox,
			EquipmentElements.Partition
		]
	},
	HIGHLIGHT_LEGEND: Array<HighlightOptionsValue> = [
		HighlightOptionsValue.Categories,
		HighlightOptionsValue.Brands,
		HighlightOptionsValue.Suppliers,
		HighlightOptionsValue.ActiveProducts,
		HighlightOptionsValue.InactiveProducts,
		HighlightOptionsValue.Status,
		HighlightOptionsValue.Format
	];

interface AutoLayoutParams {
	stands: any[];
	blocks: Block[];
	productOptions: IAutoLayoutParams;
	percentDictionary: any;
	hierarchy: any[];
	cachedPlacePredict: any;
	dataGrouped?: boolean;
	groupProperty?: string;
	plannedBrands?: string[];
	plannedBrandsPercent?: any;
	plannedCategories?: string[];
	plannedCategoriesPercent?: any;
	hasReceipts: boolean;
	personalSettings: PersonalSettings;
}

@Component({
    selector: 'app-planogram-edit',
    templateUrl: './planogram-edit.component.html',
    styleUrls: ['./planogram-edit.component.scss'],
    host: {
        '[class.execution-page-active]': 'showExecutionPage'
    },
    providers: [HighlightProductsService, RulesService, SidePanelService, ProductCardService, PlanogramActivity, PlanogramModes, CanvasDrawerService, DragOutsideService, DetailedElementWidgetOverlayService],
    animations: [
        trigger('productListState', [
            state('inactive', style({ height: '100%' })),
            state('active', style({ height: 'calc(100% - 120px)' })),
            transition('inactive => active', animate('300ms')),
            transition('active => inactive', animate('300ms'))
        ]),
        trigger('productSelectState', [
            state('inactive', style({ left: '-300px' })),
            state('active', style({ left: '231px' })),
            transition('inactive => active', animate('300ms')),
            transition('active => inactive', animate('300ms'))
        ])
    ],
    standalone: false
})
export class PlanogramEditComponent implements OnInit, OnDestroy, AfterViewInit, ComponentCanDeactivate {

	@ViewChild('canvasContainer') canvasContainer: ElementRef;
	@ViewChild('mainCanvas') mainCanvas: ElementRef;
	@ViewChild('productPreview') productPreview: ElementRef;
	@ViewChild('standLayoutWorkplace') standLayoutWorkplace: ElementRef;
	@ViewChild('equipmentModel') equipmentModel: EquipmentModelComponent;
	@ViewChild('saveTemplate') saveTemplate: EquipmentModelComponent;
	@ViewChild('layoutHistoryComponent') layoutHistoryComponent: LayoutHistoryComponent;
	@ViewChild('modelContainer') container3d: ElementRef;
	@ViewChild('extendedProductErrors') extendedProductErrors: TemplateRef<any>;
	@ViewChild('detailedOverlayHost') detailedOverlayHost: ElementRef;

	hasAssortment: boolean = false;
	hasProducts: boolean = false;
	scrollToMessage: number;
	mainStage: Konva.Stage;
	planogramLayer: Konva.Layer = new Konva.Layer();
	blocksLayer: Konva.Layer = new Konva.Layer();
	tooltipsLayer: Konva.Layer = new Konva.Layer();
	sizesLayer: Konva.Layer = new Konva.Layer();
	toolsLayer: Konva.Layer = new Konva.Layer();

	rect: Konva.Rect = new Konva.Rect({
		x: 0,
		y: 0,
		width: 0,
		height: 0,
		stroke: '#ff5a00',
		dash: [2, 2]
	});
	blockWidthLabel: Konva.Label = new Konva.Label();
	blockHeightLabel: Konva.Label = new Konva.Label();
	blockPercentLabel: Konva.Label = new Konva.Label();
	stands: Array<IPlanogram> = [];
	socketSubscriptions: Array<Subscription> = [];
	selectedStand: any = -1;
	selectedPlanogram: any = -1;
	blockAutolayout: boolean = false;
	isBlockedReversed: boolean = false;
	isRenderingReversed: boolean = false;
	equipmentUsage: Array<number> = [];

	multiSelectConfiguration: {
		begin?: Point;
		end?: Point;
		drawing: boolean;
		ctrlKey: boolean;
	} = { drawing: false, ctrlKey: false };

	planAccess: string = '';
	standName: string = 'Stand name';
	standScheduledDate: string = '';
	standId: number;
	standUpdatedAt: string;
	planogramId: number;
	planogramActive: boolean;

	standsList: Array<{ id: number, name: string }> = [];

	chatMessages: Array<IStandConversationMessage> = [];
	chatUsers: Array<IChatUser> = [];
	changedProducts: any;

	products: Product[] = []; // список продуктів в лівій панелі
	productAddStatus = false;

	beginState: HistoryInstance; // стан планограми для порівняння при збереженні

	blockBuildConfig: {
		building: boolean;
		begin?: any;
		event?: MouseEvent;
		scale?: any;
		beginX?: any;
		beginY?: any;
		base?: any;
	} = { building: false };

	planogramFilters = [];
	standFilters = [];
	public modes: PlanogramModes = inject(PlanogramModes);
	flipMode: CanvasFlipMode;
	resizeMode: boolean = false;
	changeWidth: boolean = true;
	allowedAutoLayout: boolean = false;

	zoom: number = 100; // масштаб;
	isManager: boolean = false;

	history: Array<HistoryInstance> = []; // масив з історією змін планограми
	historyIndex: number = 0; // активний елемент історії
	historyApplying: boolean = false;

	selectedSide: Side;

	brandsInfo: any[] = [];
	brandsInfoCopy: any[] = [];
	categoriesInfo: any[] = [];
	categoriesInfoCopy: any[] = [];

	reportParams: IReportParams = { turnover: 0.5, qty: 0.2, receipts_qty: 0.2, profit: 0.1 };
	saveAble: boolean = false;
    ignoreOtherSave: boolean = true; // false if the planogram has been saved and the user has not ignored it
    ignoreOtherEquipmentSave: boolean = true; // false if the equipment has been saved and the user has not ignored it
	savePending: boolean = false;
	standError: boolean = false;
	loader: boolean = false;
	modelMode: boolean = false;
	layoutRules: boolean = false;
	transparentBoxesMode: boolean = false;
	POSMaterialsMode: boolean = true;
	hasEquipmentBoxes: boolean = false;
	next_messages: number = 1;
	scale: number;
	isNeedApplyChanges: boolean = false;

	POSMaterialsClipboard: Array<POSMaterial> = [];
	productClipboard: ProductGroup[] = [];
	elementToDetail: Element = null;

	saveTimeout: any = null;
	saveObserver: Observable<boolean>;
	pingInterval = null;
	cachedReportData: any = {};
	cachedReportParams: any = {};
	langChangeSubscribtion: any;
	cachedAutolayoutParams: IAutoLayoutParams = {
		groupBy: 'none',
		sortBy: 'none',
		sortType: 'asc',
		maxFacesQty: null,
		checkSizes: true,
		useMasterPositions: true,
		useSales: false,
		hasBrands: true,
		hasSuppliers: true
	};
	autoLayoutExtraMapper: any = {
		brand: 'category',
		category: 'brand',
		supplier: 'brand',
		none: 'brand'
	};
	static autoLayoutGroupMapper: any = {
		category: 'categoriesUniqueId',
		brand: 'brandsUniqueId',
		supplier: 'suppliersUniqueId'
	};
	autoLayoutHierachy: any = {
		1: 'brand',
		2: 'supplier',
		3: 'category'
	};
	cachedPlacePredict: any = {};
	canvasRendering: boolean = false;
	layoutOpen: boolean = false;
	analysisOpen: boolean = false;
	productsAnalytic: any = [];
	panelLoader: boolean = false;
	productSortOptions: Array<{ name: string; by: string; type: 'asc' | 'desc' }>;
	planogramConf: any = {
		name: 'Planogram name'
	};
	planogramStores: Array<number> = []; // all stores where the planogram is placed
	sameVersion: boolean = true;
	userIsStoreManager: boolean = true;
	showExecutionPage: boolean = false;
	executionParams: { ids: string | number } = { ids: '' };
	executionPlanogram: { id: string | number, name: string } = null;
	brandsRecommendedPercent: any = {};
	categoriesRecommendedPercent: any = {};
	brandsPanelDataGrouped: boolean = false;
	brandsPanelSorting: SortParams = null;
	heigthOneProduct: number = 29; // висота одного товара
	error_3d: boolean = false;
	placePredictCache: any = {};
	highlightSubject: Subject<void> = new Subject<void>();
	productsStock: any = {};
	abcSubscription: Subscription = Subscription.EMPTY;
	abcData;
	forceUpdateCanvas: boolean = false;
	moveInHistory: boolean = false;
	needCanvasResizeAfterKeyboardMove: boolean = false;
	pausedInterval: boolean = false;
	productCache = [];
	blockHistoryButtons: boolean;
	deletedAllProducts: boolean;
	addProductThroughExcel: boolean = false;
	activeRule: any;
	minBlockSize: number;

	labelsDate: any = {
		date_to: this.filtersService.filters.date_to || moment(this.sessionService.defaults.date_to),
		date_from: this.filtersService.filters.date_from || moment(this.sessionService.defaults.date_to).subtract(29, 'day')
	};
	renderBehavior: BehaviorSubject<any> = new BehaviorSubject(null);
	translateSubscription: Subscription;
	tinyKeysSubscription: any;
	showRotateCursor: boolean = false;
	minStep: number;
	minShiftStep: number;

	// products panel block
	categoriesLevels: number = 0;
	clickProductsTableSubject: Subject<ITableRowClickData<IProductsTableItem>> =
		new Subject<ITableRowClickData<IProductsTableItem>>();
	filterProductTypes: Array<{ value: string, translate: string }> = [];
	searchQuery: string = '';
	selectedProductFilter: string = 'all';
	sortingProductsTableSubject: Subject<string> = new Subject<string>();
	sortProductsParams = { order: true, property: '' };
	startDraggingProductsSubject: Subject<Array<Product>> = new Subject<Array<Product>>();
	panelProducts: Array<Product>;
	panelProductsItems: Array<IProductsTableItem>;
	productsGrouping: string = '';
	productsGroupingAvailable: boolean = true;
	productsTableData: ITableData<IProductsTableItem>;
	productsTablePage: number = 1;
	productsTableSubscriptions: Array<Subscription> = [];
	updateProductsTableSubject: Subject<void> = new Subject<void>();
    usersActivitySubject: BehaviorSubject<UsersActivityData>;
	@ViewChild('productsTable') productsTable: PhSimpleTableComponent;

	// resize left panel block
	private isResizing: boolean = false;
	private startX: number = 0;
	private startWidth: number = 0;
	leftPanelSavedWidth: number = LEFT_PANEL_DEFAULT_WIDTH_PX;
	@ViewChild('leftResizablePanel') leftResizablePanel: ElementRef<HTMLDivElement>;

	protected readonly extensions: typeof ServiceExtensions = ServiceExtensions;
	protected readonly features: typeof ServiceFeatures = ServiceFeatures;
	protected highlightLegend: Array<HighlightOptionsValue> = HIGHLIGHT_LEGEND;

	static getNextPage(str) {
		return str ? (str.match(/page=(\d+)/i) || [])[1] || null : null;
	}

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private translate: TranslateService,
		private injector: Injector,
		private overlay: Overlay,
		private planogramEditTooltipService: PlanogramTooltipService,
		private addProductsFilterService: AddProductsService,
		private blockedFunctionalService: BlockedFunctionalService,
		private canvasDrawerService: CanvasDrawerService,
		private dragOutsideService: DragOutsideService,
		private equipmentService: EquipmentService,
		private filtersService: FiltersService,
		private highlightProductsService: HighlightProductsService,
		private layoutRulesService: LayoutRulesService,
		private layoutService: LayoutService,
		private notificationsService: NotificationsService,
		private mixPanelService: MixpanelService,
		private planogramLayoutService: PlanogramLayoutService,
		private planogramsService: PlanogramsService,
		private plansService: PlansService,
		private productsService: ProductsService,
		private productCardService: ProductCardService,
		private rulesService: RulesService,
		private sessionService: SessionService,
		private snackBarService: SnackBarService,
		private socketService: SocketService,
		private dwUsersActivityService: DwUsersActivityService,
		private measurementUnitPipe: MeasurementUnitPipe,
		private measurementUnitTranslatePipe: MeasurementUnitTranslatePipe,
		private POSMaterialsService: POSMaterialsService,
		private detailedElementWidgetOverlayService: DetailedElementWidgetOverlayService,
		public activity: PlanogramActivity,
	) {
		this.activity.setEditMode(true);
		this.hasAssortment = this.sessionService.defaults.has_assortment;
		this.scrollToMessage = this.route.snapshot.queryParams.message;
		this.modes.setArticleMode(!this.sessionService.personalSettings.planogramImagesMode);
		this.modes.setSequenceNumberMode(this.sessionService.personalSettings.sequenceNumberMode);
		this.transparentBoxesMode = this.sessionService.personalSettings.transparentBoxesMode;
		this.flipMode = new CanvasFlipMode(false, false);

		this.filterProductTypes.push(
			{ value: 'all', translate: 'ALL' },
			{ value: 'laid_out', translate: 'LAID_OUT' },
			{ value: 'not_laid_out', translate: 'NOT_LAID_OUT' }
		);

		if (this.sessionService.hasFeature(ServiceFeatures.AssortmentTypes)) {
			this.filterProductTypes.push(
				{ value: 'active', translate: 'COMMON.ASSORTMENT.ACTIVE_MANY' },
				{ value: 'inactive', translate: 'COMMON.ASSORTMENT.INACTIVE_MANY' }
			);
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductStock)) {
			this.filterProductTypes.push(
				{ value: 'stock_qty', translate: 'WITH_STOCK' },
				{ value: 'not_stock_qty', translate: 'WITHOUT_STOCK' }
			);
		}

		this.productSortOptions = [
			{ name: 'SIZE_ASC', by: 'Height', type: 'asc' },
			{ name: 'SIZE_DESC', by: 'Height', type: 'desc' }
		];

		if (this.sessionService.hasFeature(ServiceFeatures.ProductPrice)) {
			this.productSortOptions.push(
				{ name: 'PRICE_ASC', by: 'avgPrice', type: 'asc' },
				{ name: 'PRICE_DESC', by: 'avgPrice', type: 'desc' }
			);
		}

		this.productsGroupingAvailable = this.sessionService.hasFeature(ServiceFeatures.Brands)
			|| this.sessionService.hasFeature(ServiceFeatures.Categories);

		const elementMoveSteps: Map<MeasurementSystem, number> = new Map<MeasurementSystem, number>([
			[MeasurementSystem.Metrics, 0.001],
			[MeasurementSystem.Imperial, UnitConverter.getSaveConvertValue(0.01)]
		]);
		const elementShiftMoveSteps: Map<MeasurementSystem, number> = new Map<MeasurementSystem, number>([
			[MeasurementSystem.Metrics, 0.025],
			[MeasurementSystem.Imperial, UnitConverter.getSaveConvertValue(0.25)]
		]);
		this.minStep = elementMoveSteps.get(this.sessionService.defaults.measurement_system);
		this.minShiftStep = elementShiftMoveSteps.get(this.sessionService.defaults.measurement_system);
		this.minBlockSize = UnitConverter.convertAndRoundToSaveValue(0.05);
		
		this.renderBehavior
			.pipe(debounceTime(RENDER_DEBOUNCE_TIME))
			.subscribe((data) => {
				if (!data) return;

				this.stands[0].stand = data.stand;
				this.activity.planogramUpdate$.next(this.stands[0]);

				if (this.activeRule?.hash != data.rule.hash || !this.layoutRules) {
					this.activeRule = data.rule;
					this.historyGenerate();
				} else if (this.activity.highlightActive()) {
					this.highlightSubject.next();
				}

				this.activity.requestCanvasUpdateAsync().finally(() => {
					if (this.activity.activeBlocks().length) {
						const block = this.stands[0].stand.blocks
							.find((block: Block) => block.localId == this.activity.activeBlocks()[0].localId);
						this.activity.setBlockActive(block);
					}

					if (this.activity.selectedBlock()) {
						const block = this.stands[0].stand.blocks
							.find((block: Block) => block.localId == this.activity.selectedBlock().localId);
						this.activity.setSelectedBlock(block);
					}

					if (this.activity.activeProducts().length) this.activity.setProductActive();

					this.changeCanvasListening(true);
					this.buildProductsTable(false);
				});
			});

		this.tinyKeysSubscription = tinykeys(window, {
			'$mod+KeyZ': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();

				return this.historyBack();
			},
			'$mod+Shift+KeyZ': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();

				return this.historyForward();
			},
			'$mod+KeyS': (event: KeyboardEvent) => {
				event.preventDefault();

				if (!this.modes.equipmentEditMode()) {
					this.saveAction().finally();
				} else {
					this.saveNewEquipment().finally();
				}
			},
			'$mod+KeyA': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				event.preventDefault();

				this.selectAllStandProducts();
			},
			'$mod+KeyC': (event: KeyboardEvent) => {
				const frontView: boolean = event.target['className'].includes('canvas-container');
				const topView: boolean = event.target['nodeName'] == 'DETAILED-ELEMENT-WIDGET';

				if (!frontView && !topView) return;

				this.productClipboard = [...this.activity.activeProducts()];
				this.POSMaterialsClipboard = [...this.activity.activePosMaterials()];
			},
			'$mod+KeyV': (event: KeyboardEvent) => {
				const frontView: boolean = event.target['className'].includes('canvas-container');
				const topView: boolean = event.target['nodeName'] == 'DETAILED-ELEMENT-WIDGET';

				if (!frontView && !topView) return;

				this.pasteProducts();
				this.pastePOSMaterials();
			},
			'ArrowLeft': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				event.preventDefault();

				this.keyLeftEvent(this.minStep);
				this.moveProducts(-this.minStep, 0);
				this.movePOSMaterials(-this.minStep, 0);
			},
			'Shift+ArrowLeft': (event: KeyboardEvent) => {
				event.preventDefault();

				this.movePOSMaterials(-this.minShiftStep, 0);

				if (this.canvasRendering) return;

				if (this.stands[0].stand.getElementById(this.activity.focusedCanvas().element)?.is(HookElement)) {
					this.updateDetailedProductGroup('placementDepth', true);
				} else {
					this.updateProductGroup('FacesQty');
				}

				this.keyLeftEvent(this.minShiftStep);
			},
			'ArrowRight': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				event.preventDefault();

				this.keyRightEvent(this.minStep);
				this.moveProducts(this.minStep, 0);
				this.movePOSMaterials(this.minStep, 0);
			},
			'Shift+ArrowRight': (event: KeyboardEvent) => {
				event.preventDefault();

				this.movePOSMaterials(this.minShiftStep, 0);

				if (this.canvasRendering) return;

				if (this.stands[0].stand.getElementById(this.activity.focusedCanvas().element)?.is(HookElement)) {
					this.updateDetailedProductGroup('placementDepth');
				} else {
					this.updateProductGroup('FacesQty', true);
				}

				this.keyRightEvent(this.minShiftStep);
			},
			'ArrowUp': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				event.preventDefault();

				this.keyUpEvent(this.minStep);
				this.moveProducts(0, -this.minStep);
				this.movePOSMaterials(0, -this.minStep);
			},
			'Shift+ArrowUp': (event: KeyboardEvent) => {
				event.preventDefault();

				this.movePOSMaterials(0, -this.minShiftStep);

				if (this.canvasRendering) return;

				if (this.stands[0].stand.getElementById(this.activity.focusedCanvas().element)?.is(ShelfElement, PalletElement)) {
					this.updateDetailedProductGroup('placementDepth', true);
				} else if (!this.activity.focusedCanvas().element) {
					this.updateProductGroup('placementHeight', true);
				}

				this.keyUpEvent(this.minShiftStep);
			},
			'ArrowDown': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				event.preventDefault();

				this.keyDownEvent(this.minStep);
				this.moveProducts(0, this.minStep);
				this.movePOSMaterials(0, this.minStep);
			},
			'Shift+ArrowDown': (event: KeyboardEvent) => {
				event.preventDefault();

				this.movePOSMaterials(0, this.minShiftStep);

				if (this.canvasRendering) return;

				if (this.stands[0].stand.getElementById(this.activity.focusedCanvas().element)?.is(ShelfElement, PalletElement)) {
					this.updateDetailedProductGroup('placementDepth');
				} else if (!this.activity.focusedCanvas().element) {
					this.updateProductGroup('placementHeight');
				}

				this.keyDownEvent(this.minShiftStep);
			},
			'$mod+Alt+KeyL': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.setAlignment('left');
			},
			'$mod+Alt+KeyR': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.setAlignment('right');
			},
			'$mod+Alt+KeyW': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.setAlignment(null);
			},
			'$mod+Alt+KeyJ': (event: KeyboardEvent) => {
				event.preventDefault();

				return this.setAlignment('stretch');
			},
			'Escape': (event: KeyboardEvent) => {
				event.preventDefault();

				if (this.blockBuildConfig.building) return;

				if (this.activity.selectedBlock()) {
					if (this.activity.activeProducts().length) this.activity.setProductActive();
					this.activity.clearSelectedBlock();
					this.rebuildProductsTable();
					return this.canvasRender();
				}

				if (this.modes.drawBlockMode()) {
					return this.changeDrawBlockMode();
				}

				if (this.activity.targetElement()) this.activity.clearTargetElement();

				if (this.activity.selectedElement()) this.activity.clearSelectedElement();

				if (this.activity.activeBlocks().length) {
					this.activity.closePanel();
					this.activity.setBlockActive();
					this.rebuildProductsTable();
					return this.canvasRender();
				}

				if (this.activity.activePosMaterials().length) {
					this.activity.closePanel();
					this.activity.setPOSMaterialActive();
					return this.canvasRender();
				}

				if (this.activity.panelOpened()) {
					return;
				}

				this.activity.clearActivePanelProducts();
				this.activity.setProductActive();
				return this.canvasRender();
			},
			'Delete': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				return this.deleteItems();
			},
			'Shift+Delete': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;

				return this.deleteBlockWithoutProducts();
			},
			'Alt+Delete': (event: KeyboardEvent) => {
				if (['INPUT', 'TEXTAREA'].includes(event.target['nodeName'])) return;
				void this.deleteProduct(Utils.unique(this.activity.activeProducts().map((product) => product.product)));
			}
		});

		effect(() => {
			const sizesMode: boolean = this.modes.sizesMode();

			if (this.canvasContainer) {
				if (sizesMode) {
					this.stands
						.filter((planogram: IPlanogram) => planogram.visible)
						.forEach((planogram: IPlanogram) => {
							this.canvasDrawSizes(planogram);
						});
				} else {
					this.sizesLayer.destroyChildren();
				}

				this.sizesLayer.draw();
			}
		});
		effect(() => {
			this.modes.articleMode();
			this.modes.sequenceNumberMode();

			untracked(() => {
				if (this.canvasContainer) void this.canvasRender();

				setTimeout(() => {
					if (this.equipmentModel) void this.equipmentModel.updateScene();
				});
			});
		});
		effect(() => {
			this.activity.activeProducts();

			if (!this.stands.length) return;
			untracked(() => {
				this.stands
					.filter((planogram: IPlanogram) => planogram.visible)
					.forEach((planogram: IPlanogram) => {
						planogram.stand.products.forEach((pg: ProductGroup) => {
							const canvasProduct: Konva.Group = this.canvasDrawerService.getCanvasProduct(pg);

							if (this.activity.isProductActive(pg)) {
								this.canvasDrawerService.setCanvasProductActive(pg, canvasProduct);
							} else {
								this.canvasDrawerService.setCanvasProductInactive(pg, canvasProduct);
							}
						});
					});

				this.planogramLayer.batchDraw();
			});
		});
		effect(() => {
			const activeBlock: Block = this.activity.activeBlocks()[0];

			if (!this.stands.length) return;
			untracked(() => {
				if (activeBlock) this.canvasDrawerService.getCanvasBlock(activeBlock).moveToTop();

				this.setBrandsInfoVisibility();
				this.setCategoriesInfoVisibility();

				this.stands
					.filter((planogram: IPlanogram) => planogram.visible)
					.forEach((planogram: IPlanogram) => {
						planogram.stand.blocks.forEach((block: Block) => {
							this.canvasDrawerService.changeBlockBordersStroke(block, activeBlock);
						});
					});

				this.blocksLayer.batchDraw();
			});
		});
		effect(() => {
			const selectedBlock: Block = this.activity.selectedBlock();

			if (!this.stands.length) return;

			this.stands[0].stand.blocks.forEach((block: Block) => {
				if (block == selectedBlock) {
					this.canvasDrawerService.getCanvasBlockBody(block).hide();
					this.canvasDrawerService.getCanvasBlockTitle(block).hide();
					this.canvasDrawerService.getCanvasBlockSizes(block).show();
				} else {
					this.canvasDrawerService.getCanvasBlockBody(block).show();
					this.canvasDrawerService.getCanvasBlockTitle(block).show();
					this.canvasDrawerService.getCanvasBlockSizes(block).hide();
				}
			});

			this.blocksLayer.draw();
		});
		effect(() => {
			this.activity.activePosMaterials();

			if (!this.stands.length) return;
			untracked(() => {
				this.stands
					.filter((planogram: IPlanogram) => planogram.visible)
					.forEach((planogram: IPlanogram) => {
						planogram.stand.POSMaterials.forEach((material: POSMaterial) => {
							if (this.activity.isPOSMaterialActive(material)) {
								this.canvasDrawerService.setCanvasPOSMaterialActive(material);
							} else {
								this.canvasDrawerService.setCanvasPOSMaterialInactive(material);
							}
						});
					});

				this.blocksLayer.draw();
			});
		});
		effect(() => {
			const productMove: boolean = this.activity.productMove();

			if (!this.stands.length) return;
			untracked(() => {
				if (productMove && this.activity.activeBlocks().length) {
					this.canvasDrawerService.getCanvasBlockSizes(this.activity.activeBlocks()[0]).hide();
				}
				if (!productMove && this.pausedInterval && !this.savePending) {
					this.savePending = true;
					this.performSaveAction(true).then((result: boolean) => {
						this.savePending = false;
						if (result) this.pausedInterval = false;
					});
				}
			});
		});
		effect(() => {
			this.setElementToDetail(this.activity.targetElement());
		});

		this.activity.buildProductsTable$
			.pipe(takeUntilDestroyed())
			.subscribe((reset: boolean) => this.buildProductsTable(reset));

		this.activity.historyGenerate$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.historyGenerate());

		this.activity.rebuildProductsTable$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.rebuildProductsTable());

		this.activity.reloadABCReport$
			.pipe(takeUntilDestroyed())
			.subscribe(() => void this.getAbcReport());

		this.activity.reloadEfficiencyAnalysis$
			.pipe(takeUntilDestroyed())
			.subscribe(async () => {
				if (this.analysisOpen) {
					this.panelLoader = true;
					this.productsAnalytic = await this.getProductsEfficiencyAnalysisData();
					this.panelLoader = false;
				}
			});

		this.activity.reloadRealProductsPercents$
			.pipe(takeUntilDestroyed())
			.subscribe(async () => this.setRealProductPercents());

		this.activity.setNumbersForPerforation$
			.pipe(takeUntilDestroyed())
			.subscribe((elements: Array<Element>) => {
				const perforations = Utils.unique(elements.filter((el: Element) => el.is(HoleElement)).map((el: Element) => el.depend));
				const productsOnHoles = this.stands[0].stand.products
					.filter((pg: ProductGroup) => pg.element.is(HoleElement) && perforations.includes(pg.element.depend));

				perforations.forEach((el: Element) => {
					const resetPg = productsOnHoles
						.filter((pg: ProductGroup) => pg.element.depend == el)
						.sort((a: ProductGroup, b: ProductGroup) => {
							return Utils.round(b.begin.y + b.Height) - Utils.round(a.begin.y + a.Height) ||
								a.element.shapes[0].begin.x - b.element.shapes[0].begin.x;
						});
					resetPg.forEach((pg, idx) => {
						if (!elements.includes(pg.element))
							this.canvasDrawerService.drawProductNumber({
								productGroup: pg,
								stand: this.stands[0].stand,
								scale: this.stands[0].scale,
								beginPoint: this.stands[0].beginPoint
							}, this.canvasDrawerService.getCanvasProduct(pg), idx + 1);
					});
				});
			});

		this.activity.showDeleteMessage$
			.pipe(takeUntilDestroyed())
			.subscribe((values: { block: Block, errors: Array<ProductGroup> }) => {
				this.snackBarService.setSnackBarNotification({
					messageText: 'DELETE_BLOCK_PRODUCTS_APPROVE',
					translateParams: { block: values.block.name },
					durationSeconds: 60 * 60,
					closeButton: false,
					closeActions: [
						{
							action: 'OK',
							callback: () => {
								const productsToDelete: Array<ProductGroup> = values.errors;
								this.stands[0].stand.products = this.stands[0].stand.products.filter(
									(pg: ProductGroup) => !productsToDelete.includes(pg)
								);
								productsToDelete.forEach((p: ProductGroup) => {
									const dependedProduct = this.stands[0].stand.products.find(
										(pg) => pg.dependProduct == p
									);
									if (dependedProduct) {
										dependedProduct.dependProduct = p.element.getDroppedProduct(
											p.begin.x,
											p.begin.y,
											p.begin.z,
											this.activity.activeProducts(),
											p
										);
										dependedProduct.begin.y = dependedProduct.dependProduct
											? dependedProduct.dependProduct.getBoundingY().max
											: dependedProduct.element.getBoundingY().max;
									}
								});
								const elements: Array<Element> = this.activity.normalizeElements(
									this.stands[0].stand,
									Utils.unique(productsToDelete.map((pg) => pg.element))
								);
								this.activity.requestCanvasUpdate(this.stands, elements.map((el: Element) => el.id));

								this.history.pop();
								this.historyGenerate();
							}
						},
						{
							action: 'CHANGE_BLOCK_SETTINGS',
							callback: () => {
								this.activity.setBlockActive(values.block);
								this.openSettingsPanel();
							}
						}
					]
				});
			});

		this.activity.canvasUpdate$
			.pipe(takeUntilDestroyed())
			.subscribe(async (req: CanvasUpdateRequest) => {
				const stands = req.stands?.length ? req.stands : this.stands;
				const ids = req.ids ?? [];

				if (req.reason === 'MOVE_POS_MATERIALS') {
					const updated: boolean = this.updateActivePOSMaterialsOnCanvas(stands);
					if (!updated) await this.canvasRender(stands);
					this.needCanvasResizeAfterKeyboardMove = true;
					if (req.requestId) this.activity.canvasUpdateCompleted$.next(req);
					return;
				}

				await this.canvasUpdate(stands, ids);

				if (req.requestId) this.activity.canvasUpdateCompleted$.next(req);
			});

		this.dragOutsideService.drop$
			.pipe(takeUntilDestroyed())
			.subscribe((targetCanvas: HTMLElement) => {
				if (!this.mainCanvas?.nativeElement || targetCanvas !== this.mainCanvas.nativeElement) return;
				if (!this.activity.activeProducts().length) return;

				const planogram: IPlanogram = this.stands[0];
				const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
					.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);

				this.handleProductGroupDrop(behavior, planogram, true);
			});

		this.dragOutsideService.redrawCanvas$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.redrawCanvas());
	}

	getLayoutAccessibility(): boolean {
		if (!this.stands.length) return false;

		 return this.stands[0].stand.allElements
			 .filter((element: Element) => element.parent.is(EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze))
			 .some((element: Element) => element.allowedAutoLayout);
	}

	moveProducts(horizontal: number, vertical: number): void {
		if (this.modes.equipmentEditMode()) return;

		const planogram: IPlanogram = this.stands[0];
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		this.activity.moveProducts(planogram, behavior, horizontal, vertical);
	}

	movePOSMaterials(horizontal: number, vertical: number): void {
		if (this.modes.equipmentEditMode()) return;

		const planogram: IPlanogram = this.stands[0];
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		this.activity.movePOSMaterials(planogram, behavior, horizontal, vertical);
	}

	keyLeftEvent(step: number): void {
		if (!this.modes.equipmentEditMode() || !this.activity.selectedElement()) return;

		const element: Element = this.stands[0].stand.getElementById(this.activity.selectedElement());
		const viewMode: string = this.activity.focusedCanvas().element ? 'top' : 'front';
		if (element.depend?.parent.is(EquipmentElements.CentralPillar)) return;
		if (!element?.parent.is(...DRAGGABLE_ELEMENTS[viewMode])) return;
		if (element.shapes[0].type == 'trapeze') return;
		const elementBounding: number = element.getBoundingX().min;
		const dependBounding: number = element.depend?.getBoundingX().min;
		if (Utils.round(elementBounding - step) < dependBounding) {
			step = Utils.round(elementBounding - dependBounding);
		}
		if (step) {
			const relatedElements: Array<Element> = element.relatedElements;
			const relatedProducts: Array<ProductGroup> = [
				...element.products,
				...relatedElements.map((el: Element) => el.products).flat()
			].flat();
			element.shapes[0].drag(-step);
			relatedElements.forEach((el: Element) => el.shapes[0].drag(-step));
			relatedProducts.forEach((pg: ProductGroup) => pg.move(-step));
			this.activity.normalizeElements(this.stands[0].stand);
			this.historyGenerate();
			this.activity.requestCanvasUpdate();
		}
	}

	keyRightEvent(step: number): void {
		if (!this.modes.equipmentEditMode() || !this.activity.selectedElement()) return;

		const element: Element = this.stands[0].stand.getElementById(this.activity.selectedElement());
		const viewMode: string = this.activity.focusedCanvas().element ? 'top' : 'front';
		if (element.depend?.parent.is(EquipmentElements.CentralPillar)) return;
		if (!element?.parent.is(...DRAGGABLE_ELEMENTS[viewMode])) return;
		if (element.shapes[0].type == 'trapeze') return;
		const elementBounding: number = element.getBoundingX().max;
		const dependBounding: number = element.depend?.getBoundingX().max;
		if (Utils.round(elementBounding + step) > dependBounding) {
			step = Utils.round(dependBounding - elementBounding);
		}
		if (step) {
			const relatedElements: Array<Element> = element.relatedElements;
			const relatedProducts: Array<ProductGroup> = [
				...element.products,
				...relatedElements.map((el: Element) => el.products).flat()
			].flat();
			element.shapes[0].drag(step);
			relatedElements.forEach((el: Element) => el.shapes[0].drag(step));
			relatedProducts.forEach((pg: ProductGroup) => pg.move(step));
			this.activity.normalizeElements(this.stands[0].stand);
			this.historyGenerate();
			this.activity.requestCanvasUpdate();
		}
	}

	keyUpEvent(step: number): void {
		if (!this.modes.equipmentEditMode() || !this.activity.selectedElement()) return;

		const element: Element = this.stands[0].stand.getElementById(this.activity.selectedElement());
		const viewMode: string = this.activity.focusedCanvas().element ? 'top' : 'front';
		if (!element?.parent.is(...DRAGGABLE_ELEMENTS[viewMode])) return;
		if (this.activity.focusedCanvas().element) {
			step = this.keyUpTopEvent(element, step);
		} else {
			step = this.keyUpFrontEvent(element, step);
		}
		if (step) {
			this.activity.normalizeElements(this.stands[0].stand);
			this.historyGenerate();
			this.activity.requestCanvasUpdate();
		}
	}

	keyUpFrontEvent(element: Element, step: number): number {
		const elementBounding: number = element.getBoundingY().max;
		const dependBounding: number = element.depend?.getBoundingY().max;
		if (Utils.round(elementBounding + step) > dependBounding) {
			step = Utils.round(dependBounding - elementBounding);
		}
		if (element.parent.is(EquipmentElements.Shelf, EquipmentElements.AngleShelf, EquipmentElements.HangBar)) {
			if (element.depend.isExtensionActive(ExtensionType.Rails)) {
				const rails: Extension<RailsParams> = element.depend.getExtension<RailsParams>(ExtensionType.Rails),
					points: Array<number> = elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element.depend);

				step = Utils.round(rails.params.railHolesHeight + rails.params.distanceBetweenRailHoles);

				if (points.every((point: number) => point != Utils.round(element.getBoundingY().min + step))) {
					step = 0;
				}
			}
		}
		const relatedElements: Array<Element> = element.relatedElements;
		const relatedProducts: Array<ProductGroup> = [
			...element.products,
			...relatedElements.map((el: Element) => el.products).flat()
		].flat();
		element.shapes[0].drag(null, step);
		if (element.parent.is(EquipmentElements.AngleShelf)) {
			(element.shapes[0] as Rectangle).updateRotationPoint(this.stands[0].stand, element);
		}
		relatedElements.forEach((el: Element) => el.shapes[0].drag(null, step));
		relatedProducts.forEach((pg: ProductGroup) => pg.move(null, step));
		return step;
	}

	keyUpTopEvent(element: Element, step: number): number {
		const elementBounding: number = element.getAbsoluteBoundingZ().min;
		const dependBounding: number = element.depend?.getAbsoluteBoundingZ().min;
		if (Utils.round(elementBounding - step) < dependBounding) {
			step = Utils.round(elementBounding - dependBounding);
		}
		const relatedElements: Array<Element> = element.relatedElements;
		const relatedProducts: Array<ProductGroup> = [
			...element.products,
			...relatedElements.map((el: Element) => el.products).flat()
		].flat();
		element.shapes[0].drag(null, null, -step);
		relatedElements.forEach((el: Element) => el.shapes[0].drag(null, null, -step));
		relatedProducts.forEach((pg: ProductGroup) => pg.move(null, null, -step));
		return step;
	}

	keyDownEvent(step: number): void {
		if (!this.modes.equipmentEditMode() || !this.activity.selectedElement()) return;

		const element: Element = this.stands[0].stand.getElementById(this.activity.selectedElement());
		const viewMode: string = this.activity.focusedCanvas().element ? 'top' : 'front';
		if (!element?.parent.is(...DRAGGABLE_ELEMENTS[viewMode])) return;
		if (this.activity.focusedCanvas().element) {
			step = this.keyDownTopEvent(element, step);
		} else {
			step = this.keyDownFrontEvent(element, step);
		}
		if (step) {
			this.activity.normalizeElements(this.stands[0].stand);
			this.historyGenerate();
			this.activity.requestCanvasUpdate();
		}
	}

	keyDownFrontEvent(element: Element, step: number): number {
		const elementBounding: number = element.getBoundingY().min;
		const dependBounding: number = element.depend?.getBoundingY().min;
		if (Utils.round(elementBounding - step) < dependBounding) {
			step = Utils.round(elementBounding - dependBounding);
		}
		if (element.parent.is(EquipmentElements.Shelf, EquipmentElements.AngleShelf, EquipmentElements.HangBar)) {
			if (element.depend.isExtensionActive(ExtensionType.Rails)) {
				const rails: Extension<RailsParams> = element.depend.getExtension<RailsParams>(ExtensionType.Rails),
					points: Array<number> = elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element.depend);

				step = Utils.round(rails.params.railHolesHeight + rails.params.distanceBetweenRailHoles);

				if (points.every((point: number) => point != Utils.round(element.getBoundingY().min - step))) {
					step = 0;
				}
			}
		}
		const relatedElements: Array<Element> = element.relatedElements;
		const relatedProducts: Array<ProductGroup> = [
			...element.products,
			...relatedElements.map((el: Element) => el.products).flat()
		].flat();
		element.shapes[0].drag(null, -step);
		if (element.parent.is(EquipmentElements.AngleShelf)) {
			(element.shapes[0] as Rectangle).updateRotationPoint(this.stands[0].stand, element);
		}
		relatedElements.forEach((el: Element) => el.shapes[0].drag(null, -step));
		relatedProducts.forEach((pg: ProductGroup) => pg.move(null, -step));
		return step;
	}

	keyDownTopEvent(element: Element, step: number): number {
		const elementBounding: number = element.getAbsoluteBoundingZ().max;
		const dependBounding: number = element.depend?.getAbsoluteBoundingZ().max;
		if (Utils.round(elementBounding + step) > dependBounding) {
			step = Utils.round(dependBounding - elementBounding);
		}
		const relatedElements: Array<Element> = element.relatedElements;
		const relatedProducts: Array<ProductGroup> = [
			...element.products,
			...relatedElements.map((el: Element) => el.products).flat()
		].flat();
		element.shapes[0].drag(null, null, step);
		relatedElements.forEach((el: Element) => el.shapes[0].drag(null, null, step));
		relatedProducts.forEach((pg: ProductGroup) => pg.move(null, null, step));
		return step;
	}

	deleteBlockWithoutProducts() {
		if (!this.activity.activeBlocks().length) return;
		this.stands[0].stand.blocks = this.stands[0].stand.blocks.filter((b) => !this.activity.isBlockActive(b));
		this.isBlockedReversed = this.sessionService.hasFeature(ServiceFeatures.LayoutReverse)
			&& !!this.stands[0].stand.blocks?.length;
		this.stands[0].stand.products
			.filter((pg) => this.activity.activeBlocks()[0].products.includes(pg))
			.forEach((pg) => (pg.block = null));

		const elements: Array<Element> = this.activity.normalizeElements(this.stands[0].stand, this.activity.activeBlocks()[0].elements);

		this.activity.closePanel();

		this.activity.requestCanvasUpdateAsync(this.stands, elements.map((el: Element) => el.id)).then(() => {
			this.activity.setProductActive();
			this.rebuildProductsTable();
		});

		this.historyGenerate();
	}

	updateDetailedProductGroup(key: string, increase: boolean = false) {
		if (!this.activity.activeProducts().length) return;

		this.activity.activeProducts()
			.filter((pg: ProductGroup) => !(pg.element.is(HangBarElement) && ['placementHeight', 'placementDepth'].includes(key)))
			.filter((pg: ProductGroup) => !(pg.element.is(HookElement) && ['FacesQty', 'placementHeight'].includes(key)))
			.filter((pg: ProductGroup) => !pg.element.is(HoleElement) && !pg.isLooseForm)
			.sort((a: ProductGroup, b: ProductGroup) => a.begin.x - b.begin.x)
			.forEach((pg: ProductGroup) => {
				const { FacesQty, qty } = pg,
					_qty = FacesQty;

				pg[key] = increase ? pg[key] + 1 : pg[key] - 1 > 0 ? pg[key] - 1 : 1;
				pg.qty = increase ? qty + _qty : pg[key] - 1 > 0 ? qty - _qty : _qty;

				const block = this.stands[0].stand.findBlock(pg.begin.x, pg.begin.y, pg.Width);
				if (block && (pg.block != block.id || !block.id)) {
					if (!block.isProductSuitable(pg.product)) {
						this.activity.showDeleteMessage$.next({ block: block, errors: [pg] });
					}
				}
			});

		const stand = this.activity.activeProducts()[0].element.stand,
			stnd = this.stands.find((st) => st.stand == stand);

		stnd.layerObjects.boxGroup.destroyChildren();

		if (key == 'placementHeight') {
			let dependedProducts = [];
			this.activity.activeProducts().forEach(
				(el) =>
					(dependedProducts = [
						...dependedProducts,
						...Utils.findDependProducts(el, this.stands[0].stand.products)
					])
			);

			stnd.stand.products.forEach((p) => {
				if (dependedProducts.includes(p)) {
					p.begin.y = p.dependProduct
						? p.dependProduct.getBoundingY().max
						: p.element.getBoundingY().max;
				}
			});
		}

		this.stands = [...this.stands];

		this.activity.refreshActiveProducts();
		this.rerenderActiveStand();
	}

	updateProductGroup(key: string, increase: boolean = false) {
		if (!this.activity.activeProducts().length) return;

		const stand = this.activity.activeProducts()[0].element.stand,
			stnd = this.stands.find((st) => st.stand == stand);

		this.activity.activeProducts()
			.filter((pg: ProductGroup) => !(pg.element.is(HangBarElement) && ['placementHeight', 'placementDepth'].includes(key)))
			.filter((pg: ProductGroup) => !(pg.element.is(HookElement) && ['FacesQty', 'placementHeight'].includes(key)))
			.filter((pg: ProductGroup) => !pg.element.is(HoleElement) && !pg.isLooseForm)
			.sort((a, b) => a.begin.x - b.begin.x)
			.forEach((pg) => {
				if (!increase && pg[key] == 1) return;
				const group = pg.element.products.find((g) => g.some((p) => p == pg)),
					prevWidth = Element.calculateGroupWidth(group);
				pg[key] = increase ? pg[key] + 1 : pg[key] - 1;
				if (increase) {
					const diff =
							prevWidth < pg.Width ? Utils.round(pg.Width - prevWidth) : 0,
						prods = pg.element.products.filter((group) =>
							group.some((product) => pg == product)
						);
					if (diff) pg.element.shiftProductGroups(prods, Utils.to1DArray(prods));
				}

				const block = stand.findBlock(pg.begin.x, pg.begin.y, pg.Width);
				if (block && (pg.block != block.id || !block.id)) {
					if (!block.isProductSuitable(pg.product)) {
						this.activity.showDeleteMessage$.next({ block: block, errors: [pg] });
					}
				}
			});

		stnd.layerObjects.boxGroup.destroyChildren();

		if (key == 'placementHeight') {
			let dependedProducts = [];
			this.activity.activeProducts().forEach(
				(el) =>
					(dependedProducts = [
						...dependedProducts,
						...Utils.findDependProducts(el, this.stands[0].stand.products)
					])
			);

			stnd.stand.products.forEach((p) => {
				if (dependedProducts.includes(p)) {
					p.begin.y = p.dependProduct
						? p.dependProduct.getBoundingY().max
						: p.element.getBoundingY().max;
				}
			});
		}

		this.stands = [...this.stands];

		this.activity.refreshActiveProducts();
		this.rerenderActiveStand();
	}

	rerenderActiveStand() {
		const stand = this.activity.activeProducts()[0].element.stand,
			elementsToNormalize = [];

		this.activity.activeProducts().forEach((pg) => {
			if (!elementsToNormalize.includes(pg.element)) elementsToNormalize.push(pg.element);
		});
		const elements: Array<Element> = this.activity.normalizeElements(stand, elementsToNormalize);

		this.canvasRendering = true;
		this.setProductsPercents()
			.then(() => {
				this.setRealProductPercents();
				this.historyGenerate();
			})
			.then(() => this.activity.requestCanvasUpdateAsync(this.stands, elements.map((el: Element) => el.id)))
			.then(() => (this.canvasRendering = false));
	}

	async addProducts(products: Product[], generate_history: boolean = true) {
		products = products.filter(
			(product) => !this.products.find((p) => p.id == product.id)
		);
		this.products = products.concat(this.products);

		this.rebuildProductsTable();
		await this.setProductsPercents();
		this.setRealProductPercents();

		this.productCache.map((p) => {
			if (this.products.includes(p)) {
				this.moveInHistory = true;
			}
		});

		if (this.moveInHistory) {
			this.moveInHistory = false;
			return;
		}

		if (generate_history) this.historyGenerate();

		if (this.activity.highlightActive()) {
			this.highlightProductsService.setColors(this.products);
			const applyFunc = this.highlightProductsService.applyHighlight(
				this.products,
				this.standId,
				this.planogramId
			);
			this.activity.setHighlightedProducts(await applyFunc[this.activity.highlightValue()]());
			await this.activity.requestCanvasUpdateAsync();
		}
		this.moveInHistory = false;
	}

	openProductCard(id: number, name: string): void {
		this.productCardService.open({ key: id, name: name });
	}

	updateProductCard(productCard: IProductCard): void {
		const updObj: any = {};
		const defaultParams: DefaultProductParams = new DefaultProductParams();

		updObj.width = productCard.width || defaultParams.width;
		updObj.height = productCard.height || defaultParams.height;
		updObj.depth = productCard.depth || defaultParams.depth;
		updObj.photo = productCard.photo || EMPTY_PRODUCT_IMAGE_PATH;
		updObj.is_loose = productCard.is_loose;
		updObj.packings = productCard.packing;
		updObj.width_compression_coef = productCard.width_compression_coef;
		updObj.height_compression_coef = productCard.height_compression_coef;
		updObj.depth_compression_coef = productCard.depth_compression_coef;
		updObj.name = productCard.name;
		updObj.article = productCard.article;
		updObj.barcode = productCard.barcode;
		updObj.description = productCard.description;

		const product: Product = this.products.find((p: Product) => p.id === productCard.id);
		if (!product) return;

		if (productCard.set_compression_to_global_everywhere) {
			this.stands[0].stand.products
				.filter((pg: ProductGroup) => pg.product.id === productCard.id)
				.forEach((pg: ProductGroup) => {
					pg.height_compression_coef = null;
					pg.width_compression_coef = null;
					pg.depth_compression_coef = null;
				});
		}

		product.update(updObj);
		product.setPreviewSizes();

		const stand: IPlanogram = this.stands[0],
			elementsToNormalize: Array<Element> = [];

		stand.stand.products.forEach((pg: ProductGroup) => {
			if (pg.product.id == product.id && !elementsToNormalize.includes(pg.element)) {
				elementsToNormalize.push(pg.element);
			}

			if (pg.selectPacking && !pg.product.packings.find((packing: IProductPacking) => packing.id === pg.selectPacking)) {
				pg.selectPacking = null;
			}
		});

		elementsToNormalize.forEach((element: Element) => {
			const groupsToShift: Array<Array<ProductGroup>> = element.products.filter((group: Array<ProductGroup>) =>
				group.some((pg: ProductGroup) => product.id == pg.product.id)
			);
			groupsToShift.forEach((group: Array<ProductGroup>) =>
				element.shiftProductGroups([group], Utils.to1DArray(group))
			);
		});

		const elements: Array<Element> = this.activity.normalizeElements(stand.stand, elementsToNormalize);
		this.activity.requestCanvasUpdateAsync([stand], elements.map((el: Element) => el.id)).finally(() => {
			if (!this.activity.activeBlocks().length) this.activity.setProductActive();
		});
		this.setRealProductPercents();
	}

	selectAllStandProducts() {
		const selected_stands = this.activity.activeProducts().map((pg) => pg.element.stand);
		this.activity.setProductActive(null, false, false);

		const groups = PlanogramEditComponent.to1D(
			this.stands.filter((s) => selected_stands.includes(s.stand)).map((s) => s.stand.products)
		);

		groups.forEach((pg: ProductGroup) => this.activity.setProductActive(pg, true));

		this.activity.clearActivePanelProducts();
		this.activity.setActivePanelProducts(groups.map((pg: ProductGroup) => pg.product));
	}

	canvasClear(stands = [], elements: Array<string> = []) {
		this.stands.forEach((s) => {
			if (!stands.length || (!elements.length && stands.includes(s))) {
				s.layerObjects.planogramGroup.destroyChildren();
				s.layerObjects.tooltipGroup.destroyChildren();
			}
			if (stands.includes(s) && elements.length) {
				s.layerObjects.planogramGroup.getChildren().forEach((el) => {
					if (el.id() !== '' && elements.includes(el.id())) el.destroyChildren();
				});
				s.layerObjects.tooltipGroup.getChildren().forEach((el) => {
					if (el.id() !== '' && elements.includes(el.id())) el.destroyChildren();
				});
			}
			s.layerObjects.boxGroup.destroyChildren();
		});
		this.clearToolLayer();
	}

	createNewPOSTextMaterial(): void {
		const material: POSTextMaterial = new POSTextMaterial();
		this.stands[0].stand.POSMaterials.push(material);
		this.activity.requestCanvasUpdateAsync().then(() => this.activity.setPOSMaterialActive(material));
		this.historyGenerate();
        this.mixPanelService.trackPage(MixpanelEvents.POSTextAdd);
	}

	copyPOSMaterial(): void {
		if (this.activity.activePosMaterials().length !== 1) return;
		const material: POSMaterial = this.activity.activePosMaterials()[0].copy();
		material.y += 0.1;
		this.stands[0].stand.POSMaterials.push(material);
		this.activity.requestCanvasUpdateAsync(this.stands).then(() => this.activity.setPOSMaterialActive(material));
		this.historyGenerate();
	}

	changeDrawBlockMode(): void {
		if (this.sessionService.hasFeature(ServiceFeatures.PlanogramBlocks)) {
			this.modes.toggleDrawBlockMode();
			this.mainCanvas.nativeElement.style.cursor = this.modes.drawBlockMode() ? 'crosshair' : 'default';
			this.blockBuildConfig = { building: false };

			if (this.modes.drawBlockMode()) {
				this.activity.clearSelectedBlock();
				this.activity.setBlockActive();
			}

			this.canvasRender().finally(() => this.changeCanvasListening(!this.modes.drawBlockMode()));
		} else {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.Blocks);
		}
	}

	// обробка натиску мишки на канву
	canvasMouseDown(event) {
		const stand = this.stands[0],
			x = Utils.round((event.layerX - canvasOffset - stand.beginPoint.x) / stand.scale),
			y = Utils.round((event.layerY - canvasOffset - stand.beginPoint.y) / stand.scale),
			z = 0,
			clickedBlock = stand.stand.blocks.find((b: Block) => b.isPointOn(x, stand.stand.Height - y));

		if (this.modes.drawBlockMode()) {
			if (clickedBlock) return;

			stand.stand.blocks.push(new Block({ begin: [{ x, y, z }] }));
			stand.stand.blocks[stand.stand.blocks.length - 1].setStand(stand);
			stand.stand.blocks[stand.stand.blocks.length - 1].depth = stand.stand.Depth;

			this.blockBuildConfig.building = true;
			this.rect.listening(false);
			this.rect.x(x + canvasOffset * 2 + stand.beginPoint.x);
			this.rect.y(y);

			return;
		} else {
			if (this.activity.activeBlocks().length && !clickedBlock && !this.resizeMode) {
				this.activity.setBlockActive(null, false);
				this.rebuildProductsTable();
			}
		}
	}

	canvasMove(event: any) {
		if (this.modes.drawBlockMode()) {
			if (!this.blockBuildConfig.building) return;

			const stand = this.stands[0],
				{ scale, beginPoint } = stand,
				x = event.layerX - beginPoint.x,
				y = event.layerY - canvasOffset - beginPoint.y,
				z = 0,
				point = this.canvasGetPoint(x, y),
				reverseDrawX = point.x < stand.stand.blocks[stand.stand.blocks.length - 1].begin[0].x,
				reverseDrawY = point.y < stand.stand.blocks[stand.stand.blocks.length - 1].begin[0].y,
				rectCoord = PlanogramEditComponent.reverseCoordinates(
					stand.stand.blocks[stand.stand.blocks.length - 1].begin[0],
					point
				),
				pointsStart = {
					x: rectCoord.x1,
					y: rectCoord.y1,
					z
				},
				width = Utils.round(rectCoord.x2 - rectCoord.x1, 2),
				height = rectCoord.y2 - rectCoord.y1,
				drawSideX = stand.stand.blocks[stand.stand.blocks.length - 1].begin[0].x - pointsStart.x,
				drawSideY = stand.stand.blocks[stand.stand.blocks.length - 1].begin[0].y - pointsStart.y;

			this.rect.fill('#ff5a00');
			this.rect.opacity(0.4);
			this.rect.x(
				drawSideX == 0
					? rectCoord.x1 * scale + canvasOffset + beginPoint.x
					: rectCoord.x1 * scale + beginPoint.x
			);
			this.rect.y(rectCoord.y1 * scale + beginPoint.y + canvasOffset);
			this.rect.width(
				drawSideX == 0
					? width * scale - canvasOffset
					: width * scale + canvasOffset
			);
			this.rect.height(height * this.scale);
			const blockCopy = stand.stand.blocks[stand.stand.blocks.length - 1];
			blockCopy.clearCache();
			blockCopy.width = reverseDrawX
				? width + canvasOffset / this.scale
				: width - canvasOffset / this.scale;
			blockCopy.height = height;
			if (reverseDrawX || reverseDrawY) {
				blockCopy.updateCoordinatesForReverseDrawBlock(
					reverseDrawX,
					reverseDrawY
				);
			}
			blockCopy.cache.elements = this.refreshBlockElements(blockCopy);
			this.blocksLayer.add(this.blockWidthLabel);
			this.blocksLayer.add(this.blockHeightLabel);
			this.blocksLayer.add(this.blockPercentLabel);

			this.blockWidthLabel.show();
			this.blockWidthLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
			this.blockWidthLabel.getText().text(
				UnitConverter.getConvertValue(
					Math.abs(
						drawSideX == 0
							? width - canvasOffset / scale
							: width + canvasOffset / scale
					),
					'distance', 'inches', 3
				).toString()
			);
			this.blockWidthLabel.setAttrs({
				x:
					drawSideX == 0
						? event.layerX - (width * scale) / 2
						: event.layerX + (width * scale) / 2,
				y:
					drawSideY == 0
						? event.layerY - height * scale
						: event.layerY + height * scale - canvasOffset / 2
			});

			this.blockHeightLabel.show();
			this.blockHeightLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
			this.blockHeightLabel.getText().text(Utils.round(height, 2).toString());

			this.blockHeightLabel.setAttrs({
				x:
					drawSideX == 0
						? event.layerX - this.blockHeightLabel.height()
						: event.layerX + this.blockHeightLabel.height(),
				y:
					drawSideY == 0
						? event.layerY -
						(height * scale) / 2 +
						this.blockHeightLabel.width() / 2
						: event.layerY +
						(height * scale) / 2 +
						this.blockHeightLabel.width() / 2
			});

			this.blockPercentLabel.show();
			this.blockPercentLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
			this.blockPercentLabel.getText().text(blockCopy.percent + '%');
			this.blockPercentLabel.setAttrs({
				x:
					drawSideX == 0
						? event.layerX - width * scale + canvasOffset
						: event.layerX,
				y: drawSideY == 0 ? event.layerY - height * scale : event.layerY
			});

			this.rect.visible(true);

			this.blocksLayer.draw(); // redraw any changes.
			return;
		}
	}

	canvasUp(event) {
		if (this.modes.drawBlockMode()) {
			if (!this.blockBuildConfig.building) return;

			this.blockWidthLabel.hide();
			this.blockHeightLabel.hide();
			this.blockPercentLabel.hide();

			const stand = this.stands[0];
			const x = (event.evt.layerX - canvasOffset - stand.beginPoint.x) / stand.scale;
			const y = (event.evt.layerY - canvasOffset - stand.beginPoint.y) / stand.scale;
			const z = 0;
			const rectCoord = PlanogramEditComponent.reverseCoordinates(
				stand.stand.blocks[stand.stand.blocks.length - 1].begin[0],
				{ x: x, y: y }
			);
			const width = UnitConverter.convertAndRoundToSaveValue(rectCoord.x2 - rectCoord.x1, 'distance', 'inches', 3);
			const height = UnitConverter.convertAndRoundToSaveValue(rectCoord.y2 - rectCoord.y1, 'distance', 'inches', 3);
			const pointsStart = { x: rectCoord.x1, y: rectCoord.y1, z: z };
			const copy = stand.stand.blocks[stand.stand.blocks.length - 1].copy();

			if (this.flipMode.active) pointsStart.x = Utils.round(stand.stand.Width - (pointsStart.x + width));

			this.blockBuildConfig.building = false;

			copy.updateCoordinates([pointsStart], width, height);
			copy.depth = this.stands[0].stand.Depth;

			copy.setBlockColor();
			copy.name = Block.setBlockTitle(
				stand.stand.blocks.filter((b) => b.begin[0].x !== copy.begin[0].x).map((b) => b.name)
			);

			this.rect.visible(false);

			const intersections = this.canDrawBlock(copy, stand.stand.blocks.slice(0, -1));

			if (!!intersections.length) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'BLOCK_OVER_BLOCKS_ERROR'
				});
			}

			if (!!intersections.length || width <= this.minBlockSize || height <= this.minBlockSize) {
				stand.stand.blocks.splice(stand.stand.blocks.length - 1, 1);
				this.blocksLayer.draw();
				return;
			}

			copy.setProducts(Utils.to1DArray(stand.stand.blocks.map((b) => b.products)));

			stand.stand.blocks[stand.stand.blocks.length - 1] = copy;
			stand.stand.blocks = [...stand.stand.blocks];
			this.isBlockedReversed = this.sessionService.hasFeature(ServiceFeatures.LayoutReverse)
				&& !!this.stands[0].stand.blocks?.length;

			const elements: Array<Element> = this.activity.normalizeElements(
				stand.stand,
				stand.stand.blocks[stand.stand.blocks.length - 1].elements
			);
			this.activity.setBlockActive(copy);
			this.activity.requestCanvasUpdate(this.stands, elements.map((el: Element) => el.id));
			this.historyGenerate();

			setTimeout(() => {
				if (this.equipmentModel) void this.equipmentModel.updateScene();
			});
		}
	}

	static blockIntersects(active_block, all_blocks) {
		const active_block_poly = geometryFactory.createPolygon(
				active_block.coordinates.map((i) => new Coordinate(i.x, i.y))
			),
			all_blocks_poly = all_blocks.map((b) =>
				geometryFactory.createPolygon(
					b.coordinates.map((i) => new Coordinate(i.x, i.y))
				)
			);

		return all_blocks_poly
			.filter(
				(i) => active_block_poly.intersects(i) && !active_block_poly.touches(i)
			)
			.map((i) => active_block_poly.intersection(i));
	}

	canDrawBlock(copy, all_blocks: Block[] = []) {
		const intersections = PlanogramEditComponent.blockIntersects(
			copy,
			all_blocks
		);

		return intersections.filter((poly) => !poly.isEmpty());
	}

	canMoveBlock(b, copy) {
		if (!b.elements.length) return true;
		return Utils.equalLists(
			copy.elements.map((el) => el.constructor.name),
			b.elements.map((el) => el.constructor.name)
		);
	}

	canvasGetPoint(x: number, y: number, ignoreWall?: Wall): Point {
		// // вираховуємо відстань 0.15 метра на яку дотягувати точку.
		// const n = Math.floor(0.15 * this.scale), cellWidth = this.scale / 2.0;
		// //const n = 10, cellWidth = this.scale / 2.0;
		//
		// // прораховуємо відстані до найближчих ліній сітки з усіх сторін
		// const left = Math.abs(cellWidth * Math.floor(x / cellWidth) - x),
		//     top = Math.abs(cellWidth * Math.floor(y / cellWidth) - y),
		//     right = Math.abs(cellWidth * Math.floor(x / cellWidth + 1) - x),
		//     bottom = Math.abs(cellWidth * Math.floor(y / cellWidth + 1) - y);
		//
		// // порівнюєм з `n` та дотягаємо точку до сітки якщо потрібно
		// if (top <= n && left <= n) {
		//     x -= left;
		//     y -= top;
		// }
		// if (top <= n && right <= n) {
		//     x += right;
		//     y -= top;
		// }
		// if (bottom <= n && left <= n) {
		//     x -= left;
		//     y += bottom;
		// }
		// if (bottom <= n && right <= n) {
		//     x += right;
		//     y += bottom;
		// }
		// ділимо на масштаб щоб зберегти координату в метрах
		x = Utils.round(x / this.scale);
		y = Utils.round(y / this.scale);

		return { x, y };
	}

	drawBlocks(stand: IPlanogram) {
		const redrawCanvas = (stands = []) => {
			this.canvasRender(stands).finally(() => {
				this.stopProductMove(true);
			});
		};
		const beginPoint: Point = stand.beginPoint,
			scale: number = stand.scale;

		stand.stand.blocks.forEach((b: Block) => {
			b.cache.elements = null;
			b.setProducts(
				Utils.to1DArray(stand.stand.blocks.filter((block) => block != b).map((block) => block.products))
			);
			const canvasBlock: Konva.Group = this.canvasDrawerService.createCanvasBlock({
				block: b,
				beginPoint: stand.beginPoint,
				scale: stand.scale,
				active: b == this.activity.activeBlocks()[0],
				selected: b == this.activity.selectedBlock(),
				draggable: !this.modes.drawBlockMode() && !this.modes.equipmentEditMode(),
				zoom: this.zoom,
				flip: this.flipMode.active
			});

			if (!this.modes.drawBlockMode() && !this.modes.equipmentEditMode()) {
				const blockRect: Konva.Rect = this.canvasDrawerService.getCanvasBlockBody(b),
					blockDescriptionIcon: Konva.Image = this.canvasDrawerService.getCanvasBlockDescriptionIcon(b),
					blockName: Konva.Text = this.canvasDrawerService.getCanvasBlockName(b),
					sizes: Konva.Group = this.canvasDrawerService.getCanvasBlockSizes(b),
					sizesCursor = { top: 'n-resize', right: 'e-resize', bottom: 's-resize', left: 'w-resize' },
					productsPositions: Map<ProductGroup, Konva.Vector2d> = b.products
						.reduce((result: Map<ProductGroup, Konva.Vector2d>, pg: ProductGroup) => {
							result.set(pg, this.canvasDrawerService.getCanvasProduct(pg).position());
							return result;
						}, new Map<ProductGroup, Konva.Vector2d>());

				blockRect.on('click', () => {
					const prevActiveBlock: Block = this.activity.activeBlocks()[0];
					this.activity.clearSelectedBlock();
					this.activity.setBlockActive(b);
					if (prevActiveBlock !== b) this.rebuildProductsTable();
				});

				blockRect.on('dblclick', () => {
					this.activity.setBlockActive();
					this.activity.setSelectedBlock(b);
				});

				blockRect.on('dragstart', () => {
					this.multiSelectConfiguration.begin = null;
					this.multiSelectConfiguration.end = null;
					this.multiSelectConfiguration.drawing = false;

					this.activity.clearSelectedBlock();
					if (!this.activity.isBlockActive(b)) this.activity.setBlockActive(b);
					this.activity.setProductMove(true); // Todo find better way hide sizes rect

					this.canvasDrawerService.getCanvasBlockBorders(b).hide();
					this.canvasDrawerService.getCanvasBlockSizes(b).hide();
					this.canvasDrawerService.getCanvasBlockTitle(b).hide();
					b.updateBlockProducts(this.activity.getDependBlockProducts(b));
					b.products
						.sort((a, b) => a.begin.z - b.begin.z)
						.forEach((p) => {
							this.canvasDrawerService.getCanvasProduct(p).moveToTop();
						});
					this.blocksLayer.draw();
				});

				blockRect.on('dragmove', () => {
					b.products.forEach((p) => {
						const canvasProduct = this.canvasDrawerService.getCanvasProduct(p);
						canvasProduct.x(productsPositions.get(p).x + blockRect.x());
						canvasProduct.y(productsPositions.get(p).y + blockRect.y());
					});

					this.blocksLayer.draw();
					this.planogramLayer.draw();
				});

				blockRect.on('dragend', () => {
					if (!this.activity.activeBlocks().length) {
						blockRect.off('dragend');
						this.stopProductMove();
						return;
					}
					this.changeCanvasListening(false);
					const x = canvasBlock.x() + blockRect.x() - beginPoint.x - canvasOffset,
						y = canvasBlock.y() + blockRect.y() - beginPoint.y - canvasOffset,
						point = this.canvasGetPoint(x, y),
						elements = this.activity.activeBlocks()[0].elements,
						copy = this.activity.activeBlocks()[0].copy();

					if (this.flipMode.active) {
						point.x = b.stand.Width - b.width - point.x;
					}

					this.canvasDrawerService.getCanvasBlockBorders(b).show();
					this.canvasDrawerService.getCanvasBlockSizes(b).show();
					this.canvasDrawerService.getCanvasBlockTitle(b).show();

					copy.updateCoordinates([Object.assign({}, point)]);

					const intersections = this.canDrawBlock(
							copy,
							stand.stand.blocks.filter((i) => i != b)
						),
						paths = [];

					if (intersections.length) {
						let count = 10;

						while (count) {
							let inner_count = 5,
								inner_paths = [],
								copy_point = Object.assign({}, point),
								copied_block = copy.copy(),
								choice = Utils.randomElementFromArray(choices);

							inner_loop: for (let i = 0; i < inner_count; i++) {
								let intersections = this.canDrawBlock(
										copied_block,
										stand.stand.blocks.filter((i) => i != b)
									),
									block_bounding_x = copied_block.boundingX,
									block_bounding_y = copied_block.boundingY;

								for (let j = 0; j < intersections.length; j++) {
									const min_x = Math.min.apply(
											null,
											intersections[j].getCoordinates().map((c) => c.x)
										),
										max_x = Math.max.apply(
											null,
											intersections[j].getCoordinates().map((c) => c.x)
										),
										min_y = Math.min.apply(
											null,
											intersections[j].getCoordinates().map((c) => c.y)
										),
										max_y = Math.max.apply(
											null,
											intersections[j].getCoordinates().map((c) => c.y)
										);

									if (i % 2 == choice) {
										if (
											block_bounding_x.min < min_x &&
											block_bounding_x.max > min_x
										) {
											copy_point.x = min_x - copy.width;
										}

										if (
											block_bounding_x.min < max_x &&
											block_bounding_x.min == min_x
										) {
											copy_point.x = max_x;
										}
									} else {
										if (
											block_bounding_y.min < min_y &&
											block_bounding_y.max > min_y
										) {
											copy_point.y = min_y - copy.height;
										}

										if (
											block_bounding_y.min < max_y &&
											block_bounding_y.min == min_y
										) {
											copy_point.y = max_y;
										}
									}

									inner_paths.push(Object.assign({}, copy_point));
									copied_block.updateCoordinates([
										Object.assign({}, copy_point)
									]);

									intersections = this.canDrawBlock(
										copied_block,
										stand.stand.blocks.filter((i) => i != b)
									);

									if (!intersections.length) break inner_loop;
								}
							}

							paths.push(inner_paths);
							count--;
						}

						if (!paths || !paths.length) {
							this.snackBarService.setSnackBarNotification({
								messageText: 'BLOCK_OVER_BLOCKS_ERROR'
							});
							b.products.forEach((p) => this.canvasDrawerService.getCanvasProduct(p).destroyChildren());
							redrawCanvas([stand]);
							return;
						}

						const distances = paths
								.map((points) => points.slice(-1)[0])
								.map((p, idx) => [
									Utils.distance(point.x, point.y, p.x, p.y),
									idx
								]),
							idx = distances.sort((a, b) => a[0] - b[0])[0][1];

						const new_p = paths[idx].slice(-1)[0];

						copy.products.forEach((p) => {
							this.canvasDrawerService.getCanvasProduct(p).x(
								this.canvasDrawerService.getCanvasProduct(p).x() - (point.x - new_p.x) * scale
							);
							this.canvasDrawerService.getCanvasProduct(p).y(
								this.canvasDrawerService.getCanvasProduct(p).y() - (point.y - new_p.y) * scale
							);
						});

						point.x = new_p.x;
						point.y = new_p.y;

						copy.updateCoordinates([Object.assign({}, point)]);
					}

					const finalIntersections = this.canDrawBlock(
						copy,
						stand.stand.blocks.filter((i) => i != b)
					);

					if (
						(b.products.length && !this.canMoveBlock(b, copy)) ||
						finalIntersections.length
					) {
						b.products.forEach((p) => this.canvasDrawerService.getCanvasProduct(p).destroyChildren());

						redrawCanvas([stand]);
						return;
					}

					b.products.sort((a, b) => a.begin.z - b.begin.z);
					let product_idx = 0,
						products_matrix = (!b.elements.length ? copy : b).productsMatrix;

					for (let product of copy.products) {
						let x = Utils.round(
								(this.canvasDrawerService.getCanvasProduct(product).x() - canvasOffset - stand.beginPoint.x) / stand.scale
							),
							element: Element = copy.extractPgElement(
								products_matrix[product_idx]
							);

						if (
							element &&
							element.canHaveSiblings &&
							!element.shapes[0].isPointOn(x)
						)
							element = copy.elements.find((el) => el.shapes[0].isPointOn(x));

						if (!element) {
							b.products.forEach((p) => this.canvasDrawerService.getCanvasProduct(p).destroyChildren());

							redrawCanvas([stand]);
							return;
						}

						const productCopy = product.copy();

						productCopy.begin.x = x;
						productCopy.Element = element;

						if (!element.canDrop(productCopy)) {
							b.products.forEach((p) => this.canvasDrawerService.getCanvasProduct(p).destroyChildren());

							redrawCanvas([stand]);
							return;
						}

						product_idx++;
					}

					product_idx = 0;
					for (let product of b.products) {
						let x = Utils.round(
								(this.canvasDrawerService.getCanvasProduct(product).x() - canvasOffset - stand.beginPoint.x) / stand.scale
							),
							element: Element = copy.extractPgElement(
								products_matrix[product_idx]
							);

						if (
							element &&
							element.canHaveSiblings &&
							!element.shapes[0].isPointOn(x)
						)
							element = copy.elements.find((el) => el.shapes[0].isPointOn(x));

						if (!element) return false;

						product.begin.x = x;
						product.Element = element;

						switch (element.parent.type) {
							case 'Box':
							case 'SlicedBox':
								product.begin.y = element.getBoundingY().min;
								break;

							case 'Hook':
								product.begin.x = Utils.round(
									element.shapes[0].begin.x -
									(product.Width - (element.shapes[0] as Rectangle).width) /
									2
								);
								product.begin.y = Utils.round(element.getBoundingY().min - product.Height);
								break;

							default:
								product.begin.y = element.getBoundingY().max;
						}

						const pIdx = stand.stand.products.indexOf(product);
						stand.stand.products.splice(pIdx, 1);
						stand.stand.products.push(product);

						product_idx++;
					}

					b.updateCoordinates([Object.assign({}, point)]);

					b.products
						.sort((a, b) => a.getBoundingY().min - b.getBoundingY().min)
						.forEach((pg) => {
							if (pg.dependProduct && !b.products.includes(pg.dependProduct)) {
								pg.dependProduct = null;
							} else if (
								pg.dependProduct &&
								b.products.includes(pg.dependProduct)
							) {
								pg.begin.x = pg.dependProduct.begin.x;
								pg.begin.y = pg.dependProduct.getBoundingY().max;
							}
						});

					const blockProductErrors = b.setProducts(
						Utils.to1DArray(stand.stand.blocks.filter((block) => block != b).map((block) => block.products))
					);

					if (blockProductErrors.length) this.activity.showDeleteMessage$.next({ block: b, errors: blockProductErrors });

					this.activity.refreshActiveBlocks();
					const normalized: Array<Element> = this.activity.normalizeElements(stand.stand, [...elements, ...b.elements]);

					this.activity.setNumbersForPerforation$.next(normalized);

					this.activity.requestCanvasUpdateAsync(this.stands, normalized.map((el: Element) => el.id)).finally(() => {
						this.stopProductMove(true);
						setTimeout(() => {
							if (this.equipmentModel) void this.equipmentModel.updateScene();
						});
					});
					this.historyGenerate();
				});

				if (this.canvasDrawerService.getCanvasBlockTooltipNecessity(b)) {
					const tooltip = this.canvasDrawerService.createCanvasBlockTooltip(b, b.name, this.mainCanvas.nativeElement);
					stand.layerObjects.tooltipGroup.add(tooltip);
					blockName.listening(true);

					blockName.on('mouseenter', () => {
						tooltip.show();
						this.tooltipsLayer.draw();
					});

					blockName.on('mouseleave', () => {
						tooltip.hide();
						this.tooltipsLayer.draw();
					});
				}

				if (b.description) {
					const descriptionTooltip = this.canvasDrawerService.createCanvasBlockDescriptionTooltip(
						b,
						b.description,
						this.mainCanvas.nativeElement
					);
					stand.layerObjects.tooltipGroup.add(descriptionTooltip);
					blockDescriptionIcon.listening(true);

					blockDescriptionIcon.on('mouseenter', () => {
						descriptionTooltip.show();
						this.tooltipsLayer.draw();
					});

					blockDescriptionIcon.on('mouseleave', () => {
						descriptionTooltip.hide();
						this.tooltipsLayer.draw();
					});
				}

				sizes.find('.size-area').forEach((node: Konva.Rect) => {
					node.on('mousedown', () => {
						this.resizeMode = true;
					});

					node.on('mouseup', () => {
						this.resizeMode = false;
					});

					node.on('dragstart', () => {
						this.activity.setProductMove(true);
						this.selectedSide = node.id() as Side;

						blockRect.show();
						this.canvasDrawerService.getCanvasBlockBorders(b).hide();
						this.canvasDrawerService.getCanvasBlockSizes(b).hide();
						this.canvasDrawerService.getCanvasBlockTitle(b).hide();
					});

					node.on('dragmove', () => {
						let height: number, width: number;
						const blockCopy: Block = b.copy();
						blockCopy.clearCache();

						this.blocksLayer.add(this.blockWidthLabel);
						this.blocksLayer.add(this.blockHeightLabel);
						this.blocksLayer.add(this.blockPercentLabel);

						this.blockWidthLabel.show();
						this.blockHeightLabel.show();
						this.blockPercentLabel.show();

						switch (node.id()) {
							case 'bottom':
								height = UnitConverter.convertAndRoundToSaveValue(
									(node.y() + node.height() / 2) / scale,
									'distance', 'inches', 3
								);
								if (height < this.minBlockSize) height = this.minBlockSize;
								blockRect.height(height * scale);
								blockCopy.height = height;
								break;

							case 'top':
								height = UnitConverter.convertAndRoundToSaveValue(
									(canvasBlock.height() - node.y() - node.height() / 2) / scale,
									'distance', 'inches', 3
								);
								if (height < this.minBlockSize) height = this.minBlockSize;
								blockRect.height(height * scale);
								blockCopy.height = height;
								blockRect.y(Utils.round(0 - (blockRect.height() - canvasBlock.height())));
								blockCopy.begin[0].y = Utils.round((canvasBlock.y() + blockRect.y() - beginPoint.y - canvasOffset) / scale);
								break;

							case 'right':
								width = UnitConverter.convertAndRoundToSaveValue(
									(node.x() + node.width() / 2) / scale,
									'distance', 'inches', 3
								);
								if (width < this.minBlockSize) width = this.minBlockSize;
								blockRect.width(width * scale);
								blockCopy.width = width;
								break;

							case 'left':
								width = UnitConverter.convertAndRoundToSaveValue(
									(canvasBlock.width() - node.x() - node.width() / 2) / scale,
									'distance', 'inches', 3
								);
								if (width < this.minBlockSize) width = this.minBlockSize;
								blockRect.width(width * scale);
								blockCopy.width = width;
								blockRect.x(0 - (blockRect.width() - canvasBlock.width()));
								blockCopy.begin[0].x = Utils.round((canvasBlock.x() + blockRect.x() - beginPoint.x - canvasOffset) / scale);
								break;
						}

						if (blockCopy.image) {
							blockRect.fillPatternScale({
								x: blockRect.width() / blockRect.fillPatternImage().width,
								y: blockRect.height() / blockRect.fillPatternImage().height
							});
						}

						blockCopy.cache.elements = this.refreshBlockElements(blockCopy);
						this.blockHeightLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
						this.blockHeightLabel.getText().text(
							UnitConverter.getConvertValue(
								Math.abs(blockRect.height() / scale), 'distance', 'inches', 3
							).toString());
						this.blockWidthLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
						this.blockWidthLabel.getText().text(
							UnitConverter.getConvertValue(
								Math.abs(blockRect.width() / scale), 'distance', 'inches', 3
							).toString());
						this.blockPercentLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
						this.blockPercentLabel.getText().text(blockCopy.percent + '%');
						this.blockHeightLabel.setAttrs({
							x: canvasBlock.x() + blockRect.x() + this.blockHeightLabel.height(),
							y: canvasBlock.y() + blockRect.y() + blockRect.height() / 2 + this.blockHeightLabel.width() / 2
						});
						this.blockWidthLabel.setAttrs({
							x: canvasBlock.x() + blockRect.x() + blockRect.width() / 2 - this.blockWidthLabel.width() / 2,
							y: canvasBlock.y() + blockRect.y() + blockRect.height() - this.blockWidthLabel.height()
						});
						this.blockPercentLabel.setAttrs({
							x: canvasBlock.x() + blockRect.x(),
							y: canvasBlock.y() + blockRect.y()
						});

						this.blocksLayer.draw();
					});

					node.on('dragend', () => {
						if (!this.activity.selectedBlock() && !this.activity.activeBlocks().length) {
							node.off('dragend');
							this.mainCanvas.nativeElement.style.cursor = 'default';
							this.blockWidthLabel.hide();
							this.blockHeightLabel.hide();
							this.blockPercentLabel.hide();
							this.resizeMode = false;
							this.stopProductMove();
							return;
						}
						this.resizeMode = false;
						this.changeCanvasListening(false);
						if (this.activity.selectedBlock() == b) blockRect.hide();
						this.canvasDrawerService.getCanvasBlockBorders(b).show();
						this.canvasDrawerService.getCanvasBlockSizes(b).show();
						this.canvasDrawerService.getCanvasBlockTitle(b).show();

						this.blockWidthLabel.hide();
						this.blockHeightLabel.hide();
						this.blockPercentLabel.hide();

						let x: number = canvasBlock.x() + blockRect.x() - canvasOffset - beginPoint.x,
							y: number = canvasBlock.y() + blockRect.y() - canvasOffset - beginPoint.y;

						if (this.selectedSide == 'right') x += blockRect.width();
						if (this.selectedSide == 'bottom') y += blockRect.height();

						const point: Point = this.canvasGetPoint(x, y),
							elements: Array<Element> = b.elements,
							copy: Block = b.copy();

						let selectedSide = this.selectedSide;

						if (this.flipMode.active) {
							if (this.selectedSide == 'right') selectedSide = 'left';
							if (this.selectedSide == 'left') selectedSide = 'right';
							point.x = b.stand.Width - point.x;
						}

						copy.resize(point.x, point.y, selectedSide);

						const intersections = this.canDrawBlock(
							copy,
							stand.stand.blocks.filter((bl) => bl != b)
						);

						if (intersections.length) {
							switch (selectedSide) {
								case 'left':
									point.x = Math.max.apply(
										null,
										intersections
											.map((p) => p.getCoordinates().map((c) => c.x))
											.flat()
									);
									break;

								case 'right':
									point.x = Math.min.apply(
										null,
										intersections
											.map((p) => p.getCoordinates().map((c) => c.x))
											.flat()
									);
									break;

								case 'top':
									point.y = Math.max.apply(
										null,
										intersections
											.map((p) => p.getCoordinates().map((c) => c.y))
											.flat()
									);
									break;

								case 'bottom':
									point.y = Math.min.apply(
										null,
										intersections
											.map((p) => p.getCoordinates().map((c) => c.y))
											.flat()
									);
									break;
							}
						}

						b.resize(point.x, point.y, selectedSide);

						const normalized: Array<Element> = this.activity.normalizeElements(this.stands[0].stand, [...elements, ...b.elements]);

						const blockProductErrors = copy.setProducts();

						if (blockProductErrors.length) this.activity.showDeleteMessage$.next({ block: b, errors: blockProductErrors });

						if (this.activity.activeBlocks().length) this.activity.refreshActiveBlocks();

						this.activity.requestCanvasUpdateAsync(this.stands, normalized.map((el: Element) => el.id)).finally(() => {
							this.stopProductMove(true);
						});
						this.historyGenerate();
					});

					node.on('mouseenter', () => {
						this.mainCanvas.nativeElement.style.cursor = sizesCursor[node.id()];
					});

					node.on('mouseleave', () => {
						this.mainCanvas.nativeElement.style.cursor = 'default';
					});
				});
			}

			stand.layerObjects.boxGroup.add(canvasBlock);
		});

		if (this.activity.activeBlocks().length) this.canvasDrawerService.getCanvasBlock(this.activity.activeBlocks()[0]).moveToTop();
	}

	drawPosMaterials(stand: IPlanogram): void {
		for (let material of stand.stand.POSMaterials) {
			const canvasMaterial: Konva.Group = this.canvasDrawerService.createCanvasPOSMaterial({
				material: material,
				beginPoint: stand.beginPoint,
				scale: stand.scale,
				zoom: this.zoom,
				draggable: !this.modes.drawBlockMode() && !this.modes.equipmentEditMode(),
				active: this.activity.isPOSMaterialActive(material),
				canvasElement: this.mainCanvas.nativeElement,
				tooltipGroup: stand.layerObjects.tooltipGroup
			});

			canvasMaterial.off('click dragstart dragend');

			if (!this.modes.drawBlockMode() && !this.modes.equipmentEditMode()) {
				canvasMaterial.on('click', (event: Konva.KonvaEventObject<MouseEvent>) => {
					this.activity.setPOSMaterialActive(material, navigator.platform == 'MacIntel' ? event.evt.metaKey : event.evt.ctrlKey);
				});

				canvasMaterial.on('dragstart', () => {
					if (!this.activity.isPOSMaterialActive(material)) this.activity.setPOSMaterialActive(material);
				});

				canvasMaterial.on('dragend', () => {
					material.x = Utils.round((canvasMaterial.x() - stand.beginPoint.x - konvaSettings.canvasOffset) / stand.scale);
					material.y = Utils.round((canvasMaterial.y() - stand.beginPoint.y - konvaSettings.canvasOffset) / stand.scale);
					this.historyGenerate();
					this.activity.requestCanvasUpdate(this.stands);
				});

				const textLabel: Konva.Text = canvasMaterial.findOne<Konva.Text>('.pos-text-material-text');

				if (textLabel) {
					textLabel.off('dblclick');

					textLabel.on('dblclick', () => {
						textLabel.visible(false);
						if (!this.activity.isPOSMaterialActive(material)) this.activity.setPOSMaterialActive(material);

						const container: HTMLDivElement = this.mainStage.container();
						const textPosition: Point = textLabel.getAbsolutePosition();
						const rect: DOMRect = container.getBoundingClientRect();
						const rotation: number = textLabel.getAbsoluteRotation();
						const inputPosition: Point = {
							x: rect.left + textPosition.x,
							y: rect.top + textPosition.y,
						};

						const padding: number = 2;
						const textarea: HTMLTextAreaElement = document.createElement('textarea');
						textarea.value = textLabel.text();
						textarea.style.position = 'absolute';
						textarea.style.top = `${inputPosition.y}px`;
						textarea.style.left = `${inputPosition.x}px`;
						textarea.style.height = `${textLabel.height()}px`;
						textarea.style.fontSize = `${textLabel.fontSize()}px`;
						textarea.style.fontFamily = textLabel.fontFamily();
						textarea.style.fontStyle = textLabel.fontStyle();
						textarea.style.lineHeight = textLabel.lineHeight().toString();
						textarea.style.color = textLabel.fill().toString();
						textarea.style.padding = padding + 'px';
						textarea.style.boxSizing = 'border-box';
						textarea.style.margin = '0';
						textarea.style.outline = 'none';
						textarea.style.zIndex = '1000';
						textarea.style.transformOrigin = 'top left';
						textarea.style.transform = `rotate(${rotation}deg)`;
						textarea.style.overflow = 'hidden';
						textarea.style.resize = 'none';
						textarea.style.whiteSpace = 'pre-wrap';
						textarea.style.maxWidth = `${material.width * stand.scale}px`;

						document.body.appendChild(textarea);
						textarea.focus();

						textarea.addEventListener('input', () => {
							if (textarea.value.length > GlobalSettings.MORE_SYMBOL_VALIDATION) {
								textarea.value = textarea.value.slice(0, GlobalSettings.MORE_SYMBOL_VALIDATION);
							}

							textLabel.text(textarea.value);
							textLabel.width(null);
							textarea.style.height = `${textarea.scrollHeight}px`;

							if (textLabel.width() > material.width * stand.scale) {
								textLabel.width(material.width * stand.scale);
							}

							canvasMaterial.findOne('.pos-material-border').setAttrs({
								width: textLabel.width(),
								height: textLabel.height()
							});
							canvasMaterial.width(textLabel.width());
							rotationCircle.setAttrs({
								x: textLabel.width(),
								y: textLabel.height()
							});
						});

						textarea.addEventListener('keydown', (e) => {
							if (['Enter', 'Escape'].includes(e.key)) {
								textarea.blur();
								e.stopPropagation();
							}
						});

						textarea.addEventListener('blur', () => {
							const prevValue: string = material.name;
							const value: string = textarea.value || prevValue;
							textLabel.text(value);
							document.body.removeChild(textarea);
							material.name = textLabel.text();
							textLabel.visible(true);
							canvasMaterial.findOne('.pos-material-border').width(textLabel.width());
							canvasMaterial.width(textLabel.width());
							rotationCircle.setAttrs({
								x: textLabel.width(),
								y: textLabel.height()
							});
							clearInterval(checkZoomInterval);
							if (prevValue !== material.name) this.historyGenerate();
							this.activity.setPOSMaterialActive(material);
						});

						const currentPixelRatio: number = window.devicePixelRatio;
						const currentZoom: number = this.zoom;
						const checkZoomInterval = setInterval(() => {
							if (window.devicePixelRatio !== currentPixelRatio || this.zoom !== currentZoom) textarea.blur();
						}, 500);
					});
				}

				const rotationCircle: Konva.Circle = canvasMaterial.findOne<Konva.Circle>('.pos-material-rotation-circle');

				if (rotationCircle) {
					rotationCircle.off('mouseenter mouseleave dragmove dragend');

					rotationCircle.on('mouseenter', () => this.showRotateCursor = true);

					rotationCircle.on('mouseleave', () => this.showRotateCursor = false);

					rotationCircle.on('dragmove', (event: Konva.KonvaEventObject<MouseEvent>) => {
						this.showRotateCursor = true;
						const x: number = event.evt.offsetX;
						const y: number = event.evt.offsetY;
						const rotationXPoint: number = material.x * stand.scale + konvaSettings.canvasOffset + stand.beginPoint.x;
						const rotationYPoint: number = material.y * stand.scale + konvaSettings.canvasOffset + stand.beginPoint.y;
						const radians: number = Math.atan2(x - rotationXPoint, y - rotationYPoint);
						let angle: number = Math.round(radians * (180 / Math.PI) * -1 + 90);
						angle -= Math.round(Math.atan(canvasMaterial.height() / canvasMaterial.width()) * 180 / Math.PI);

						if (angle > 85 && angle < 95) angle = 90;
						if (angle < -85 && angle > -95) angle = -90;
						if (angle < 5 && angle > -5) angle = 0;
						if (angle < 185 && angle > 175) angle = 180;
						if (angle > 180) angle -= 360;

						// the point where the product would start if the rotation angle were 0
						const baseCorePoint: [number, number] = Utils.rotatePoint(
							[rotationXPoint, rotationYPoint],
							[canvasMaterial.x(), canvasMaterial.y()],
							-canvasMaterial.rotation()
						);

						// the point where the product will start after changing the angle of rotation
						const newCorePoint: [number, number] = Utils.rotatePoint(
							[rotationXPoint, rotationYPoint],
							baseCorePoint,
							angle
						);

						canvasMaterial.setAttrs({
							x: newCorePoint[0],
							y: newCorePoint[1]
						});
						canvasMaterial.rotation(angle);
						rotationCircle.setAttrs({
							x: canvasMaterial.width(),
							y: canvasMaterial.height()
						});
					});

					rotationCircle.on('dragend', () => {
						this.showRotateCursor = false;
						let angle: number = canvasMaterial.rotation();
						if (material.angle === angle) return;
						material.angle = angle;
						this.activity.setPOSMaterialActive(material);
					});
				}
			}

			stand.layerObjects.boxGroup.add(canvasMaterial);
		}
	}

	static reverseCoordinates(pointBegin, pointEnd) {
		let bpX = pointBegin.x,
			bpY = pointBegin.y,
			lpX = pointEnd.x,
			lpY = pointEnd.y,
			delta;

		if (bpX > lpX) {
			delta = Math.abs(bpX - lpX);
			bpX = lpX;
			lpX = bpX + delta;
		}
		if (bpY > lpY) {
			delta = Math.abs(bpY - lpY);
			bpY = lpY;
			lpY = bpY + delta;
		}
		return { x1: bpX, y1: bpY, x2: lpX, y2: lpY };
	}

	canvasDrawElement(stand, element: Element) {
		const canvasElement: Konva.Group = this.canvasDrawerService.createCanvasEquipmentElement(
			{
				element: element,
				beginPoint: stand.beginPoint,
				scale: stand.scale,
				active: false,
				flip: this.flipMode.active
			},
			stand.stand
		);

		if (this.transparentBoxesMode) {
			canvasElement.find('.back-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
			canvasElement.find('.front-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
		}

		if (this.activity.targetElement() == element) {
			this.canvasDrawerService.setEquipmentElementHovered(element);
		} else {
			this.canvasDrawerService.setEquipmentElementInactive(element);
		}

		canvasElement.off('click dblclick mouseenter mouseleave');
		canvasElement.on('click', () => {
			if (this.modes.equipmentEditMode()) {
				if (element.parent.is(...DRAGGABLE_ELEMENTS.front)) {
					this.activity.setSelectedElement(element.id);
					this.canvasDrawDistanceLines(element);
				} else {
					this.activity.clearSelectedElement();
					this.canvasDrawDistanceLines();
				}
			}
			if (['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'HangBar', 'Hook', 'Perforation', 'PerforationWall'].includes(element.parent.type)) {
				this.canvasDrawerService.setEquipmentElementInactive(this.activity.targetElement());
				this.activity.setTargetElement(element);
			}
		});
		canvasElement.on('dblclick', () => this.openDetailedElementWidget(stand, element));

		if (this.modes.equipmentEditMode() && element.parent.is(...DRAGGABLE_ELEMENTS.front)) {
			let relatedElements: Array<Element> = [],
				relatedProducts: Array<ProductGroup> = [],
				xMovement: number = 0,
				yMovement: number = 0,
				prevX: number = 0,
				prevY: number = 0;

			canvasElement.draggable(true);
			canvasElement.off('dragstart dragmove dragend');
			canvasElement.on('dragstart', () => {
				if (['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'HangBar', 'Hook', 'Perforation', 'PerforationWall'].includes(element.parent.type)) {
					this.canvasDrawerService.setEquipmentElementInactive(this.activity.targetElement());
					this.activity.setTargetElement(element);
				}
				relatedElements = element.relatedElements;
				relatedProducts = [...element.products, ...relatedElements.map((el: Element) => el.products).flat()].flat();
				prevX = canvasElement.x();
				prevY = canvasElement.y();
				this.activity.setSelectedElement(element.id);
				this.canvasDrawDistanceLines(element);

				if (tooltip) {
					tooltip.hide();
					this.tooltipsLayer.draw();
				}
			});
			canvasElement.on('dragmove', () => {
				const dependElement: Konva.Group = this.canvasDrawerService.getCanvasEquipmentElement(element.depend);

				if (element.depend?.parent.is(EquipmentElements.CentralPillar) || element.shapes[0].type == 'trapeze') {
					canvasElement.x(prevX);
				} else {
					if (canvasElement.x() < dependElement.x()) canvasElement.x(dependElement.x());
					if (canvasElement.x() + canvasElement.width() > dependElement.x() + dependElement.width()) {
						canvasElement.x(dependElement.x() + dependElement.width() - canvasElement.width());
					}
				}
				if (element.parent.is(EquipmentElements.CentralPillar)) {
					canvasElement.y(prevY);
				} else {
					if (canvasElement.y() < dependElement.y()) canvasElement.y(dependElement.y());
					if (canvasElement.y() + canvasElement.height() > dependElement.y() + dependElement.height()) {
						canvasElement.y(dependElement.y() + dependElement.height() - canvasElement.height());
					}
				}

				relatedElements.forEach((el: Element) => {
					const elem: Konva.Group = this.canvasDrawerService.getCanvasEquipmentElement(el);

					elem.offsetX(prevX - canvasElement.x());
					elem.offsetY(prevY - canvasElement.y());
				});
				relatedProducts.forEach((pg: ProductGroup) => {
					const productGroup: Konva.Group = this.canvasDrawerService.getCanvasProduct(pg);

					productGroup.offsetX(prevX - canvasElement.x());
					productGroup.offsetY(prevY - canvasElement.y());
				});
				this.canvasDrawDistanceLines(element,{ x: 0, y: 0 }, { x: -prevX + canvasElement.x(), y: -prevY + canvasElement.y() });
				this.planogramLayer.batchDraw();
			});
			canvasElement.on('dragend', () => {
				const dependElement: Konva.Group = this.canvasDrawerService.getCanvasEquipmentElement(element.depend);

				if (element.depend?.parent.is(EquipmentElements.CentralPillar) || element.shapes[0].type == 'trapeze') {
					canvasElement.x(prevX);
				} else {
					if (canvasElement.x() < dependElement.x()) canvasElement.x(dependElement.x());
					if (canvasElement.x() + canvasElement.width() > dependElement.x() + dependElement.width()) {
						canvasElement.x(dependElement.x() + dependElement.width() - canvasElement.width());
					}
				}
				if (element.parent.is(EquipmentElements.CentralPillar)) {
					canvasElement.y(prevY);
				} else {
					if (canvasElement.y() < dependElement.y()) canvasElement.y(dependElement.y());
					if (canvasElement.y() + canvasElement.height() > dependElement.y() + dependElement.height()) {
						canvasElement.y(dependElement.y() + dependElement.height() - canvasElement.height());
					}
				}

				xMovement = Utils.round((canvasElement.x() - prevX) / this.scale);
				yMovement = Utils.round((prevY - canvasElement.y()) / this.scale);

				if (
					element.parent.is(...[EquipmentElements.Shelf, EquipmentElements.AngleShelf, EquipmentElements.HangBar])
					&& element.depend.isExtensionActive(ExtensionType.Rails)
				) {
					const anchorPoint: number = Utils.getNearestPoint(
						elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element.depend),
						element.getBoundingY().min + yMovement
					);

					yMovement = Utils.round(yMovement - (element.getBoundingY().min + yMovement - anchorPoint));
				}

				if (element.parent.is(EquipmentElements.Hook) && element.depend.parent.is(EquipmentElements.HangBar)) {
					yMovement = 0;
				}

				element.shapes[0].drag(xMovement, yMovement);

				if (element.parent.is(EquipmentElements.AngleShelf)) {
					(element.shapes[0] as Rectangle).updateRotationPoint(stand.stand, element);
				}

				relatedElements.forEach((el: Element) => el.shapes[0].drag(xMovement, yMovement));
				relatedProducts.forEach((pg: ProductGroup) => pg.move(xMovement, yMovement));
				stand.stand.blocks.forEach((block: Block) => block.clearCache());
				this.activity.normalizeElements(stand.stand);
				this.historyGenerate();
				this.activity.requestCanvasUpdate();
			});
		}

		if (['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'HangBar', 'Hook'].includes(element.parent.type)) {
			canvasElement.on('mouseenter', () => {
				this.canvasDrawerService.setEquipmentElementHovered(element);
				this.planogramLayer.draw();
			});
			canvasElement.on('mouseleave', () => {
				if (element == this.activity.targetElement()) return;

				this.canvasDrawerService.setEquipmentElementInactive(element);
				this.planogramLayer.draw();
			});
		}
		// додавання логіки тут

		const tooltip: Konva.Label = this.canvasDrawerService.createCanvasEquipmentElementTooltip(
			{
				element: element,
				beginPoint: stand.beginPoint,
				scale: stand.scale,
				active: false,
				flip: this.flipMode.active
			},
			stand.stand,
			{
				SHELF: this.translate.instant('SHELF'),
				HANG_BAR: this.translate.instant('HANG_BAR'),
				BOX: this.translate.instant('BOX'),
				ONE_HOOK: this.translate.instant('ONE_HOOK'),
				LENGTH: this.measurementUnitPipe.transform(this.translate.instant('LENGTH'), 'distance'),
				DEPTH: this.measurementUnitPipe.transform(this.translate.instant('DEPTH'), 'distance'),
				LINEAR_METERS: this.translate.instant('LINEAR_METERS', { unit: this.measurementUnitTranslatePipe.transform('distance', false, 'feet') }),
				FREE_SPACE_ON_THE_SHELF_M: this.measurementUnitPipe.transform(this.translate.instant('FREE_SPACE_ON_THE_SHELF_M'), 'distance'),
				FREE_SPACE_ON_THE_HANG_BAR_M: this.measurementUnitPipe.transform(this.translate.instant('FREE_SPACE_ON_THE_HANG_BAR_M'), 'distance'),
				FREE_SPACE_IN_THE_BOX_M: this.measurementUnitPipe.transform(this.translate.instant('FREE_SPACE_IN_THE_BOX_M'), 'distance'),
				FREE_SPACE_ON_THE_HOOK_M: this.measurementUnitPipe.transform(this.translate.instant('FREE_SPACE_ON_THE_HOOK_M'), 'distance'),
				QUANTITY_OF_LAID_OUT_SKU: this.translate.instant('QUANTITY_OF_LAID_OUT_SKU')
			}
		);

		if (tooltip) {
			stand.layerObjects.tooltipGroup.add(tooltip);

			canvasElement.on('mouseenter', () => {
				tooltip.show();
				this.tooltipsLayer.draw();
			});
			canvasElement.on('mouseleave', () => {
				tooltip.hide();
				this.tooltipsLayer.draw();
			});
		}

		if (element.parent.is(EquipmentElements.Box, EquipmentElements.SlicedBox)) {
			stand.layerObjects.planogramGroup.add(canvasElement);
		} else {
			stand.layerObjects.planogramGroup.add(canvasElement);
		}

		return true;
	}

	pasteProducts() {
		if (!this.activity.targetElement() || !this.productClipboard?.length) return;

		const element = this.activity.targetElement(),
			siblings = element.getSiblingElements,
			elementSegments = element.getSiblingSegments(),
			groupsOnShelf = element.products.sort(
				(a, b) => b[0].begin.x - a[0].begin.x
			),
			normalizeList = [],
			pgAccordance = [];

		if (!elementSegments.length)
			elementSegments.push({
				min: element.getBoundingX().min,
				max: element.getBoundingX().max
			});

		if (
			this.productClipboard.some((pg) => pg.isLooseForm) &&
			element.constructor.name != 'BoxElement'
		) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'WEIGHT_PRODUCT_ON_SHELF_ERROR'
			});
		}

		let elementMin = Utils.max([
				Utils.min(elementSegments.map((seg) => seg.min)),
				element.shapes[0].begin.x
			]),
			beginX = groupsOnShelf.length
				? Element.calculateGroupMax(groupsOnShelf[0])
				: elementMin,
			activeProducts = this.productClipboard.sort(
				(a, b) =>
					(['Hook', 'Hole'].includes(a.element.parent.type) &&
					['Hook', 'Hole'].includes(b.element.parent.type)
						? 0
						: a.begin.x - b.begin.x) ||
					(['Hook', 'Hole'].includes(element.parent.type)
						? a.begin.z - b.begin.z
						: b.begin.z - a.begin.z) ||
					(['Hook', 'Hole'].includes(a.element.parent.type) &&
					['Hook', 'Hole'].includes(b.element.parent.type)
						? 0
						: a.begin.y - b.begin.y)
			);

		if (['Perforation', 'PerforationWall'].includes(element.parent.type)) {
			const holes = this.getFreeHoles(element);
			activeProducts = activeProducts
				.slice(0, holes.length)
				.map((product, idx) => {
					const productCopy = product.copy();

					productCopy.Element = holes[idx];
					productCopy.begin.x = holes[idx].shapes[0].begin.x;
					productCopy.dependProduct = null;
					productCopy.qty = 1;
					productCopy.placementDepth = 1;
					productCopy.placementHeight = 1;

					if (!normalizeList.includes(holes[idx])) normalizeList.push(holes[idx]);
					return productCopy;
				});
		} else {
			activeProducts = activeProducts.map((product) => {
				const productCopy = product.copy();

				if (element.is(HangBarElement)) {
					productCopy.rotation = ProductGroupRotation.Side;
					productCopy.image.rotation = 0;
				}

				if (!this.productClipboard.includes(product.dependProduct)) {
					productCopy.dependProduct = null;
				} else {
					const pgIndex = pgAccordance.findIndex(
						(item) => item.prev == product.dependProduct
					);
					productCopy.dependProduct =
						pgIndex >= 0 ? pgAccordance[pgIndex].current : null;
				}

				if (productCopy.dependProduct) {
					productCopy.Element = productCopy.dependProduct.element;
					productCopy.begin.x = productCopy.dependProduct.begin.x;
					productCopy.begin.y = productCopy.dependProduct.getBoundingY().max;
				} else {
					const sameXElement = this.productClipboard.filter(
						(p) =>
							p.element == product.element &&
							p.begin.x == product.begin.x &&
							p.product.id !== product.product.id &&
							!p.dependProduct
					);

					let correctElement = element.shapes[0].isPointOn(beginX)
						? element
						: siblings.find((el) => el.shapes[0].isPointOn(beginX));
					if (!correctElement)
						correctElement = [element, ...siblings].sort(
							(a, b) => a.shapes[0].begin.x - b.shapes[0].begin.x
						)[0];
					productCopy.Element = correctElement;

					productCopy.begin.x = beginX;

					if (element.parent.type == 'Hook') {
						productCopy.begin.x = element.shapes[0].begin.x;
						productCopy.dependProduct = null;
						productCopy.qty = productCopy.placementDepth;
						productCopy.placementHeight = 1;
					}

					if (sameXElement.length && element.parent.type != 'Hook') {
						const existProduct = sameXElement
							.filter((pg) => pgAccordance.some((item) => item.prev == pg))
							.map((pg) => pgAccordance.find((item) => item.prev == pg).current)
							.sort((a, b) => a.begin.z - b.begin.z);

						if (existProduct.length) {
							productCopy.Element = existProduct[0].element;
							productCopy.begin.x = existProduct[0].begin.x;
							productCopy.begin.z = existProduct[0].getBoundingZ().min;
						}
					}
				}

				pgAccordance.push({ prev: product, current: productCopy });
				if (element.parent.type != 'Hook')
					beginX = Utils.max([beginX, productCopy.getBoundingX().max]);
				return productCopy;
			});
		}

		if (activeProducts.some((pg) => !pg)) return;

		if (activeProducts.some((pg) => !element.canDrop(pg))) return;

		// remove products if there is a weight product in the box or active product is weight
		if (
			element.constructor.name == 'BoxElement' &&
			(element as BoxElement).isNeedRemoveProducts(this.productClipboard[0])
		) {
			element.stand.products = element.stand.products.filter(
				(pg) => !Utils.to1DArray(element.products).includes(pg)
			);
		}

		// remove depend products if product target is hook
		if (element.constructor.name == 'HookElement') {
			activeProducts.forEach((pg) => (pg.dependProduct = null));
		}

		activeProducts.forEach((pg) => {
			element.stand.products.push(pg);
		});

		element.shiftProductGroups(
			Utils.groupByChildProperty(activeProducts, 'begin', 'x'),
			activeProducts
		);

		const elements: Array<Element> = this.activity.normalizeElements(element.stand, [element, ...normalizeList]);
		this.activity.setNumbersForPerforation$.next(elements);

		this.activity.requestCanvasUpdateAsync(this.stands, elements.map((el: Element) => el.id)).finally(() => {
			this.activity.setProductActive();
			activeProducts.forEach((pg) => this.activity.setProductActive(pg, true));
		});
		this.historyGenerate();
	}

	pastePOSMaterials(): void {
		if (!this.POSMaterialsClipboard.length) return;
		for (let material of this.POSMaterialsClipboard) {
			const newMaterial: POSMaterial = material.copy();
			newMaterial.y += 0.1;
			this.stands[0].stand.POSMaterials.push(newMaterial);
		}
		this.activity.requestCanvasUpdate(this.stands);
		this.historyGenerate();
	}

	canvasDrawProductGroup(stand, productGroup: ProductGroup) {
		let dataObject: any = {};
		if (this.activity.isProductHighlighted(productGroup.product.id)) {
			dataObject = this.highlightProductsService.getCorrectHighlight(
				this.activity.highlightValue(),
				this.activity.highlightedProducts(),
				this.stands[0].stand.products,
				productGroup
			);
		} else if (this.activity.isProductRotated(productGroup.product.id)) {
			dataObject = this.activity.rotatedProducts()[productGroup.product.id];
		} else if (this.activity.isProductInRule(productGroup.product.id)) {
			const object = this.activity.layoutRulesProducts()[productGroup.product.id],
				property = ['ShelfElement', 'PalletElement'].includes(
					productGroup.element.constructor.name
				)
					? Utils.getElementIndex(productGroup.element, stand.stand).toString()
					: 'default';
			dataObject = object[property] || object['default'] || {};
		}

		if (this.activity.isProductActive(productGroup) || this.activity.isProductActive(productGroup.dependProduct)) {
			this.canvasDrawerService.getCanvasProduct(productGroup).destroyChildren();
		}

		const canvasProduct: Konva.Group = this.canvasDrawerService.createCanvasProduct({
			productGroup: productGroup,
			stand: stand.stand,
			scale: stand.scale,
			beginPoint: stand.beginPoint,
			articleMode: this.modes.articleMode(),
			sequenceNumberMode: this.modes.sequenceNumberMode(),
			flipMode: this.flipMode.active,
			dataObject: dataObject,
			showStockQty: this.activity.labelsState().stockQty,
			showPrices: this.activity.labelsState().price,
			showAbcTurnover: this.activity.labelsState().abcTurnover,
			showAbcReceipts: this.activity.labelsState().abcReceipts,
			zoom: this.zoom,
			callback: () => {
				if (this.activity.isProductActive(productGroup)) {
					this.canvasDrawerService.setCanvasProductActive(productGroup, canvasProduct);
				}
				stand.layerObjects.planogramGroup.draw();
			}
		});

		canvasProduct.off(
			'click dblclick dragstart dragmove dragend mouseenter mouseleave'
		);

		stand.layerObjects.planogramGroup.add(canvasProduct);

		if (this.activity.isProductActive(productGroup)) {
			this.canvasDrawerService.setCanvasProductActive(productGroup, canvasProduct);
		}

		let prevX: number = canvasProduct.x(), prevY: number = canvasProduct.y();

		canvasProduct.draggable(!this.modes.drawBlockMode() && !this.modes.equipmentEditMode());

		if (!this.modes.equipmentEditMode()) {
			canvasProduct.on('click', (event) => {
				this.activity.setProductActive(productGroup, navigator.platform == 'MacIntel' ? event.evt.metaKey : event.evt.ctrlKey);
				this.activity.syncProducts(productGroup, event.evt);
			});

			canvasProduct.on('dblclick', () => this.activity.changeProductPosition(productGroup));
		}

		canvasProduct.on('dragstart', (event) => {
			this.activity.setProductMove(true);
			this.clearDragOutsidePreviews();

			let dependedProducts = [];

			if (!this.activity.isProductActive(productGroup)) {
				this.activity.clearActiveProducts();
			}

			if (this.activity.activeProducts().length) {
				[...this.activity.activeProducts().filter((el) => el !== productGroup), productGroup].forEach((el) => {
					if (this.activity.isProductActive(el.dependProduct)) return;
					if (el.element.parent.type != 'Hook') {
						dependedProducts = [
							...dependedProducts,
							...Utils.findDependProducts(el, this.stands[0].stand.products)
						];
					}
				});
			} else {
				this.activity.clearActiveProducts();

				dependedProducts =
					productGroup.element.parent.type == 'Hook'
						? []
						: Utils.findDependProducts(
							productGroup,
							this.stands[0].stand.products
						);
			}

			this.canvasDrawerService.getCanvasProductTooltip(productGroup).hide();

			if (dependedProducts.length) {
				[productGroup, ...dependedProducts]
					.filter((pg) => !this.activity.isProductActive(pg))
					.forEach((pg) => this.activity.setProductActive(pg, true));
			} else {
				if (!this.activity.isProductActive(productGroup)) this.activity.setProductActive(productGroup, true);
			}

			this.activity.activeProducts()
				.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingZ().max - b.getBoundingZ().max)
				.map((pg: ProductGroup) => this.canvasDrawerService.getCanvasProduct(pg))
				.forEach((canvasProduct: Konva.Group) => {
					canvasProduct.moveToTop();
					canvasProduct.find('.hanger').forEach((canvasHanger: Konva.Group) => canvasHanger.hide());
				});

			this.activity.syncProducts(productGroup, event.evt);
			prevX = canvasProduct.x();
			prevY = canvasProduct.y();
		});

		canvasProduct.on('dragmove', () => {
			const diffX: number = canvasProduct.x() - prevX, diffY: number = canvasProduct.y() - prevY;

			this.activity.activeProducts()
				.filter((pg: ProductGroup) => pg != productGroup)
				.forEach((pg: ProductGroup) => {
					const konvaEl: Konva.Group = this.canvasDrawerService.getCanvasProduct(pg);
					konvaEl.x(konvaEl.x() + diffX);
					konvaEl.y(konvaEl.y() + diffY);
				});

			prevX = canvasProduct.x();
			prevY = canvasProduct.y();
			this.updateDragOutsidePreviews();
		});

		canvasProduct.on('dragend', () => {
			const sourceCanvas = this.mainCanvas?.nativeElement;
			const dropCanvases = Array.from(document.querySelectorAll('.canvas-container'))
				.filter((el) => el instanceof HTMLElement) as Array<HTMLElement>;
			const targetCanvas = sourceCanvas
				? this.dragOutsideService.emitDropIfTargetDifferent(this.activity.activeProducts(), dropCanvases, sourceCanvas)
				: null;
			if (targetCanvas) return this.redrawCanvas();

			const planogram: IPlanogram = this.stands[0];
			const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
				.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);

			this.handleProductGroupDrop(behavior, planogram);
		});

		this.planogramEditTooltipService
			.buildTooltipText(
				productGroup,
				dataObject,
				this.sessionService.personalSettings.planogramEditTooltipFields,
				{ abcTurnover: this.activity.labelsState().abcTurnover, abcReceipts: this.activity.labelsState().abcReceipts }
			)
			.subscribe((tooltip: string) => {
				const tooltipLabel: Konva.Label = this.canvasDrawerService.createCanvasProductTooltip(
					productGroup,
					tooltip,
					this.mainCanvas.nativeElement
				);

				stand.layerObjects.tooltipGroup.add(tooltipLabel);
			});

		canvasProduct.on('mouseenter', () => {
			this.canvasDrawerService.getCanvasProductTooltip(productGroup).show();
			this.tooltipsLayer.batchDraw();
		});

		canvasProduct.on('mouseleave', () => {
			this.canvasDrawerService.getCanvasProductTooltip(productGroup).hide();
			this.tooltipsLayer.batchDraw();
		});
	}

	private redrawCanvas = () => {
		const elements: Array<Element> = Utils.unique(
			this.activity.activeProducts().map((pg: ProductGroup) => [pg.element, ...pg.element.relatedElements]).flat()
		);

		this.clearDragOutsidePreviews();
		this.canvasRender(this.stands, elements.map((el: Element) => el.id)).finally(() => {
			this.stopProductMove(true);
		});
	};

	async canvasRender(stands = [], elements: Array<string> = []) {
		this.canvasClear(stands, elements);

		try {
			this.standError = false;

			this.stands.forEach((stand) => {
				if (!stand.visible) return;

				if (!stands.length || stands.includes(stand)) {
					this.standError = stand.stand.products.some((pg) => pg.error);

					if (this.activity.labelsState().abcTurnover || this.activity.labelsState().abcReceipts) {
						stand.stand.products.forEach((pg) => {
							if (!this.abcData) return;

							pg.product.abc_turnover = (
								this.abcData['turnover'][pg.product.id] || {}
							).abc;
							pg.product.abc_receipts = (
								this.abcData['receipts'][pg.product.id] || {}
							).abc;
						});
					}

					this.canvasRenderEquipment(stand, elements);

					this.drawBlocks(stand);

					if (this.POSMaterialsMode) this.drawPosMaterials(stand);
				}

				if (this.modes.sizesMode()) this.canvasDrawSizes(stand);
			});

			this.planogramLayer.setZIndex(0);
			this.blocksLayer.setZIndex(1);
			this.sizesLayer.setZIndex(2);
			this.tooltipsLayer.setZIndex(3);
			this.toolsLayer.setZIndex(4);

			this.planogramLayer.batchDraw();
			this.blocksLayer.draw();
			this.sizesLayer.draw();
			this.tooltipsLayer.draw();
			this.toolsLayer.draw();

			this.findProductsOnStand();
		} catch (e) {
			console.log(e, 'render error');
			setTimeout(() => {
				this.zoomApply(this.zoom);
			});
		}
	}

	async canvasUpdate(stands: Array<IPlanogram> = [], ids: Array<string> = []) {
		if (this.showExecutionPage) return;

		if (this.canvasResize() || this.forceUpdateCanvas) {
			this.forceUpdateCanvas = false;
			await this.canvasRender();
		} else {
			await this.canvasRender(stands, ids);
		}
	}

	private updateActivePOSMaterialsOnCanvas(stands: Array<IPlanogram>): boolean {
		if (!this.POSMaterialsMode || !stands?.length) return false;

		const stand: IPlanogram = stands[0];
		if (!stand) return false;

		const activeMaterials: Array<POSMaterial> = this.activity.activePosMaterials();
		if (!activeMaterials.length) return true;

		for (const material of activeMaterials) {
			const canvasMaterial: Konva.Group = this.canvasDrawerService.getCanvasPOSMaterial(material);
			if (!canvasMaterial || !canvasMaterial.getLayer()) return false;

			canvasMaterial.setAttrs({
				x: material.x * stand.scale + stand.beginPoint.x + konvaSettings.canvasOffset,
				y: material.y * stand.scale + stand.beginPoint.y + konvaSettings.canvasOffset,
				rotation: material.angle
			});
		}

		this.blocksLayer.batchDraw();
		return true;
	}

	changeCanvasListening(listening: boolean) {
		this.planogramLayer.listening(listening);
		this.planogramLayer.batchDraw();
		this.blocksLayer.listening(listening);
		this.blocksLayer.batchDraw();
	}

	findProductsOnStand(): void {
		this.products.forEach((product: Product) => {
			product.active = this.stands[0].stand.products
				.some((pg: ProductGroup) => pg.product.id === product.id && pg.availability);
		});
	}

	canvasRenderEquipment(planogram: IPlanogram, elements: Array<string> = []) {
		(planogram.stand as any).orderedElements
			.filter((element: Element) => !element.is(HoleElement))
			.forEach((element: Element) => {
				if (!elements.length || elements.includes(element.id)) this.canvasDrawElement(planogram, element);
			});

		planogram.stand.products
			.sort((a: ProductGroup, b: ProductGroup) => a.begin.z - b.begin.z || a.begin.x - b.begin.x || a.begin.y - b.begin.y)
			.map((pg: ProductGroup) => {
				if (!elements.length || elements.includes(pg.element.id)) this.canvasDrawProductGroup(planogram, pg);
			});

		this.canvasDrawerService.setCorrectPlanogramOrder(planogram.stand, this.flipMode.active);

		if (this.modes.equipmentEditMode()) {
			this.canvasDrawDistanceLines(this.stands[0].stand.getElementById(this.activity.selectedElement()));
		}
	}

	canvasResize(): boolean {
		if (!this.stands[0]) return false;

		const containerWidth: number = this.canvasContainer.nativeElement.clientWidth;
		const containerHeight: number = this.canvasContainer.nativeElement.clientHeight;

		let standWidth: number = this.stands.filter((planogram: IPlanogram): boolean => planogram.visible).length
			> 1 ? containerWidth / 2 : containerWidth,
			needRender: boolean = false;

		this.stands
			.filter((planogram: IPlanogram): boolean => planogram.visible)
			.forEach((stand: any, idx: number): void => {
				let canvasBeginY: number;
				let bottomDistanceForLabels: number = this.zoom > 100 ? 0.12 : 0.08;
				const prevScale: number = stand.scale;
				const prevWidth: number = stand.width;
				const prevHeight: number = stand.height;
				const prevBeginPoint: Point = stand.beginPoint ? { ...stand.beginPoint } : { x: 0, y: 0 };

				const standBounds: IPlanogramBounds = Utils.calculatePlanogramBounds(stand.stand);
				const instrumentWidth: number = standBounds.width,
					instrumentHeight: number = Utils.round(standBounds.height + bottomDistanceForLabels);

				if (instrumentWidth / instrumentHeight > standWidth / containerHeight) {
					stand.scale = Math.floor(((standWidth - canvasOffset * 2) / instrumentWidth) * (this.zoom / 100));
				} else {
					stand.scale = Math.floor(((containerHeight - canvasOffset * 2) / instrumentHeight) * (this.zoom / 100));
				}

				canvasBeginY = 0;
				this.scale = stand.scale;
				stand.width = stand.scale * instrumentWidth;
				stand.height = stand.scale * instrumentHeight;
				needRender = needRender
					|| prevScale !== stand.scale
					|| prevWidth !== stand.width
					|| prevHeight !== stand.height;

				const offsetX: number = standBounds.minX < 0 ? Math.abs(standBounds.minX) * stand.scale : 0;
				const offsetY: number = (standBounds.maxY - stand.stand.Height) * stand.scale;

				if (idx == 0) {
					stand.beginPoint = {
						x: offsetX,
						y: canvasBeginY + offsetY
					};
				} else {
					const prevStand: IPlanogram = this.stands.filter((s: IPlanogram): boolean => s.visible)[idx - 1];
					stand.beginPoint = {
						x: prevStand.beginPoint.x + prevStand.width + 5 + offsetX,
						y: offsetY
					};
				}
				needRender = needRender
					|| prevBeginPoint.x !== stand.beginPoint.x
					|| prevBeginPoint.y !== stand.beginPoint.y;
			});

		const width: number = this.stands[0].width + canvasOffset * 2;
		const height: number = this.stands[0].height + canvasOffset * 2;

		this.stands
			.filter((s: IPlanogram): boolean => s.visible)
			.forEach((stand: IPlanogram): void => {
				const prevBeginPointY: number = stand.beginPoint?.y || 0;
				if (containerHeight > height) {
					stand.beginPoint.y += Math.ceil((containerHeight - height) / 2);
				}
				if (stand.height < height - canvasOffset * 2) {
					stand.beginPoint.y += height - canvasOffset * 2 - stand.height;
				}

				stand.beginPoint.y = Math.round(stand.beginPoint.y);

				needRender = needRender || stand.beginPoint.y !== prevBeginPointY;
			});

		const visibleStands: Array<IPlanogram> = this.stands.filter((planogram: IPlanogram): boolean => planogram.visible);

		if (containerWidth > width) {
			visibleStands.forEach((stand: IPlanogram, idx: number): void => {
				const prevBeginPointX: number = stand.beginPoint?.x || 0;
				const currentBounds: IPlanogramBounds = Utils.calculatePlanogramBounds(stand.stand);
				const baseOffsetX: number = currentBounds.minX < 0 ? Math.abs(currentBounds.minX) * stand.scale : 0;

				if (idx === 0) {
					stand.beginPoint.x = baseOffsetX + Math.ceil((containerWidth - width) / 2);
				} else {
					const prevStand: IPlanogram = visibleStands[idx - 1];
					stand.beginPoint.x = prevStand.beginPoint.x + prevStand.width + 5 + baseOffsetX;
				}

				stand.beginPoint.x = Math.round(stand.beginPoint.x);
				needRender = needRender || stand.beginPoint.x !== prevBeginPointX;
			});
		} else {
			visibleStands.forEach((stand: IPlanogram, idx: number): void => {
				const prevBeginPointX: number = stand.beginPoint?.x || 0;
				const currentBounds: IPlanogramBounds = Utils.calculatePlanogramBounds(stand.stand);
				const baseOffsetX: number = currentBounds.minX < 0 ? Math.abs(currentBounds.minX) * stand.scale : 0;

				if (idx === 0) {
					stand.beginPoint.x = baseOffsetX;
				} else {
					const prevStand: IPlanogram = visibleStands[idx - 1];
					stand.beginPoint.x = prevStand.beginPoint.x + prevStand.width + 5 + baseOffsetX;
				}

				stand.beginPoint.x = Math.round(stand.beginPoint.x);
				needRender = needRender || stand.beginPoint.x !== prevBeginPointX;
			});
		}

		const finalWidth: number = this.zoom == 100 ? containerWidth : containerWidth > width ? containerWidth : width;

		this.mainCanvas.nativeElement.style.width = `${finalWidth}px`;
		this.mainCanvas.nativeElement.style.height = `${containerHeight > height ? containerHeight : height}px`;

		this.mainStage.width(finalWidth);
		this.mainStage.height(containerHeight > height ? containerHeight : height);

		return needRender;
	}

	canvasDrawSizes(planogram: IPlanogram) {
		this.sizesLayer.destroyChildren();
		this.sizesLayer.add(...this.canvasDrawerService.createPlanogramSizes({
			element: null,
			beginPoint: planogram.beginPoint,
			scale: planogram.scale,
			zoom: this.zoom,
			active: false,
			flip: this.flipMode.active
		}, planogram.stand));
	}

	canvasDrawDistanceLines(element: Element = null, normalize: Point = { x: 0, y: 0 }, delta: Point = { x: 0, y: 0 }): void {
		this.toolsLayer.destroyChildren();

		if (element) {
			const distanceLines: Array<Konva.Line | Konva.Label> = this.canvasDrawerService.createEquipmentDistanceLines({
				element: element,
				beginPoint: this.stands[0].beginPoint,
				scale: this.stands[0].scale,
				normalize: normalize,
				active: true,
				flip: this.flipMode.active
			}, this.stands[0].stand, false, delta);

			if (distanceLines.length) this.toolsLayer.add(...distanceLines);
		}

		this.toolsLayer.draw();
	}

	changeBoxesTransparentMode(): void {
		this.transparentBoxesMode = !this.transparentBoxesMode;
		if (this.transparentBoxesMode) {
			this.mainStage.find('.back-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
			this.mainStage.find('.front-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
			this.mixPanelService.trackPage(MixpanelEvents.TransparentBoxes, { page: 'planogram edit' });
		} else {
			this.mainStage.find('.back-wall').forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillGreyColor));
			this.mainStage.find('.front-wall').forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillWhiteColor));
		}
	}

	changePOSMaterialsMode(): void {
		this.POSMaterialsMode = !this.POSMaterialsMode;
		void this.canvasRender();
	}

	toggleFlip(): void {
		this.flipMode.toggle();
		this.stands[0].flip = this.flipMode.active;
		void this.canvasRender();
	}

	toggleModel() {
		if (
			this.activeRule &&
			this.activeRule.hasOwnProperty('saved') &&
			!this.activeRule.saved
		) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					twoAnswers: true,
					label: 'RULES.MODAL.CLOSE_WARNING',
					confirmLabel: 'YES',
					cancelLabel: 'NO'
				}
			});

			dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
				if (!result.answer) return;

				this.activeRule = null;
				this.layoutRules = false;
				this.activity.clearLayoutRulesProducts();
				this.modelMode = !this.modelMode;
				this.recountStandLayoutWidth();
			});

			return;
		}

		this.layoutRules = false;
		this.activity.clearLayoutRulesProducts();
		this.modelMode = !this.modelMode;
		this.recountStandLayoutWidth();
	}

	async checkRuleProducts(rule: any, callback) {
		const getObj = (ids, level) => {
			if (level.group.type) {
				const list = [];
				level.children.forEach((child) => {
					const obj = child.id
						? Object.assign({}, ids, { [level.group.type]: Number(child.id) })
						: ids;
					list.push(...getObj(obj, child));
				});
				return list;
			}
			return Object.keys(ids).length ? [Object.assign({}, ids)] : [];
		};

		const groups = getObj({}, rule.conf.ruleData).filter((group) => {
			if (Object.keys(group).length == 1 && group.hasOwnProperty('shelf'))
				return false;
			return !this.products.some((product) => {
				let exists = [];
				if (group.brand) exists.push(product.brandsUniqueId == group.brand);
				if (group.category) exists.push(product.categoriesUniqueIds.includes(group.categories));
				if (group.supplier) exists.push(product.suppliersUniqueId == group.supplier);
				if (group.characteristic)
					exists.push(
						Utils.to1DArray(product.markers).some(
							(cha) => cha.id == group.characteristic
						)
					);
				return exists.every((state) => state);
			});
		});

		if (groups.length) {
			if (
				!this.planogramConf.shops.length &&
				groups.some((group) => group.hasOwnProperty('supplier'))
			) {
				const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
					width: '810px',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: 'RULES.MODAL.SUPPLIERS_GROUPS_INACTIVE_PLAN',
						twoAnswers: false,
						confirmLabel: 'CONTINUE',
						labelTranslateParams: {
							rule_name: rule.name
						}
					}
				});
				dialogRef.afterClosed().subscribe();
			} else {
				if (!this.products.length) {
					this.loader = true;
					const products = await this.productsService.getLayoutRulesProducts(
						this.stands[0].planogram_id,
						groups
					);
					await this.addProducts(
						products.map((p) => new Product(p)),
						false
					);
					this.loader = false;
					callback();
				} else {
					const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
						width: '574px',
						autoFocus: false,
						backdropClass: 'back-drop',
						data: {
							label: 'RULES.MODAL.PRODUCTS',
							twoAnswers: true,
							confirmLabel: 'YES',
							cancelLabel: 'NO'
						}
					});

					dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
						if (result.answer) {
							this.loader = true;
							const products = await this.productsService.getLayoutRulesProducts(
								this.stands[0].planogram_id,
								groups
							);
							await this.addProducts(
								products.map((p) => new Product(p)),
								false
							);
							this.loader = false;
						}
						callback();
					});
				}
			}
		} else {
			if (!this.products.length) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'RULES.POPUP.CANT_APPLY'
				});
			} else {
				callback();
			}
		}
	}

	async editRule(rule: any) {
		if (this.stands[0].stand.availableElements.some((el: Element) => el.is(HangBarElement))) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'RULES.HANG_BAR_WARNING'
			});
		}

		if (this.stands[0].stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'RULES.ISLAND_RACK_WARNING'
			});
		}

		await this.checkRuleProducts(rule, () => this.toggleLayoutRules(rule));
	}

	async updateRuleProducts(event: any) {
		await this.checkRuleProducts(event.rule, () => this.layoutRulesService.updateRule$.next());
	}

	editEquipmentHandler(trigger: MatMenuTrigger): void {
		if (!this.modes.equipmentEditMode()) this.mixPanelService.trackPage(MixpanelEvents.EquipmentEditOnPlanogram);
		if (this.modes.equipmentEditMode() && this.saveAble) {
			trigger.openMenu();
		} else {
			this.toggleEquipmentEditMode();
		}
	}

	toggleEquipmentEditMode(): void {
		this.modes.toggleEquipmentEditMode();
		this.activity.clearActivePanelProducts();
		this.activity.setProductActive();
		this.activity.clearSelectedBlock();
		this.activity.setBlockActive();
		this.activity.setPOSMaterialActive();
		this.analysisOpen = false;
		if (this.modes.equipmentEditMode()) {
			this.saveAction(true).finally();
		} else {
			this.activity.clearSelectedElement();
		}
		this.history.length = 0;
		this.historyIndex = 0;
		this.historyGenerate();
		this.saveAble = false;
		this.beginState = this.history[this.historyIndex];
		this.activity.requestCanvasUpdate();
	}

	toggleLayoutRules(rule?: any) {
		if (
			!rule &&
			this.activeRule &&
			this.activeRule.hasOwnProperty('saved') &&
			!this.activeRule.saved
		) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					twoAnswers: true,
					label: 'RULES.MODAL.CLOSE_WARNING',
					confirmLabel: 'YES',
					cancelLabel: 'NO'
				}
			});

			dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
				if (!result.answer) return;

				this.modelMode = false;

				this.activeRule = rule;
				this.layoutRules = !this.layoutRules;

				if (this.layoutRules) this.toggleLayoutRulesPanel(true);
				if (!this.layoutRules) this.activity.clearLayoutRulesProducts();

				this.recountStandLayoutWidth();
			});

			return;
		}

		this.modelMode = false;

		this.activeRule = rule;
		this.layoutRules = !this.layoutRules;

		if (this.layoutRules) {
			this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleCreate);
			this.activity.closePanel();
			if (this.stands[0].stand.availableElements.some((el: Element) => el.is(HangBarElement))) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'RULES.HANG_BAR_WARNING'
				});
			}

			if (this.stands[0].stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'RULES.ISLAND_RACK_WARNING'
				});
			}
		}
		if (!this.layoutRules) this.activity.clearLayoutRulesProducts();

		this.recountStandLayoutWidth();
	}

	toggleLayoutRulesPanel(forceClose: boolean = false): void {
		if (!this.sessionService.hasFeature(ServiceFeatures.LayoutRules)) {
			this.mixPanelService.trackPage(MixpanelEvents.BlockedFunctionalPage, { type: 'layoutRules' });

			let functionalType: string;
			this.translate
				.get('INFO_MODAL.USE_IN_TARIFF', { tariff: 'Enterprise' })
				.subscribe((text: string) => functionalType = text);

			this.dialog
				.open<InfoModalComponent, IInfoModalData<InfoModalComponent>>(InfoModalComponent, {
					width: '60vw',
					height: '80vh',
					autoFocus: false,
					backdropClass: 'back-drop',
					panelClass: 'full-width-modal-actions',
					data: {
						media: '/static/gif/blocked-functional/layoutRules.gif',
						mediaType: 'image',
						type: functionalType,
						title: 'DEMO.BLOCKED_FUNCTIONAL.TITLES.LAYOUT_RULES',
						description: 'DEMO.BLOCKED_FUNCTIONAL.LAYOUT_RULES',
						secondaryButtonText: 'GO_BACK',
						secondaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							ref.close();
						},
						primaryButtonText: 'COMMON.CHOOSE_TARIFF',
						primaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							this.mixPanelService.trackPage(MixpanelEvents.ChooseTariff, { page: 'Blocked functional' });
							window.open(
								this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/tariff-plans'),
								'_blank'
							);
							ref.close();
						}
					}
				});
			return;
		}

		if (!this.sessionService.hasExtension(ServiceExtensions.LayoutRules)) {
			this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleBlocked);
			this.dialog
				.open<InfoModalComponent, IInfoModalData<InfoModalComponent>>(InfoModalComponent, {
					width: '60vw',
					height: '80vh',
					autoFocus: false,
					backdropClass: 'back-drop',
					panelClass: 'full-width-modal-actions',
					data: {
						media: '/static/gif/blocked-functional/layoutRules.gif',
						mediaType: 'image',
						type: 'INFO_MODAL.ADDITIONAL_FEATURES',
						title: 'DEMO.BLOCKED_FUNCTIONAL.TITLES.LAYOUT_RULES',
						description: 'DEMO.BLOCKED_FUNCTIONAL.LAYOUT_RULES',
						secondaryButtonText: 'GO_BACK',
						secondaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							ref.close();
						},
						primaryButtonText: 'COMMON.LEAVE_REQUEST',
						primaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleRequested);
							window.$crisp.push(['do', 'chat:open']);
							ref.close();
						}
					}
				});
			return;
		}

		if (this.layoutRules && !forceClose) return;

		if (this.activity.panelType() == 'layoutrules' && this.activity.panelOpened()) {
			this.activity.closePanel();
		} else {
			this.activity.openPanel('layoutrules');
			this.layoutOpen = false;
		}
	}

	recountStandLayoutWidth() {
		if (this.equipmentModel) this.equipmentModel.inited = false;
		setTimeout(() => {
			this.activity.requestCanvasUpdate();
			if (this.equipmentModel) this.equipmentModel.init();
		});
	}

	closePanel() {
		this.activity.closePanel();
	}

	async deleteItems() {
		let dependedProducts = [], elements: Array<Element> = [];
		this.activity.activeProducts().forEach(
			(el) =>
				(dependedProducts = [
					...dependedProducts,
					...Utils.findDependProducts(el, this.stands[0].stand.products)
				])
		);
		this.stands.forEach((stand) => {
			stand.stand.products = stand.stand.products.filter(
				(p) => !this.activity.isProductActive(p)
			);
			stand.stand.products.forEach((p) => {
				if (this.activity.isProductActive(p.dependProduct)) {
					dependedProducts = dependedProducts
						.filter((pg) => pg !== p)
						.filter((v, i, a) => a.indexOf(v) === i);

					p.dependProduct = p.element.getDroppedProduct(
						p.begin.x,
						p.element.parent.type == 'AngleShelf'
							? p.getBoundingY().max
							: p.begin.y,
						p.begin.z,
						this.activity.activeProducts(),
						p
					);
					p.begin.y = p.dependProduct
						? p.dependProduct.getBoundingY().max
						: p.element.getBoundingY().max;
				}
			});
			stand.stand.products
				.filter((p) => p.dependProduct)
				.sort((a, b) => a.begin.y - b.begin.y)
				.forEach((p) => {
					if (dependedProducts.includes(p)) {
						p.begin.y = p.dependProduct
							? p.dependProduct.getBoundingY().max
							: p.element.getBoundingY().max;
					}
				});
		});

		const removedBlocks = this.stands[0].stand.blocks.filter((b) => this.activity.isBlockActive(b));

		if (this.stands[0].stand.blocks.filter((b) => b.products).length && removedBlocks.length) {
			this.stands.forEach((stand) => {
				stand.stand.products = stand.stand.products.filter(
					(p) => !removedBlocks[0].products.includes(p)
				);
				stand.stand.products.forEach((p) => {
					if (removedBlocks[0].products.includes(p.dependProduct)) {
						dependedProducts = dependedProducts.filter((pg) => pg !== p);

						p.dependProduct = p.element.getDroppedProduct(
							p.begin.x,
							p.begin.y,
							p.begin.z,
							this.activity.activeProducts(),
							p
						);
						p.begin.y = p.dependProduct
							? p.dependProduct.getBoundingY().max
							: p.element.getBoundingY().max;
					}
				});

				stand.stand.products.forEach((p) => {
					if (dependedProducts.includes(p)) {
						p.begin.y = p.dependProduct
							? p.dependProduct.getBoundingY().max
							: p.element.getBoundingY().max;
					}
				});
			});
		}

		this.stands[0].stand.blocks = this.stands[0].stand.blocks.filter((b) => !removedBlocks.includes(b));
		this.isBlockedReversed = this.sessionService.hasFeature(ServiceFeatures.LayoutReverse)
			&& !!this.stands[0].stand.blocks?.length;

		this.stands.forEach((stand) => {
			const elementsToNormalize = [];
			this.activity.activeProducts().forEach((pg: ProductGroup) => {
				if (!elementsToNormalize.includes(pg.element)) elementsToNormalize.push(pg.element);
			});
			removedBlocks.forEach((block: Block) =>
				block.elements.forEach((el: Element) => {
					if (!elementsToNormalize.includes(el)) elementsToNormalize.push(el);
				})
			);
			elements = this.activity.normalizeElements(stand.stand, elementsToNormalize);
			this.activity.setNumbersForPerforation$.next(elements);
		});

		if (this.activity.activePosMaterials().length) {
			this.stands[0].stand.POSMaterials = this.stands[0].stand.POSMaterials
				.filter((material: POSMaterial) => !this.activity.isPOSMaterialActive(material));
            if (this.activity.activePosMaterials().some((material: POSMaterial) => material instanceof POSImageMaterial)) {
                this.mixPanelService.trackPage(MixpanelEvents.POSDeleteFromPlanogram);
            }
            if (this.activity.activePosMaterials().some((material: POSMaterial) => material instanceof POSTextMaterial)) {
                this.mixPanelService.trackPage(MixpanelEvents.POSTextDelete);
            }
			this.activity.clearPOSMaterials();
			this.POSMaterialsClipboard = [];
		}

		let needRebuildProducts: boolean = false;
		if (removedBlocks.includes(this.activity.selectedBlock())) {
			needRebuildProducts = true;
			this.activity.clearSelectedBlock();
		}

		if (removedBlocks.includes(this.activity.activeBlocks()[0])) {
			needRebuildProducts = true;
		}
		this.activity.setBlockActive();
		this.activity.closePanel();
		this.activity.clearActivePanelProducts();
		this.setRealProductPercents();
		this.activity.requestCanvasUpdateAsync(this.stands, elements.map((el: Element) => el.id)).finally(() => {
			this.activity.setProductActive();
			if (needRebuildProducts) {
				this.rebuildProductsTable();
			} else {
				this.buildProductsTable(false);
			}
		});
		this.historyGenerate();
	}

	deletePOSForever(id: number): void {
		const imageMaterials: Array<POSImageMaterial> = this.stands[0].stand.POSMaterials
			.filter((material: POSMaterial): material is POSImageMaterial => material instanceof POSImageMaterial);

		for (let instance of this.history) {
			instance.POSMaterials = instance.POSMaterials.filter(
				(material: POSMaterial) => !(material instanceof POSImageMaterial) || material.id !== id
			);
		}

		if (imageMaterials.some((material: POSImageMaterial) => material.id === id)) {
			this.stands[0].stand.POSMaterials = this.stands[0].stand.POSMaterials.filter(
				(material: POSMaterial) => !(material instanceof POSImageMaterial) || material.id !== id
			);
			this.activity.setActivePOSMaterials(
				this.activity.activePosMaterials().filter(
					(material: POSMaterial) => !(material instanceof POSImageMaterial) || material.id !== id
				)
			);
			this.POSMaterialsClipboard = [];

			this.activity.requestCanvasUpdate(this.stands);
		}
	}

	async deleteProduct(products: Array<Product>) {
		let elements: Array<Element> = [];
		this.products = this.products.filter((p) => !products.includes(p));
		this.activity.closePanel();

		products.forEach((product: Product) => this.activity.removeActivePanelProduct(product));

		this.stands.forEach((stand) => {
			const elementsToNormalize = [];
			stand.stand.products.forEach((pg: ProductGroup) => {
				if (products.includes(pg.product) && !elementsToNormalize.includes(pg.element)) elementsToNormalize.push(pg.element);
			});
			stand.stand.products = stand.stand.products.filter((pg) => !products.includes(pg.product));
			stand.stand.products.forEach((pg: ProductGroup) => {
				if (
					pg.dependProduct &&
					(this.activity.isProductActive(pg.dependProduct) || products.includes(pg.dependProduct?.product))
				) {
					pg.dependProduct = pg.element.getDroppedProduct(
						pg.begin.x,
						pg.begin.y,
						pg.begin.z,
						this.activity.activeProducts(),
						pg
					);
					pg.begin.y = pg.dependProduct ? pg.dependProduct.getBoundingY().max : pg.element.getBoundingY().max;
				}
			});
			elements = this.activity.normalizeElements(stand.stand, elementsToNormalize);
			this.activity.setNumbersForPerforation$.next(elements);
		});

		this.activity.reloadEfficiencyAnalysis$.next();

		this.buildProductsTable(false);

		await this.setProductsPercents();
		this.setRealProductPercents();
		this.setBrandsInfoVisibility();
		this.setCategoriesInfoVisibility();
		this.activity.requestCanvasUpdateAsync(this.stands, elements.map((el: Element) => el.id)).finally(() => this.activity.setProductActive());
		this.historyGenerate();
	}

	deleteAllProduct() {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'ARE_YOU_SURE_YOU_WANT_DELETE_ALL_PRODUSCTS',
				confirmLabel: 'YES',
				cancelLabel: 'NO',
				twoAnswers: true
			}
		});

		dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
			if (!result.answer) return;
			this.activity.clearActivePanelProducts();

			const productIds: Array<number> = Utils.to1DArray(
				this.panelProductsItems.map((item: IProductsTableItem) =>
					item.children
						? item.children.map((product: IProductsTableItem) => product.key)
						: item.key
				)
			)
			this.stands.forEach((stand) => {
				stand.stand.products = stand.stand.products.filter((pg: ProductGroup) =>
					!productIds.includes(pg.product.id)
				);
				this.activity.normalizeElements(stand.stand);
			});

			await this.setProductsPercents();
			this.setRealProductPercents();

			this.products = this.products.filter((p: Product) => !productIds.includes(p.id));

			this.productsAnalytic = {};
			this.rebuildProductsTable();
			this.activity.requestCanvasUpdateAsync().finally(() => this.activity.setProductActive());
			this.historyGenerate();

			this.deletedAllProducts = true;
		});
	}

	async dropHandler(event: DndDropEvent): Promise<void> {
		switch (event.type) {
			case 'rule': return this.dropRule(event);
			case 'product': return this.dropProduct(event);
			case 'POSImageMaterial': return this.dropPOSImageMaterial(event);
		}
	}

	async dropRule(event: DndDropEvent): Promise<void> {
		this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleDrop, {
			ruleName: event.data.name
		});
		this.activeRule = event.data;
		const planogram: IPlanogram = this.stands[0];
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		const point: Point = behavior.convertCanvasPointToPlanogram({ x: event.event.offsetX, y: event.event.offsetY });
		const block: Block = planogram.stand.findBlock(point.x, point.y);
		if (block) this.activity.setBlockActive(block);
		if (this.activity.blockForRule().id && !this.activity.blockForRule().shelves.length) return;
		this.changeCanvasListening(false);
		await this.checkRuleProducts(event.data, () => {
			const ruleLevels: RuleLevel = new RuleLevel(event.data.conf.ruleData);
			ruleLevels.recommendedPercent = 100;
			ruleLevels.plannedPercent = 100;
			ruleLevels.calculatedPercent = 100;

			this.rulesService
				.applyRule({
					planogram: this.stands[0],
					activeBlock: this.activity.blockForRule(),
					ruleLevels: ruleLevels,
					products: this.products,
					reportParams: event.data.conf.reportParams,
					selectedStores: event.data.conf.shops,
					dateOptions: event.data.conf.dateOpt,
					useSales: event.data.conf.useSales,
					expandFaces: event.data.conf.expandFaces,
					checkDistance: event.data.conf.checkDistance,
					names: {
						brand: new Map(),
						category: new Map(),
						supplier: new Map(),
						characteristic: new Map()
					}
				})
				.then((planogram: EquipmentBase) => {
					if (planogram.availableElements.some((el: Element) => el.is(HangBarElement))) {
						this.snackBarService.setSnackBarNotification({
							messageText: 'RULES.HANG_BAR_WARNING'
						});
					}

					if (planogram.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
						this.snackBarService.setSnackBarNotification({
							messageText: 'RULES.ISLAND_RACK_WARNING'
						});
					}

					this.applyLayoutRule({
						data: this.rulesService.getColorsSet(ruleLevels),
						rule: event.data,
						stand: planogram
					});
				});
		});
	}

	createPOSImageMaterial(data: POSMaterialImageSaveObject): void {
		const material: POSImageMaterial = new POSImageMaterial(data);
		this.stands[0].stand.POSMaterials.push(material);
		this.activity.requestCanvasUpdateAsync().then(() => this.activity.setPOSMaterialActive(material));
		this.historyGenerate();
        this.mixPanelService.trackPage(MixpanelEvents.POSAdd);
	}

	dropPOSImageMaterial(event: DndDropEvent): void {
		const data: POSMaterialImageSaveObject = event.data;
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory.createBehavior(this.stands[0], null, this.stands[0].scale, this.stands[0].beginPoint);
		const point: Point = behavior.convertCanvasPointToPlanogram({ x: event.event.offsetX, y: event.event.offsetY });
		this.createPOSImageMaterial({
			...data,
			x: point.x,
			y: this.stands[0].stand.Height - point.y
		});
	}

	dropProduct(event: DndDropEvent): void {
		const planogram: IPlanogram = this.stands[0];
		const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
			.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
		this.activity.dropProduct(planogram, behavior, event);
	}

	getAbcReport() {
		if (this.activity.labelsState().abcTurnover || this.activity.labelsState().abcReceipts) {
			this.abcSubscription.unsubscribe();
			this.abcSubscription = from(
				this.planogramsService.getAbcReport({
					date_from: this.activity.labelsState().dateFrom,
					date_to: this.activity.labelsState().dateTo,
					planogram: this.planogramId,
					stand: this.standId,
					metrics: ['turnover', 'profit'],
					colors: this.filtersService.filters.colors,
					percents: this.filtersService.filters.percents,
					products: this.stands[0].stand.products.map((p) => p.product.id),
					all_shops: this.activity.labelsState().allShops
				})
			).subscribe((result) => {
				this.abcData = result;

				if (!this.activity.productMove()) {
					this.activity.requestCanvasUpdate();
				} else {
					this.forceUpdateCanvas = true;
				}
			});
		}
	}

	getPreviewImage() {
		const photo = this.activity.previewProduct()?.photo || '';
		const filename = photo.split('/').slice(-1)[0];
		return ['img404.jpg', '0'].includes(filename) ? EMPTY_PRODUCT_IMAGE_PATH : photo;
	}

	setAlignment(type: string) {
		if (!this.stands[0]) return;
		this.stands[0].stand.alignment = type as Alignment;

		this.activity.normalizeElements(this.stands[0].stand);
		this.activity.requestCanvasUpdate();
		this.activity.historyGenerate$.next();
	}

	// застосування даних з HistoryInstance
	async historyApply(historyObj: HistoryInstance) {
		const productAdd = this.products.length < historyObj.products.length,
			productDelete = this.products.length > historyObj.products.length,
			productsCache = productAdd
				? historyObj.products.filter((p) => !this.products.includes(p))
				: productDelete
					? this.products.filter((p) => !historyObj.products.includes(p))
					: undefined;

		if (!this.products.length && this.productAddStatus) this.deletedAllProducts = true;

		if (historyObj.rule && this.layoutRules) this.activeRule = historyObj.rule;
		this.stands.forEach((stand: IPlanogram) => {
			stand.stand = historyObj.stands[stand.id] ? historyObj.stands[stand.id].copy() : stand.stand.copy();
			this.activity.planogramUpdate$.next(stand);
		});
		this.products = [...historyObj.products];
		this.stands[0].stand.blocks = historyObj.blocks.map((b) => b.copy());
		this.stands[0].stand.POSMaterials = historyObj.POSMaterials.map((b) => b.copy());
		this.stands[0].stand.blocks.forEach((b) => {
			b.stand = this.stands[0].stand;
			b.setProducts(
				Utils.to1DArray(
					this.stands[0].stand.blocks.filter((block) => block != b).map((block) => block.products)
				)
			);
		});
		this.isBlockedReversed = this.sessionService.hasFeature(ServiceFeatures.LayoutReverse)
			&& !!this.stands[0].stand.blocks?.length;

		this.stands.forEach((stand) => {
			stand.stand.allElements.forEach((el) => {
				el.stand = stand.stand;
				if (el.constructor.name == 'ShelfElement') (el as ShelfElement).setPlacementHeight(stand.stand);
			});

			this.activity.normalizeElements(stand.stand);
		});

		if (!(historyObj.rule && this.layoutRules) || !Object.keys(historyObj.rule).length) {
			this.activeRule = null;
			this.layoutRules = false;
			this.activity.clearLayoutRulesProducts();

			this.recountStandLayoutWidth();
			this.activity.requestCanvasUpdate(this.stands, this.stands[0].stand.allElements.map((el: Element) => el.id));
		}

		await this.setProductsPercents();
		this.setRealProductPercents();

		this.activity.closePanel();
		this.activity.setProductActive();

		this.saveAble = this.history.length > 1 && historyObj != this.beginState;
        if (this.saveAble && !this.saveTimeout && this.ignoreOtherSave) this.setAutoSaveTimeout();
        if ((!this.saveAble || !this.ignoreOtherSave) && this.saveTimeout) {
			clearTimeout(this.saveTimeout);
			this.saveTimeout = null;
		}
		if (this.analysisOpen) {
			this.panelLoader = true;
			this.stands = [...this.stands];
			this.panelLoader = false;
		}

		if (this.highlightLegend.includes(this.activity.highlightValue())) this.highlightSubject.next();

		this.buildProductsTable(false);

		setTimeout(() => {
			if (this.equipmentModel) void this.equipmentModel.updateScene();
		});

		if (!this.productAddStatus) return;

		if (productAdd && productsCache) {
			this.moveInHistory = true;

			if (this.deletedAllProducts || this.addProductThroughExcel) {
				this.deletedAllProducts = false;
				this.productCache = [...productsCache];
			} else {
				this.productCache = [];
			}
		}
	}

	// історія назад
	async historyBack() {
		if (this.historyIndex == 0 || this.historyApplying || this.savePending) return;
		this.historyIndex -= 1;
		this.historyApplying = true;
		this.historyApply(this.history[this.historyIndex]).then(() => (this.historyApplying = false));
	}

	// історія вперед
	async historyForward() {
		if (this.historyIndex + 1 >= this.history.length || this.historyApplying || this.savePending) return;
		this.historyIndex += 1;
		this.historyApplying = true;
		this.historyApply(this.history[this.historyIndex]).then(() => (this.historyApplying = false));
	}

	// створення з теперішніх даних HistoryInstance і запис його в історію.
	historyGenerate() {
		this.productCache = [];

		setTimeout(() => {
			if (this.equipmentModel) void this.equipmentModel.updateScene();
		});
		// створення обєкта
		const stands = {};
		this.stands.forEach((s) => {
			stands[s.id] = s.stand.copy();
		});
		const historyObj: HistoryInstance = {
			stands,
			products: [...this.products],
			blocks: this.stands[0].stand.blocks.map((b) => b.copy()),
			POSMaterials: this.stands[0].stand.POSMaterials.map((b) => b.copy()),
			rule: Utils.deepCopy(this.activeRule) as LayoutRule
		};

		// відкидання поверненої історії та запис нового обєкта
		this.history.splice(this.historyIndex + 1);
		this.history.push(historyObj);

		// зберігаєм тільки останні 20 станів
		this.history = this.history.slice(-20);

		// запис нового активного елемента історії - останнього
		this.historyIndex = this.history.length - 1;

		if (this.analysisOpen) {
			this.panelLoader = true;
			this.stands = [...this.stands];
			this.panelLoader = false;
		}

		if (this.highlightLegend.includes(this.activity.highlightValue())) this.highlightSubject.next();

        if (!this.saveTimeout && this.historyIndex > 0) this.setAutoSaveTimeout();
		this.saveAble = true;
	}

    setAutoSaveTimeout(): void {
		const autosaveIntervalSeconds: number = this.sessionService.settings?.planogramAutosaveIntervalSeconds ?? 0;
		
		if (autosaveIntervalSeconds > 0) {
			this.saveTimeout = setTimeout(async () => {
				this.savePending = true;
				await this.performSaveAction(true);
				this.savePending = false;
			}, 1000 * autosaveIntervalSeconds);
		}
	}

	changeProductsGrouping(grouping: string): void {
		if (this.productsGroupingAvailable) {
			if (this.productsGrouping === grouping) {
				this.productsGrouping = '';
			} else {
				this.productsGrouping = grouping;
			}
			this.rebuildProductsTable();
		}
	}

	buildProductsTable(resetPage: boolean = true): void {
		let products: Array<Product> = [...this.products];

		const categories: Array<any> = Utils.to1DArray(products.map((product: Product) => product.categories));

		let maxLevel: number = 0;
		for (const category of categories) {
			if (category.level > maxLevel) {
				maxLevel = category.level;
			}
		}
		this.categoriesLevels = Math.max(maxLevel - 1, 0);

		if (resetPage) this.productsTablePage = 1;

		// filtering for block
		if (this.activity.activeBlocks().length || this.activity.selectedBlock()) {
			const block: Block = this.activity.selectedBlock() || this.activity.activeBlocks()[0];
			products = products.filter((product: Product) => block.isProductSuitable(product));
		}

		// searching
		if (this.searchQuery) {
			products = products.filter((product: Product) => {
				const categories: Array<any> = Utils.to1DArray(product.categories.map(c => c.slice(1))), // slice(1) - delete root category (ex: 'MegaTest')
					searchAlgorithm: SearchAlgorithmType = this.sessionService.personalSettings.searchAlgorithm;

				return Utils.searchItem<Product>(product, ['article', 'barcode', 'name'], this.searchQuery, searchAlgorithm) ||
					product.brands.some(brand => Utils.searchItem(brand, ['name'], this.searchQuery, searchAlgorithm)) ||
					categories.some(category => Utils.searchItem(category, ['name'], this.searchQuery, searchAlgorithm));
			});
		}

		// filtering
		if (this.selectedProductFilter !== 'all') {
			products = products.filter((product: Product) => {
				switch (this.selectedProductFilter) {
					case 'laid_out': return product.active;
					case 'not_laid_out': return !product.active;
					case 'stock_qty': return product.stock_qty && product.stock_qty > 0;
					case 'not_stock_qty': return !product.stock_qty || product.stock_qty < 0;
					case 'inactive': return !product.is_active && this.sessionService.defaults.has_assortment;
					case 'active': return product.is_active && this.sessionService.defaults.has_assortment;
					default: return true;
				}
			});
		}

		// sorting
		const property: string = this.sortProductsParams.property;
		if (property) {
			products = products.sort((a: Product, b: Product) => {
				switch (property) {
					case 'brand':
						return Utils.generalSorting({
							firstEl: a.brandsUniqueName || '',
							secondEl: b.brandsUniqueName || '',
							order: this.sortProductsParams.order
						});
					case 'assortment_type':
						return Utils.generalSorting({
							firstEl: a.assortment_type?.name || '',
							secondEl: b.assortment_type?.name || '',
							order: this.sortProductsParams.order
						});
					case 'status':
						return Utils.generalSorting({
							firstEl: a.statusesUniqueName || '',
							secondEl: b.statusesUniqueName || '',
							order: this.sortProductsParams.order
						});
					case 'article':
					case 'stock_qty':
					case 'barcode':
					case 'name':
					case 'sales_qty':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: this.sortProductsParams.order
						});
					case 'price':
						return Utils.generalSorting({
							firstEl: a.avgPrice,
							secondEl: b.avgPrice,
							order: this.sortProductsParams.order
						});
					case 'is_new':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: true
						});
				}
			});

			// Now we move the empty elements to the end of the list
			const emptyItems: Array<Product> = products.filter((item: Product) => {
				if (property === 'is_new') return false;
				return property === 'brand' ? !item.brands.length : (!item[property] && item[property] !== 0);
			});
			products = products
				.filter((item: Product) => {
					if (property === 'is_new') return true;
					return property === 'brand' ? item.brands.length : (item[property] || item[property] === 0);
				})
				.concat(emptyItems);
		}

		this.panelProducts = products;
		const needOpenGroups: boolean = !!this.searchQuery;

		if (this.productsGroupingAvailable) {
			const categoryLevel: number = Number(this.productsGrouping.split('_')[1]);
			if (categoryLevel && categoryLevel > this.categoriesLevels) {
				this.productsGrouping = products.length ? 'categories_' + this.categoriesLevels : '';
			}

			if (this.productsGrouping === 'brands') {
				this.panelProductsItems = this.buildProductsItemsByBrands(products, resetPage, needOpenGroups);
			} else if (this.productsGrouping.includes('categories')) {
				this.panelProductsItems = this.buildProductsItemsByCategories(products, categoryLevel, resetPage, needOpenGroups);
			} else {
				this.panelProductsItems = this.buildProductsItems(products);
			}
		} else {
			this.panelProductsItems = this.buildProductsItems(products);
		}

		const columns: Array<IColumnType> = [
			{
				sorter: true,
				key: 'name',
				size: 26,
				default_visible: true,
				title: 'NAME',
				button: false,
				always_visible: true,
				dtype: 'product_name',
				show_tooltip: true
			},
			{
				sorter: true,
				key: 'article',
				size: 8,
				default_visible: true,
				title: 'ARTICLE_VALUE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			},
			{
				sorter: true,
				key: 'barcode',
				size: 10,
				default_visible: this.sessionService.hasFeature(ServiceFeatures.LoadAssortment),
				title: 'BARCODE_VALUE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			}
		];

		if (this.sessionService.hasFeature(ServiceFeatures.AssortmentTypes)) {
			columns.push({
				sorter: true,
				key: 'assortment_type',
				size: 10,
				default_visible: false,
				title: 'ASSORTMENT_TYPE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductStatuses)) {
			columns.push({
				sorter: true,
				key: 'status',
				size: 10,
				default_visible: false,
				title: 'PRODUCT_STATUS_TYPE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Brands)) {
			columns.push({
				sorter: true,
				key: 'brand',
				size: 12,
				default_visible: true,
				title: 'BRAND_NAME',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductStock)) {
			columns.push({
				sorter: true,
				key: 'stock_qty',
				size: 8,
				default_visible: true,
				title: 'STOCK_QTY',
				button: false,
				always_visible: false,
				dtype: 'number',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductPrice)) {
			columns.push({
				sorter: true,
				key: 'price',
				size: 8,
				default_visible: false,
				title: 'PRICE',
				button: false,
				always_visible: false,
				dtype: 'string',
				show_tooltip: true
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Receipts)) {
			columns.push({
				sorter: true,
				key: 'sales_qty',
				size: 8,
				default_visible: false,
				title: 'SALES_QTY',
				button: false,
				always_visible: false,
				dtype: 'number',
				show_tooltip: true
			});
		}

		this.productsTableData = {
			count: products.length,
			has_data: !!products.length,
			next: 'yes',
			results: {
				columns: columns,
				dataSource: this.panelProductsItems.slice(0, this.productsTablePage * PRODUCTS_TABLE_PAGE_SIZE)
			}
		};

		if (!resetPage || needOpenGroups) {
			for (let item of this.productsTableData.results.dataSource) {
				if (!item.opened || !item.children) continue;
				const index: number = this.productsTableData.results.dataSource.indexOf(item);
				this.productsTableData.results.dataSource.splice(index + 1, 0, ...item.children);
			}
		}
	}

	buildProductsItems(products: Array<Product>): Array<IProductsTableItem> {
		return products.map((product: Product) => {
			return {
				key: product.id,
				name: {
					value: product.name,
					clickable: false,
					tags: this.buildProductsTableTags([product]),
					is_master_position: product.is_master_position
				},
				clickable_params: {
					dragData: {
						element: [product],
						type: 'product'
					}
				},
				article: product.article,
				barcode: product.barcode,
				brand: product.brandsUniqueName,
				stock_qty: product.stock_qty,
				price: product.price,
				assortment_type: product.assortment_type?.name,
				status: product.statusesUniqueName,
				sales_qty: product.sales_qty
			}
		});
	}

	buildProductsTableTags(products: Array<Product>, hideTitle: boolean = false): { colors: Array<string>, title: string } {
		let title: string;
		const tags: Array<string> = [];
		if (products.every((product: Product) => !product.is_active)) tags.push('black');
		if (products.every((product: Product) => product.active)) {
			tags.push('orange');
		} else {
			if (!products.some((product: Product) => product.active) && products.every((product: Product) => product.is_new)) {
				tags.push('green');
				title = 'PRODUCT_CARD.ACTIVE_NOT_LAYOUT';
			}
		}
		if (tags.includes('black') && tags.includes('orange')) {
			title = 'PRODUCT_CARD.NOT_ACTIVE_LAYOUT_THIS';
		} else {
			if (tags.includes('black')) title = 'PRODUCT_CARD.NOT_ACTIVE_NOT_LAYOUT_THIS';
			if (tags.includes('orange')) title = 'PRODUCT_CARD.ACTIVE_LAYOUT_THIS';
		}

		return {
			colors: tags,
			title: hideTitle ? '' : title
		}
	}

	buildProductsItemsByBrands(
		products: Array<Product>,
		resetPage: boolean,
		needOpenGroups: boolean
	): Array<IProductsTableItem> {
		const grouping: Array<Array<Product>> = Utils.groupBy(products, 'brandsUniqueId'),
			property: string = this.sortProductsParams.property;
		let items: Array<IProductsTableItem> = grouping.map((group: Array<Product>) => {
			return {
				key: group[0].brandsUniqueId,
				name: {
					value: group[0].brandsUniqueName || 'NO_BRAND',
					tags: this.buildProductsTableTags(group, true),
				},
				article: null,
				barcode: null,
				brand: group[0].brandsUniqueName,
				stock_qty: group.some((p: Product) => p.stock_qty !== null)
					? group.reduce((a: number, b: Product) => a + b.stock_qty, 0)
					: null,
				sales_qty: group.some((p: Product) => p.sales_qty !== null)
					? group.reduce((a: number, b: Product) => a + b.sales_qty, 0)
					: null,
				price: null,
				assortment_type: null,
				status: null,
				opened: needOpenGroups ||
					(
						!resetPage &&
						Boolean(this.panelProductsItems.find((p: IProductsTableItem) => p.key === group[0].brandsUniqueId)?.opened)
					),
				children: group.map((product: Product) => {
					return {
						key: product.id,
						name: {
							value: product.name,
							tags: this.buildProductsTableTags([product]),
							is_master_position: product.is_master_position
						},
						article: product.article,
						barcode: product.barcode,
						brand: product.brandsUniqueName,
						stock_qty: product.stock_qty,
						sales_qty: product.sales_qty,
						price: product.price,
						assortment_type: product.assortment_type?.name,
						status: product.statusesUniqueName,
						level: 2,
						clickable_params: {
							dragData: {
								element: [product],
								type: 'product'
							}
						}
					}
				}),
				clickable_params: {
					dragData: {
						element: group,
						type: 'product'
					}
				}
			}
		});

		if (property) {
			items = items.sort((a: IProductsTableItem, b: IProductsTableItem) => {
				switch (property) {
					case 'brand':
					case 'article':
					case 'stock_qty':
					case 'sales_qty':
					case 'barcode':
					case 'assortment_type':
					case 'status':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: this.sortProductsParams.order
						});
					case 'price':
						return Utils.generalSorting({
							firstEl: Utils.avgProductPrice(a.price),
							secondEl: Utils.avgProductPrice(b.price),
							order: this.sortProductsParams.order
						});
					case 'name':
						return Utils.generalSorting({
							firstEl: a.name.value,
							secondEl: b.name.value,
							order: this.sortProductsParams.order
						});
				}
			});

			// Тепер переміщуємо порожні елементи в кінець списку.
			const emptyItems: Array<IProductsTableItem> = items.filter((item: IProductsTableItem) => {
				if (property === 'is_new') return false;
				return property === 'name' ? !item.brand : (!item[property] && item[property] !== 0);
			});
			items = items
				.filter((item: IProductsTableItem) => {
					if (property === 'is_new') return true;
					return property === 'name' ? item.brand : (item[property] || item[property] === 0);
				})
				.concat(emptyItems);
		}

		return items;
	}

	buildProductsItemsByCategories(
		products: Array<Product>,
		categoryLevel: number,
		resetPage: boolean,
		needOpenGroups: boolean
	): Array<IProductsTableItem> {
		const groupBy: GroupByCategory = new GroupByCategory('category', String(categoryLevel)),
			property: string = this.sortProductsParams.property;
		let items: Array<IProductsTableItem> = groupBy
			.group(products)
			.map((group: { id: string, name: string, products: Array<Product> }) => {
				return {
					key: +group.id,
					name: {
						value: group.name || 'WITHOUT_CATEGORY',
						tags: this.buildProductsTableTags(group.products, true)
					},
					article: null,
					barcode: null,
					brand: null,
					stock_qty: group.products.some((p: Product) => p.stock_qty !== null)
						? group.products.reduce((a: number, b: Product) => a + b.stock_qty, 0)
						: null,
					sales_qty: group.products.some((p: Product) => p.sales_qty !== null)
						? group.products.reduce((a: number, b: Product) => a + b.sales_qty, 0)
						: null,
					price: null,
					assortment_type: null,
					status: null,
					opened: needOpenGroups ||
						(
							!resetPage &&
							Boolean(this.panelProductsItems.find((p: IProductsTableItem) => p.key === +group.id)?.opened)
						),
					children: group.products.map((product: Product) => {
						return {
							key: product.id,
							name: {
								value: product.name,
								tags: this.buildProductsTableTags([product]),
								is_master_position: product.is_master_position
							},
							article: product.article,
							barcode: product.barcode,
							brand: product.brandsUniqueName,
							stock_qty: product.stock_qty,
							price: product.price,
							assortment_type: product.assortment_type?.name,
							status: product.statusesUniqueName,
							sales_qty: product.sales_qty,
							level: 2,
							clickable_params: {
								dragData: {
									element: [product],
									type: 'product'
								}
							}
						}
					}),
					clickable_params: {
						dragData: {
							element: group.products,
							type: 'product'
						}
					}
				}
			});

		if (property) {
			items = items.sort((a: IProductsTableItem, b: IProductsTableItem) => {
				switch (property) {
					case 'brand':
					case 'article':
					case 'stock_qty':
					case 'sales_qty':
					case 'barcode':
					case 'assortment_type':
					case 'status':
						return Utils.generalSorting({
							firstEl: a[property],
							secondEl: b[property],
							order: this.sortProductsParams.order
						});
					case 'price':
						return Utils.generalSorting({
							firstEl: Utils.avgProductPrice(a.price),
							secondEl: Utils.avgProductPrice(b.price),
							order: this.sortProductsParams.order
						});
					case 'name':
						return Utils.generalSorting({
							firstEl: a.name.value,
							secondEl: b.name.value,
							order: this.sortProductsParams.order
						});
				}
			});

			// Тепер переміщуємо порожні елементи в кінець списку.
			const emptyItems: Array<IProductsTableItem> = items.filter((item: IProductsTableItem) => {
				if (property === 'is_new') return false;
				return property === 'name' ? !item.name.value : (!item[property] && item[property] !== 0);
			});
			items = items
				.filter((item: IProductsTableItem) => {
					if (property === 'is_new') return true;
					return property === 'name' ? item.name.value : (item[property] || item[property] === 0);
				})
				.concat(emptyItems);
		}

		return items;
	}

	rebuildProductsTable(): void {
		if (this.productsTable) this.productsTable.scrollToTop();
		this.buildProductsTable();
	}

	ngOnDestroy() {
		if (this.translateSubscription) this.translateSubscription.unsubscribe();
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();

        if (this.stands?.length) {
            this.socketService.deleteSubscription(SocketGroup.StandConversation, { stand_id: Number(this.standId) });
            this.socketService.deleteSubscription(SocketGroup.PlanogramUsersActivity, { planogram_id: Number(this.standId) });
            this.socketService.deleteSubscription(SocketGroup.EquipmentUsersActivity, { equipment_id: Number(this.stands[0]?.stand?.id )});
        }
		this.socketSubscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
		this.productsTableSubscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
		this.detailedElementWidgetOverlayService.closeAll();

		if (this.saveTimeout) {
			clearTimeout(this.saveTimeout);
			this.saveTimeout = null;
		}
		if (this.pingInterval) clearInterval(this.pingInterval);
		this.clearDragOutsidePreviews();
		this.mainStage.destroy();
	}

	async getStand(standId: number, bind = true, planogramId?: number) {
		let standInfo = await lastValueFrom(
			this.planogramsService.getItem$(standId, { planogram_id: planogramId || this.planogramId })
		);

		if (!standInfo) return;

		if (standId == this.route.snapshot.params.id) {
			this.standId = standId;
			this.standName = standInfo.name;
			this.standScheduledDate = standInfo.scheduled_date;
			this.equipmentUsage = standInfo.instrument.instrument_usage;

			if (!this.planogramId && standInfo.planogram_stand.length && !planogramId) {
				this.planogramId = standInfo.planogram_stand[0].planogram;
				this.planogramConf = await lastValueFrom(this.plansService.getItem$(this.planogramId));
				if (!this.planogramConf) return;
				standInfo = await lastValueFrom(
					this.planogramsService.getItem$(standId, { planogram_id: this.planogramId })
				);
				if (!standInfo) return;
			}
		}
		this.standUpdatedAt = standInfo.date;

		if (standInfo.indicators.length) {
			const isInnerUser: boolean = SessionService.isStaff(this.sessionService.username)
				|| SessionService.isNIQ(this.sessionService.username);

			if (!(isInnerUser && !SessionService.isInnerClient(this.sessionService.clientId))) {
				standInfo.indicators.forEach((indicator: any) => {
					this.planogramsService.deleteIndicator(indicator.id).subscribe();
				});
			}
		}

		if (standInfo.planogram_stand?.length) {
			this.planogramStores = standInfo.planogram_stand
				.map((item: IPlanogramPlanData) => item.planogram_shop)
				.filter((store: number) => !Utils.isNullOrUndefined(store));
		}

		const response: any = {
			visible: true,
			layerObjects: {
				planogramGroup: new Konva.Group(),
				sizesGroup: new Konva.Group(),
				tooltipGroup: new Konva.Group(),
				boxGroup: new Konva.Group()
			},
			id: standInfo.id,
			name: standInfo.name,
            chat: standInfo.chat,
			locked: standInfo.locked,
			planogram_stand: standInfo.planogram_stand,
			main_stand: standInfo.main_stand,
			image: standInfo.image,
			products: standInfo.products,
			conf: standInfo.conf,
			blocks: standInfo.blocks,
			planogram_id: planogramId || this.planogramId,
			flip: false
		};

		if (bind) {
			this.planogramLayer.add(response.layerObjects.planogramGroup);
			this.sizesLayer.add(response.layerObjects.sizesGroup);
			this.tooltipsLayer.add(response.layerObjects.tooltipGroup);
			this.blocksLayer.add(response.layerObjects.boxGroup);
		}

		response.stand = new EquipmentBase({
			id: standInfo.instrument.id,
			name: standInfo.instrument.name,
			code: standInfo.instrument.code,
			producer: standInfo.instrument.producer,
			type: standInfo.instrument.type,
			type_name: standInfo.instrument.type.name,
			alignment: standInfo.alignment,
			arbitrary: standInfo.arbitrary,
			posMaterials: standInfo.pos_materials,
			textMaterials: standInfo.text_materials
		});

		this.equipmentService.setEquipmentConfElements(response.stand, standInfo.instrument.conf.elements);

		if (standInfo.conf.products && bind) {
			let products = standInfo.conf.products.map((p) => {
				if (!p.planedPercent) p.planedPercent = 0;

				return new Product(p);
			});

			const ids = standInfo.conf.products.map((p) => p.id);

			products.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));

			this.products = this.products.concat(
				products.filter((pr) => !this.products.map((i) => i.id).includes(pr.id))
			);
		}

		if (bind) this.addProductsFilterService.filters = standInfo.conf.productFilters || {};

		if (standInfo.conf.reportParams && bind) {
			this.reportParams = standInfo.conf.reportParams;
		}

		response.stand.products = [];

		standInfo.products
			.sort((a, b) => a.position_y - b.position_y)
			.forEach((p) => {
				const product = this.products.find((product) => product.id == p.master_product);

				if (!product) return;

				const element = response.stand.getElementById(p.element_id);

				if (!element) return;
				if (product.photo?.split('/').slice(-1)[0] === '0') {
					product.photo = EMPTY_PRODUCT_IMAGE_PATH;
				}

				const begin = {
						x: p.position_x,
						y: p.position_y,
						z: p.position_z
					},
					{ qty, rotation, packing, weight, angle } = p,
					placementHeight = p.placement_height,
					productGroup = new ProductGroup({
						product,
						element,
						begin,
						qty,
						weight,
						rotation,
						angle,
						placementHeight,
						selectPacking: packing,
						block: p.block,
						height_compression_coef: p.height_compression_coef,
						width_compression_coef: p.width_compression_coef,
						depth_compression_coef: p.depth_compression_coef,
						applied_compressions: p.applied_compressions,
						dependProduct: Utils.getDependProduct(p, response.stand.products, element),
						detailedRotationPoint: this.sessionService.personalSettings.productRotationPoint
					});

				productGroup.placementDepth = p.placement_depth || 1;

				if (!productGroup.element) return;

				response.stand.products.push(productGroup);
			});

		if (standInfo.blocks && bind) response.stand.setBlocks(standInfo.blocks);

		const replaceEmptyKeyWithMinusOne = (obj: any) => {
			const newObj = {};

			for (const key in obj) {
				const newKey = key === '' ? '-1' : key;
				newObj[newKey] = obj[key];
			}

			return newObj;
		}

		this.brandsRecommendedPercent = replaceEmptyKeyWithMinusOne(standInfo.conf.brandsRecommendedPercent || {});
		this.categoriesRecommendedPercent = replaceEmptyKeyWithMinusOne(standInfo.conf.categoriesRecommendedPercent || {});

		response.stand.orderedElements = Utils.setElementsOrder(response.stand.allElements);

		if (bind) {
			try {
				response.stand.orderedElements.forEach((el) => {
					if (NORMALIZED_ELEMENTS.includes(el.constructor.name)) el.normalize(response.stand);
				});
			} catch (err) {
				console.error(err);
			}
		}

		return response;
	}

	async ngOnInit() {
		if (this.sessionService.hasAccess('ph-plan-view')) this.planAccess = 'view';
		if (this.sessionService.hasAccess('ph-plan-edit')) this.planAccess = 'edit';
		this.isManager = this.sessionService.defaults.isManager;

		this.productsService.getProductsCount().subscribe((response: { count: number }) => {
			this.hasProducts = !!response.count;

			if (
				!this.hasProducts
				&& this.sessionService.hasFeature(ServiceFeatures.LoadAssortment)
			) {
				this.dialog
					.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
						width: '720px',
						autoFocus: false,
						backdropClass: 'back-drop',
						data: {
							twoAnswers: true,
							label: 'ASSORTMENT_UPLOAD.YOU_DONT_HAVE_PRODUCTS',
							secondLabel: 'ASSORTMENT_UPLOAD.WANT_TO_LOAD_NEW',
							confirmLabel: 'ASSORTMENT_UPLOAD.LOAD_NEW',
							cancelLabel: 'DEMO.ASSORTMENT.USE_TEST'
						}
					})
					.afterClosed()
					.subscribe((result: IConfirmModalAnswer) => {
						if (result.answer === null) return;
						if (result.answer) {
							this.router
								.navigate(this.sessionService.getLink(['product', 'assortment-upload']))
								.finally();
						} else {
							this.router
								.navigate(this.sessionService.getLink(['product']), {
									queryParams: {
										needLoad: true
									}
								})
								.finally();
						}
					});
			}
		});

		//setTimeout(() => this.openInitOnboardingModal(), 1000);

		this.loader = true;
		let stands_ids = [this.route.snapshot.params.id];
		this.userIsStoreManager = this.sessionService.defaults.isManager;
		this.planogramId = this.route.snapshot.queryParams.planogram;

		if (this.planogramId) {
			this.planogramConf = await lastValueFrom(this.plansService.getItem$(this.planogramId));
			if (!this.planogramConf) return;
		}

		const leftPanelSavedWidth: number = this.sessionService.settings.planogramEditLeftPanelWidth;
		if (leftPanelSavedWidth) this.leftPanelSavedWidth = leftPanelSavedWidth;

		const [users, ,...stands] = await Promise.all([
			this.layoutService.getUsers(),
			this.loadMessages(),
			...stands_ids.map((s) => this.getStand(s))
		]);
		if (!stands[0]) return;
		if (stands[0].locked) this.router.navigate(this.sessionService.getLink(['stand', 'list']));

		this.chatUsers = users;
		this.stands = stands;
		this.activity.planogram = this.stands[0];
		this.activity.setFocusedCanvas(this.stands[0].stand.id, null, this.mainCanvas.nativeElement);
		this.mainCanvas.nativeElement.addEventListener('mousedown', () => {
			if (!this.activity.isCanvasFocused(this.mainCanvas.nativeElement)) {
				this.activity.setFocusedCanvas(this.stands[0].stand.id, null, this.mainCanvas.nativeElement);
				this.stands[0].stand.allElements.forEach((el: Element) => {
					el.shapes.forEach((shape: Figure) => (shape as any).representation = 'front');
				});
			}
		});
		this.activity.updateLabelsState({ dateTo: this.labelsDate.date_to, dateFrom: this.labelsDate.date_from });
		this.isBlockedReversed = this.sessionService.hasFeature(ServiceFeatures.LayoutReverse)
			&& !!this.stands[0].stand.blocks?.length;
		this.hasEquipmentBoxes = this.stands[0].stand.hasBoxes;
		this.standsList = (
			this.stands[0].planogram_stand.find((e: IPlanogramPlanData) => e.planogram == this.planogramId)?.stands || []
		)
			.map((s) => ({ id: s.main_stand_id, name: s.name }))
			.sort(Utils.sortBy('name'));

		if ('highlight' in this.route.snapshot.queryParams)
			await this.changeHighlightOption(
				this.route.snapshot.queryParams.highlight
			);

		if ('rotation' in this.route.snapshot.queryParams) {
			const standId = this.route.snapshot.params.id,
				notificationId = this.route.snapshot.queryParams.rotation;

			await this.getChangedProducts(notificationId, standId, this.planogramId);
		}

		const planogramsList = (this.stands[0].planogram_stand || [])
			.map((p) => ({ id: p.planogram_id, name: p.planogram_name }))
			.sort(Utils.sortBy('name'));
		planogramsList.forEach((p) => ((p as any).active = false));

		const planogramIndex = planogramsList.findIndex(
				(item) => (item as any).id == this.route.snapshot.queryParams.planogram
			),
			standIndex = this.standsList.findIndex(
				(item) => item.id == this.stands[0].main_stand
			);

		this.standFilters = ~standIndex
			? this.changeIndex(this.standsList, standIndex, 0)
			: this.standsList;
		if (this.sessionService.hasFeature(ServiceFeatures.StorePlans)) {
			this.planogramFilters = ~planogramIndex
				? this.changeIndex(planogramsList, planogramIndex, 0)
				: planogramsList;
		}

		const planogramFilter = this.stands[0].planogram_stand.find((plan) => {
			return plan.planogram_id == this.planogramId;
		});
		this.planogramActive = !!planogramFilter.planogram_shop;

		await this.setProductsPercents();
		this.setRealProductPercents();
		await this.activity.requestCanvasUpdateAsync();

		this.historyGenerate();
		this.beginState = this.history[0];
		this.flipMode.check(this.stands[0].stand);

		this.loader = false;

		this.allowedAutoLayout = this.getLayoutAccessibility();

		this.stands[0].hasBrands = this.sessionService.defaults.has_brands;
		this.stands[0].hasSuppliers = this.planogramConf.has_suppliers;
		this.cachedAutolayoutParams.hasBrands = this.sessionService.defaults.has_brands;
		this.cachedAutolayoutParams.hasSuppliers = this.planogramConf.has_suppliers;

		if (this.route.snapshot.queryParams.sort_new) {
			this.sortProducts('is_new', true);
		}

		this.saveAble = false;
		
		this.translateSubscription = this.translate.onLangChange.subscribe(() => this.canvasRender());

		if (
			this.sessionService.hasExtension(ServiceExtensions.LayoutRules) &&
			!this.sessionService.defaults.settings.hideApplyRule &&
			!this.products.length
		) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				panelClass: 'full-width-modal-actions',
				data: {
					label: 'RULES.MODAL.APPLY',
					twoAnswers: true,
					confirmLabel: 'RULES.APPLY',
					cancelLabel: 'RULES.MODAL.CONTINUE_EDITING',
					checkBox: true,
					checkBoxLabel: 'NOT_SHOW_ANYMORE'
				}
			});

			dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer === null) return;
				if (result.checkBoxState) {
					this.sessionService.defaults.settings.hideApplyRule = result.checkBoxState;
					this.sessionService.updateSettings();
				}
				if (result.answer) {
					this.toggleLayoutRulesPanel();
				}
			});
		}

		this.buildProductsTable();

		this.socketService.addSubscription(SocketGroup.StandConversation, { stand_id: Number(this.stands[0].main_stand) });
        this.socketService.addSubscription(SocketGroup.PlanogramUsersActivity, { planogram_id: Number(this.stands[0].main_stand )});
        this.socketService.addSubscription(SocketGroup.EquipmentUsersActivity, { equipment_id: Number(this.stands[0].stand?.id )});
        this.usersActivitySubject = this.socketService.planogramUsersActivity;

		this.productsTableSubscriptions.push(
			this.sortingProductsTableSubject.subscribe((value: string) => {
				const property: string = value.replace('-', ''),
					order: boolean = value.includes('-');
				this.sortProducts(property, order);
			}),

			this.updateProductsTableSubject.subscribe(() => {
				const displayedCount: number = this.productsTablePage * PRODUCTS_TABLE_PAGE_SIZE,
					allCount: number = this.panelProductsItems.length;

				if (allCount > displayedCount) {
					this.productsTableData.results.dataSource.push(
						...this.panelProductsItems.slice(displayedCount, displayedCount + PRODUCTS_TABLE_PAGE_SIZE)
					);
					this.productsTableData = {...this.productsTableData};
					this.productsTablePage++;
				}
			}),

			this.startDraggingProductsSubject.subscribe((products: Array<Product>) => {
				if (this.modes.equipmentEditMode()) return;
				if (products.some((product: Product) => !this.activity.isPanelProductActive(product))) {
					this.activity.clearActivePanelProducts();
					for (let product of products) {
						this.activity.addActivePanelProduct(product);
					}
				}
				this.startDragProduct(products[0]);
			}),

			this.clickProductsTableSubject.subscribe((data: ITableRowClickData) => {
				if (!this.modes.equipmentEditMode()) {
					this.setPanelProductsActive(data.element.clickable_params.dragData.element, data.event);
				}
			})
		);

		this.socketSubscriptions.push(
			this.socketService.conversationReadMessage.subscribe((data: IStandConversationDone) => {
				this.chatMessages.forEach((message: IStandConversationMessage) => {
					if (message.id == data.id) message.done = data.done;
				});
				this.chatMessages = [...this.chatMessages];
			}),

			this.socketService.conversationUpdateImage.subscribe((data: IStandConversationImage) => {
				this.chatMessages.forEach((message: IStandConversationMessage) => {
					if (message.id == data.stand_conversation_id) {
						message.files = data.files;
						this.savePending = false;
					}
				});
				this.chatMessages = [...this.chatMessages];
			}),

			this.socketService.conversationNewMessage.subscribe((data: IStandConversationMessage) => {
				this.chatMessages = [...this.chatMessages, data];
			}),

			this.productCardService.saveSubject.subscribe((product: IProductCard) => {
				this.updateProductCard(product);
				this.highlightSubject.next();
			}),

            this.socketService.planogramSave.subscribe((response: IUserSave) => {
                if (response.session_id === this.sessionService.sessionId) return;
                const isStaff: boolean = SessionService.isStaff(response.email);
                const isNiq: boolean = SessionService.isNIQ(response.email);
                if (!SessionService.isStaff(this.sessionService.defaults.email) && isStaff) return;
                if (
                    !SessionService.isNIQ(this.sessionService.defaults.email) && 
                    !SessionService.isStaff(this.sessionService.defaults.email) && 
                    isNiq
                ) return;
                this.ignoreOtherSave = false;
                this.snackBarService.setSnackBarNotification({
                    messageText: 'COMMON.PLANOGRAM.OTHER_USER_SAVE',
                    translateParams: { user: response.full_name },
                    durationSeconds: 60 * 60,
                    closeButton: false,
                    closeActions: [
                        { action: 'COMMON.RELOAD_PAGE', callback: () => window.location.reload() },
                        { action: 'OK', callback: () => this.ignoreOtherSave = true }
                    ]
                });
            }),

            this.socketService.equipmentSave.subscribe((response: IUserSave) => {
                if (response.session_id === this.sessionService.sessionId) return;
                const isStaff: boolean = SessionService.isStaff(response.email);
                const isNiq: boolean = SessionService.isNIQ(response.email);
                if (!SessionService.isStaff(this.sessionService.defaults.email) && isStaff) return;
                if (
                    !SessionService.isNIQ(this.sessionService.defaults.email) && 
                    !SessionService.isStaff(this.sessionService.defaults.email) && 
                    isNiq
                ) return;
                this.ignoreOtherEquipmentSave = false;
                this.snackBarService.setSnackBarNotification({
                    messageText: 'COMMON.EQUIPMENT.OTHER_USER_SAVE',
                    translateParams: { user: response.full_name },
                    durationSeconds: 60 * 60,
                    closeButton: false,
                    closeActions: [
                        { action: 'COMMON.RELOAD_PAGE', callback: () => window.location.reload() },
                        { action: 'OK', callback: () => this.ignoreOtherEquipmentSave = true }
                    ]
                });
            })
		);
	}

	async loadMessages() {
		if (!this.next_messages) return;

		const response = await this.layoutService.getMessages(
			this.route.snapshot.params.id,
			this.next_messages
		);

		this.next_messages = PlanogramEditComponent.getNextPage(
			response.next
		);

		this.chatMessages = this.chatMessages.concat(response.results);
	}

	ngAfterViewInit() {
		this.mainStage = new Konva.Stage({
			container: this.mainCanvas.nativeElement
		});
		this.mainStage.add(this.planogramLayer);
		this.mainStage.add(this.sizesLayer);
		this.mainStage.add(this.blocksLayer);
		this.mainStage.add(this.tooltipsLayer);
		this.mainStage.add(this.toolsLayer);
		this.blocksLayer.add(this.rect);

		this.blockHeightLabel.add(
			new Konva.Tag({
				rotation: -90
			}),
			new Konva.Text({
				text: '',
				fontSize: 12,
				rotation: -90
			})
		);
		this.blockWidthLabel.add(
			new Konva.Tag(),
			new Konva.Text({
				text: '',
				fontSize: 12
			})
		);
		this.blockPercentLabel.add(
			new Konva.Tag(),
			new Konva.Text({
				text: '',
				fontSize: 12
			})
		);

		const stageContent = this.mainStage.getContent();
		this.mainStage.on('pointerdown', (event: Konva.KonvaEventObject<PointerEvent>) => {
			if (!this.mainStage) return;
			if (this.modes.drawBlockMode() || this.resizeMode || this.modes.equipmentEditMode() || this.activity.activeBlocks().length)
				return;

			const eventPointerPosition = this.mainStage.getPointerPosition(),
				pointedShape: any = this.mainStage.getIntersection(eventPointerPosition);

			if (pointedShape) {
				const shapeChain = [pointedShape, pointedShape.parent, pointedShape.parent?.parent];
				const isDraggable = shapeChain.some((shape) => shape?.draggable());
				const isProductSide = shapeChain.some((shape) => shape?.name() === 'product-side');

				if (isDraggable || isProductSide) return;
			}

			this.multiSelectConfiguration.begin = eventPointerPosition;
			this.multiSelectConfiguration.end = null;
			this.multiSelectConfiguration.drawing = true;
			this.multiSelectConfiguration.ctrlKey = event.evt[navigator.platform == 'MacIntel' ? 'metaKey' : 'ctrlKey'];

			if (stageContent?.setPointerCapture) stageContent.setPointerCapture(event.evt.pointerId);
		});
		this.mainStage.on('pointermove', (event: Konva.KonvaEventObject<PointerEvent>) => {
			if (!this.mainStage) return;
			event.evt.preventDefault();

			if (
				this.multiSelectConfiguration.begin &&
				!Utils.equalObjects(this.multiSelectConfiguration.begin, this.mainStage.getPointerPosition()) &&
				this.multiSelectConfiguration.drawing
			)
				this.activity.clearDragProducts();

			if (
				!this.multiSelectConfiguration.drawing ||
				!this.multiSelectConfiguration.begin ||
				this.modes.drawBlockMode() ||
				this.modes.equipmentEditMode() ||
				this.activity.dragProducts().length ||
				this.activity.activeBlocks().length
			)
				return;

			const eventPointerPosition = this.mainStage.getPointerPosition();
			const beginPoint = this.multiSelectConfiguration.begin;

			this.toolsLayer.destroyChildren();
			this.toolsLayer.add(new Konva.Rect({
				x: Math.min(eventPointerPosition.x, beginPoint.x),
				y: Math.min(eventPointerPosition.y, beginPoint.y),
				width: Math.abs(eventPointerPosition.x - beginPoint.x),
				height: Math.abs(eventPointerPosition.y - beginPoint.y),
				stroke: '#99ddff',
				strokeWidth: 1
			}));
			this.toolsLayer.draw();

			this.multiSelectConfiguration.end = eventPointerPosition;
		});
		this.mainStage.on('pointerup', (event: Konva.KonvaEventObject<PointerEvent>) => {
			if (stageContent.releasePointerCapture) stageContent.releasePointerCapture(event.pointerId);

			this.canvasUp(event);

			if (this.multiSelectConfiguration.drawing) {
				this.multiSelectConfiguration.drawing = false;
				this.clearToolLayer();

				if (!this.multiSelectConfiguration.ctrlKey && this.multiSelectConfiguration.end) {
					this.activity.setProductActive(null, false, false);
				}

				if (!this.multiSelectConfiguration.begin || !this.multiSelectConfiguration.end) return;

				const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
					.createBehavior(this.stands[0], null, this.scale, this.stands[0].beginPoint);
				const begin: Point = behavior.convertCanvasPointToPlanogram(this.multiSelectConfiguration.begin);
				const end: Point = behavior.convertCanvasPointToPlanogram(this.multiSelectConfiguration.end);
				const selectedArea = geometryFactory.createPolygon([
					new Coordinate(begin.x, begin.y),
					new Coordinate(begin.x, end.y),
					new Coordinate(end.x, end.y),
					new Coordinate(end.x, begin.y),
					new Coordinate(begin.x, begin.y)
				]);
				const productsCoordinates = this.stands[0].stand.products
					.filter((pg: ProductGroup) => this.stands[0].stand.blocks.every((b) => !b.products.includes(pg)))
					.filter((pg: ProductGroup) => {
						const wall: Element = pg.element.findDepend(...BACK_WALLS_LIST);
						if (!wall) return true;
						if (this.flipMode.active) {
							return pg.begin.z < wall.center.z;
						} else {
							return pg.begin.z >= wall.center.z;
						}
					})
					.map((pg: ProductGroup) => {
						const onAngleShelf = pg.element.parent.type == 'AngleShelf';
						const shelfHeight = onAngleShelf ? pg.element.Height : 0;
						const productBoundingX: Bounding = pg.getBoundingX();
						const productBoundingY: Bounding = pg.getBoundingY();

						if (this.flipMode.active) {
							const standWidth: number = pg.element.stand.Width;

							return {
								coords: [
									{ x: standWidth - productBoundingX.min, y: productBoundingY.min - shelfHeight },
									{ x: standWidth - productBoundingX.max, y: productBoundingY.min - shelfHeight },
									{ x: standWidth - productBoundingX.max, y: productBoundingY.max - shelfHeight },
									{ x: standWidth - productBoundingX.min, y: productBoundingY.max - shelfHeight },
									{ x: standWidth - productBoundingX.min, y: productBoundingY.min - shelfHeight }
								],
								pg: pg
							};
						} else {
							return {
								coords: [
									{ x: productBoundingX.min, y: productBoundingY.min - shelfHeight },
									{ x: productBoundingX.max, y: productBoundingY.min - shelfHeight },
									{ x: productBoundingX.max, y: productBoundingY.max - shelfHeight },
									{ x: productBoundingX.min, y: productBoundingY.max - shelfHeight },
									{ x: productBoundingX.min, y: productBoundingY.min - shelfHeight }
								],
								pg: pg
							};
						}
					});
				const productsToAdd = productsCoordinates.filter(({ coords }) => {
					const productArea = geometryFactory.createPolygon(coords.map((p: Point) => new Coordinate(p.x, p.y)));
					return selectedArea.intersects(productArea);
				});

				if (!productsToAdd.length) this.activity.setProductActive(null, false, false);
				productsToAdd.forEach((pg) => this.activity.setProductActive(pg.pg, true));

				this.isNeedCloseRightPanel();
			}
		});

		//вимкнення контексного меню на канві
		this.mainCanvas.nativeElement.addEventListener(
			'click',
			(event) => {
				if (event.ctrlKey) return;
				event.preventDefault();
			},
			false
		);

		this.mainCanvas.nativeElement.addEventListener(
			'contextmenu',
			(event) => {
				event.preventDefault();
			},
			false
		);
	}

	openAddProductsPanel() {
		this.closePanel();
		this.productAddStatus = !this.productAddStatus;
	}

	openBrandPanel() {
		if (this.activity.panelType() == 'brand' && this.activity.panelOpened()) return this.activity.closePanel();
		this.activity.openPanel('brand');
		this.mixPanelService.trackPage(MixpanelEvents.BrandsStatistics);
		this.layoutOpen = false;
	}

	openSettingsPanel() {
		if (this.activity.settingsPanelDisabled()) return this.activity.closePanel();

		if (this.modes.drawBlockMode()) this.changeDrawBlockMode();

		this.activity.openPanel('size');
		this.layoutOpen = false;
	}

	openHighlightsPanel(): void {
		if (this.sessionService.hasFeature(ServiceFeatures.ProductsHighlight)) {
			this.activity.openPanel('highlights');
			this.layoutOpen = false;
		} else {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.ProductsHighlightsPlanogramEdit);
		}
	}

	openLabelsPanel() {
		if (this.activity.panelType() == 'labels' && this.activity.panelOpened()) return this.activity.closePanel();
		this.activity.openPanel('labels');
		this.layoutOpen = false;
	}

	openPOSMaterialsPanel(): void {
		this.activity.openPanel('pos');
		this.layoutOpen = false;
	}

	startDragProduct(product: Product) {
		if (!this.activity.isPanelProductActive(product)) {
			this.activity.clearActivePanelProducts();
			this.activity.setProductActive();
			this.activity.setDragProducts([product]);
			this.activity.setActivePanelProducts([product]);
			this.activity.closePanel();
		} else {
			this.activity.setDragProducts(this.activity.activePanelProducts());
		}
	}

	async redirectToReport() {
		if (this.saveAble && this.ignoreOtherSave && !(await this.saveAction())) return;
		this.loader = true;

		this.router
			.navigate(
				this.sessionService.getLink(['stand', 'place-predict']),
				{ queryParams: { planogram_id: this.planogramId, stand_id: this.standId } }
			)
			.finally();

		this.loader = false;
	}

	uploadExcel() {
		const dialogRef = this.dialog.open(ProductsAddExcelModalComponent, {
			width: '600px',
			height: 'fit-content',
			panelClass: 'upload-excel-modal',
			autoFocus: false,
			backdropClass: 'back-drop'
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			if (!result || (!result.products && !result.file)) return;

			this.loader = true;
			this.productAddStatus = false;

			const products_ids = [];

			if (result.file) {
				const response = await this.addProductsFilterService.uploadExcel(result.file);

				Array.prototype.push.apply(products_ids, response.results.products);
			}

			if (result.products) {
				Array.prototype.push.apply(products_ids, result.products);
			}

			if (!products_ids.length) {
				this.loader = false;
				this.snackBarService.setSnackBarNotification({
					messageText: 'PLANOGRAM_EDIT.PRODUCT_LIST_UPLOAD_ERROR',
					durationSeconds: 10
				});
				return;
			}

			let products = await lastValueFrom(
				this.addProductsFilterService.getProducts(
					{ key: 'product_id=', plan_id: this.planogramId, planogram_id: this.standId },
					{ ids: products_ids.join(',') }
				)
			);

			await this.addProducts(
				products.results.map((p) => {
					p.planedPercent = 10;
					return new Product(p);
				})
			);

			this.findProductsOnStand();
			this.loader = false;
			this.addProductThroughExcel = true;
		});
	}

	showErrorAlert() {
		const errorTranslates: Array<{ key: string, params: Object }> = [];
		const sortedBaseElements: Array<Element> = this.stands[0].stand.bases
			.sort((a: Element, b: Element) => a.getBoundingX().min - b.getBoundingX().min);

		this.stands[0].stand.products
			.filter((pg: ProductGroup) => pg.error)
			.forEach((pg: ProductGroup) => {
				const elementIndex: number = Utils.getElementIndex(pg.element, pg.element.stand);
				const equipmentBase: Element = [pg.element, ...pg.element.getDepends()]
					.find((depend: Element) => !depend.depend);
				const equipmentIndex: number = sortedBaseElements.indexOf(equipmentBase) + 1;
				const translateParams: Object = {
					article: pg.product.article,
					element: elementIndex,
					equipment: equipmentIndex
				};

				if (pg.dependProduct) translateParams['depend'] = pg.dependProduct.product.article;

				const productErrorToTranslate: { [key in ProductErrors]: string } = {
					[ProductErrors.ShelfLeftLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SHELF_LEFT_LIMIT',
					[ProductErrors.ShelfRightLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SHELF_RIGHT_LIMIT',
					[ProductErrors.BoxLeftLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.BOX_LEFT_LIMIT',
					[ProductErrors.BoxRightLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.BOX_RIGHT_LIMIT',
					[ProductErrors.HangBarLeftLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.HANG_BAR_LEFT_LIMIT',
					[ProductErrors.HangBarRightLimit]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.HANG_BAR_RIGHT_LIMIT',
					[ProductErrors.ShelfDepth]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SHELF_DEPTH_LIMIT',
					[ProductErrors.BoxDepth]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.BOX_DEPTH_LIMIT',
					[ProductErrors.HookDepth]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.HOOK_DEPTH_LIMIT',
					[ProductErrors.ElementHeight]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SHELF_HEIGHT_LIMIT',
					[ProductErrors.TrapezeLimits]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.TRAPEZE_LIMITS',
					[ProductErrors.DependLimits]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.DEPEND_WIDTH_LIMIT',
					[ProductErrors.DependDepth]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.DEPEND_DEPTH_LIMIT',
					[ProductErrors.CentralPillarIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.PILLAR_INTERSECTION',
					[ProductErrors.PartitionIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.PARTITION_INTERSECTION',
					[ProductErrors.SideWallsIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.SIDE_WALLS_INTERSECTION',
					[ProductErrors.BoxIntersection]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.BOX_INTERSECTION',
					[ProductErrors.ProductIntersection] : 'PLANOGRAM_EDIT.PRODUCT_ERRORS.PRODUCT_INTERSECTION',
					[ProductErrors.LooseOnShelf]: 'PLANOGRAM_EDIT.PRODUCT_ERRORS.LOOSE_ERROR'
				}

				pg.errorTypes.forEach((error: ProductErrors) => {
					errorTranslates.push({ key: productErrorToTranslate[error], params: translateParams });
				});
			});

		this.snackBarService.setSnackBarNotification({
			durationSeconds: 2 * 60 * 60,
			messageText: 'LAYOUT_INCORRECT_ERROR',
			content: this.extendedProductErrors,
			contentContext: { errors: errorTranslates }
		});
	}

	static checkAutolayoutFreeSpace(
		target_shelf_products,
		target_space,
		groupAccordance,
		hierarchy,
		max_faces?
	) {
		const min = Math.min(...target_shelf_products.map((i) => i.ItemWidth));

		let diff = Utils.round(target_space - target_shelf_products.reduce((a, b) => a + b.Width, 0)),
			max_iterations = 100,
			iteration_count = 0;

		while (diff >= min) {
			iteration_count++;
			const zero_products = target_shelf_products
				.filter((pg) => !pg.qty)
				.map((pg) => pg.product.id.toString());
			for (let product_id of zero_products) {
				const product = target_shelf_products.find(
					(pg) => pg.product.id == product_id
				);

				if (!product) continue;

				if (product.ItemWidth > diff) continue;

				const totalWidth = Utils.round(
					groupAccordance[product.product.id].realWidth +
					product.ItemWidth
				);
				if (totalWidth > groupAccordance[product.product.id].maximumWidth)
					continue;

				product.FacesQty = 1;
				groupAccordance[product.product.id].realWidth = totalWidth;

				diff = Utils.round(diff - product.ItemWidth);

				if (diff >= min) continue;
				break;
			}

			if (diff < min) break;

			for (let product_id of hierarchy) {
				const product = target_shelf_products.find(
					(pg) => pg.product.id == product_id
				);

				if (!product) continue;

				if (product.ItemWidth > diff) continue;

				if (max_faces && product.FacesQty >= max_faces) continue;

				const totalWidth = Utils.round(
					groupAccordance[product.product.id].realWidth +
					product.ItemWidth
				);
				if (totalWidth > groupAccordance[product.product.id].maximumWidth)
					continue;

				product.FacesQty += 1;
				groupAccordance[product.product.id].realWidth = totalWidth;

				diff = Utils.round(diff - product.ItemWidth);

				if (diff >= min) continue;
				break;
			}

			if (iteration_count === max_iterations) break;
		}
	}

	async openCopyPlanogramModal(): Promise<void> {
		if (!this.sessionService.hasFeature(ServiceFeatures.PlanogramCopy)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.PlanogramCopying);
			return;
		}
		if (this.loader) return;
		if (!this.stands[0].stand?.products?.length && !this.stands[0].stand?.blocks?.length) return;

		this.dialog
			.open(CopyPlanogramModalComponent, {
				width: '70%',
				autoFocus: false,
				backdropClass: 'back-drop'
			})
			.afterClosed()
			.subscribe(async (result: CopyingData) => {
				if (!result) return;
				this.loader = true;

				// for correct conf in copying function
				this.stands[0].conf.reportParams = this.reportParams;
				this.stands[0].conf.products = this.products.map((p: Product) => {
					return { id: p.id, planedPercent: p.planedPercent };
				});

				const planogramFrom: IPlanogram = Object.assign({}, this.stands[0], { stand: this.stands[0].stand.copy() }),
					planograms: Array<IPlanogram> = await lastValueFrom(
						this.planogramsService
							.bulkFetch(
								result.planograms.filter((i, idx, array) => {
									return idx == array.findIndex(e => e.planogram_id == i.planogram_id);
								})
							)
							.pipe(
								map((data: Array<IPlanogramData>) => {
									return data.map((item: IPlanogramData) => {
										return this.planogramLayoutService.getPlanogramObject(
											item,
											[],
											item.planogram_stand[0].planogram_id,
											false
										);
									});
								})
							)
					),
					notifications: Array<CopyingNotification> = await this.planogramLayoutService
						.makeCopying(planograms, planogramFrom, result);

				await lastValueFrom(this.notificationsService.createCopyPlanogramNotification({
					from_planogram_id: planogramFrom.id,
					to: notifications.map((item: CopyingNotification) => {
						const notificationsMessages: Array<string> = [];
						if (item.stand_errors?.length) notificationsMessages.push('SIMPLE_COPY_ERROR');
						if (!item.stand_errors?.length && !item.errors?.length) notificationsMessages.push('SIMPLE_COPY_SUCCESS');
						if (!item.errors?.length && result.type) notificationsMessages.push('SIMPLE_COPY_SUCCESS');
						if (item.deniedReversed) notificationsMessages.push('REVERSE_LAYOUT.DENIED');

						return {
							plan_id: item.planogram_id,
							planogram_id: item.id,
							errors: item.errors,
							messages: notificationsMessages
						}
					})
				}));

				this.loader = false;
				this.mixPanelService.trackPage(MixpanelEvents.PlanogramCopyingFromEdit, {
					standId: planogramFrom.id,
					standCount: planograms.length,
					isReversed: result.isReversed,
					copyingType: result.type || 'SIMPLE'
				});
				this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Copy);
			});
	}

	openSendForExecution(): void {
		if (!this.sessionService.hasFeature(ServiceFeatures.PlanogramsExecution)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.SendExecution);
			return;
		}
		if (this.loader || !this.stands[0] || this.modes.equipmentEditMode()) return;

		this.analysisOpen = false;

		if (this.saveAble) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(
				ConfirmModalComponent,
				{
					width: '574px',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: 'PLANOGRAM_EDIT.SAVE_AND_SEND_EXECUTION_CONFIRM',
						cancelLabel: 'COMMON.CANCEL',
						confirmLabel: 'GO',
						twoAnswers: true
					}
				}
			);

			dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
				if (!result?.answer) return;
				this.performSaveAction().then((saved: boolean) => {
					if (!saved) return;
					this.proceedToSendForExecution();
				});
			});
			return;
		}

		this.proceedToSendForExecution();
	}

	proceedToSendForExecution(): void {
		this.executionParams = { ids: this.stands[0].main_stand };
		this.executionPlanogram = this.planogramId
			? { id: this.planogramId, name: this.standName }
			: null;
		this.showExecutionPage = true;
	}

	cancelExecutionPage(): void {
		this.exitExecutionPage();
	}

	closeExecutions(event: IExecuteEvent): void {
		this.exitExecutionPage();

		this.snackBarService.setSnackBarNotification({
			messageText: 'THREAD_EXECUTION_MESSAGE'
		});

		const updated = event?.responseData?.find((p: any) => p.stand_id == this.stands[0]?.main_stand);
		if (updated?.scheduled_date) this.standScheduledDate = updated.scheduled_date;

		delete event.responseData;

		this.mixPanelService.trackPage(MixpanelEvents.SendExecutionFromPlanogramEdit, event);
	}

	private exitExecutionPage(): void {
		this.showExecutionPage = false;
		this.executionPlanogram = null;
	}

    async performSaveAction(disableLoader: boolean = false): Promise<boolean> {
		if (this.saveObserver) await lastValueFrom(this.saveObserver);
		this.saveObserver = from(this.saveAction(disableLoader));
		const result: boolean = await lastValueFrom(this.saveObserver);
		this.saveObserver = null;
		return result;
	}

	stopProductMove(enableCanvasListening: boolean = false): void {
		this.activity.setProductMove(false);
		this.clearDragOutsidePreviews();
		
		if (enableCanvasListening) this.changeCanvasListening(true);
	}

	async saveAction(autoSave: boolean = false, forceSave: boolean = false) {
        if (this.saveTimeout) {
			clearTimeout(this.saveTimeout);
			this.saveTimeout = null;
		}
		if (!this.ignoreOtherSave) {
			return Promise.resolve(true);
		}
        if (this.activity.productMove() || this.blockBuildConfig.building || !this.saveAble) {
			this.pausedInterval = true;
			return Promise.resolve(undefined);
		}
		if (this.standError && !forceSave) {
            this.pausedInterval = true;
			return autoSave ? Promise.resolve(null) : Promise.resolve(this.showErrorAlert());
		}

		this.sameVersion = false;
		if (!autoSave) this.loader = true;
		if (autoSave) this.savePending = true;

		return this.planogramsService.updatePlacementShelf(this.stands[0].id, this.getSaveConfiguration())
			.then(async (updateStand) => {
				const makeSvgConf = [
					{
						main_stand_id: updateStand['main_stand'],
						article_photo_mode: this.modes.articleMode(),
						sequence_number_mode: this.modes.sequenceNumberMode(),
						transparent_boxes: this.transparentBoxesMode,
						show_pos_materials: this.POSMaterialsMode,
						view_mode: this.activity.focusedCanvas().element ? 'top': 'direct',
						element_id: this.activity.focusedCanvas().element ?? undefined,
						planogram_id: Number(this.planogramId),
						size_mode: this.modes.sizesMode(),
						show_stock_qty: this.activity.labelsState().stockQty,
						show_prices: this.activity.labelsState().price,
						show_abc_turnover: this.activity.labelsState().abcTurnover,
						show_abc_receipts: this.activity.labelsState().abcReceipts,
						abc_data: this.abcData
					}
				];
				this.standUpdatedAt = updateStand.date;
				await this.planogramsService.makeSvg(makeSvgConf);
				this.beginState = this.history[this.historyIndex];
				this.recountStandLayoutWidth();
				if (!autoSave) this.openSaveOnboardingModal();
				this.saveAble = false;
				return true;
			})
			.catch((error) => {
				this.snackBarService.setSnackBarNotification({
					messageText: 'PLANOGRAM_EDIT.PLANOGRAM_SAVE_ERROR',
					durationSeconds: 0,
					closeButton: true,
					closeActions: [
						{
							action: 'ERROR.TO_SUPPORT',
							callback: (): void => {
								window.$crisp.push(['do', 'chat:open']);
							}
						}
					]
				});
				this.saveAble = true;

				return false;
			})
			.finally(() => {
				this.savePending = false;
				this.loader = false;
			});
	}

	scrollSelectedProduct(target: EventTarget): void {
		const parent: HTMLElement = Utils.findEventParent(target, 'mat-mdc-row');
		if (!parent) return;

		const { top } = parent.getBoundingClientRect(),
			rowHeight: number = 50,
			layoutPanelHeight: number = 70, // need if when layout rules and auto-layout blocked
			topDiff: number = window.innerHeight - top - rowHeight - layoutPanelHeight;

		if (topDiff < this.productPreview.nativeElement.clientHeight) {
			setTimeout(() => {
				this.productsTable.tableContainerElement.nativeElement.scrollTop +=
					this.productPreview.nativeElement.clientHeight - topDiff;
			}, 0);
		}
	}

	setPanelProductsActive(products: Array<Product>, event?: MouseEvent): void {
		if (!products?.length) return;

		this.activity.clearDragProducts();
		if (event) this.scrollSelectedProduct(event.target);
		this.addToActiveProducts(products, event?.shiftKey, navigator.platform == 'MacIntel' ? event?.metaKey : event?.ctrlKey);
		this.isNeedCloseRightPanel();
	}

	setActiveElementsFromProducts(): void {
		const groups = this.activity.activePanelProducts()
			.map((p: Product) => this.findProductGroupOnStand(p))
			.filter((group: Array<ProductGroup>) => group);

		PlanogramEditComponent.to1D(groups).forEach((pg) => this.activity.setProductActive(pg, true));
	}

	addToActiveProducts(products: Array<Product>, shiftPressed?: boolean, ctrlPressed?: boolean): void {
		this.activity.setProductActive();

		// if there is no product that was clicked on, then we clear all the selected products and exit
		if (!products?.length) {
			this.activity.clearActivePanelProducts();
			return;
		}

		// if neither ctrl nor shift is pressed - then clear all selected products and add the ones you clicked on and exit
		if (!shiftPressed && !ctrlPressed) {
			this.activity.clearActivePanelProducts();
			this.activity.setActivePanelProducts(products);
			this.setActiveElementsFromProducts();
			return;
		}

		// if shift is pressed, then clear all selected products (if ctrl is not pressed) and add there the products
		// that are between the last added and the one that was clicked and exit
		if (shiftPressed) {
			const tableProducts: Array<IProductsTableItem> = this.productsTableData.results.dataSource;
			let startIndex: number = tableProducts.findIndex((p: IProductsTableItem) => p.clickable_params.dragData.element[0] === products[0]);
			let endIndex: number = tableProducts.findIndex((p: IProductsTableItem) => {
				return this.activity.activePanelProducts().slice(-1)[0] == p.clickable_params.dragData.element.slice(-1)[0];
			});

			if (endIndex === -1) {
				this.activity.clearActivePanelProducts();
				this.activity.setActivePanelProducts(products);
				this.setActiveElementsFromProducts();
				return;
			}

			if (startIndex > endIndex) [startIndex, endIndex] = [endIndex, startIndex];

			for (let i = startIndex; i <= endIndex; i++) {
				const itemProducts: Array<Product> = tableProducts[i].clickable_params.dragData.element;
				itemProducts.forEach((product: Product) => {
					if (!this.activity.isPanelProductActive(product)) this.activity.addActivePanelProduct(product);

				});
			}

			this.setActiveElementsFromProducts();

			return;
		}

		// if ctrl is pressed, then add to the selected products or remove the group that was clicked from there
		if (ctrlPressed) {
			const allSelected: boolean = products.every((p: Product) => p.selected);
			products.forEach((product: Product) => {
				if (allSelected) {
					this.activity.removeActivePanelProduct(product);
				} else {
					if (!this.activity.isPanelProductActive(product)) this.activity.addActivePanelProduct(product);
				}
			});
			this.setActiveElementsFromProducts();
		}
	}

	static to1D(array) {
		return Array.prototype.concat.apply([], array);
	}

	findProductGroupOnStand(product: Product): Array<ProductGroup> {
		return PlanogramEditComponent
			.to1D(this.stands.filter((s) => s.visible).map((stand) => stand.stand.products))
			.filter((pg: ProductGroup) => pg.product == product);
	}

	sortSelectedProductGroups(option) {
		if (this.stands[0].stand.arbitraryLayout) return;

		const elementsToNormalize: Element[] = [];
		let productsToSort;
		if (this.activity.activeProducts().length) {
			productsToSort = this.activity.activeProducts();
		} else if (this.activity.activeBlocks().length) {
			this.activity.activeBlocks()[0].updateBlockProducts(
				this.activity.getDependBlockProducts(this.activity.activeBlocks()[0])
			);
			productsToSort = this.activity.activeBlocks()[0].products;
		}

		productsToSort.forEach((pg) => {
			if (!elementsToNormalize.includes(pg.element)) {
				elementsToNormalize.push(pg.element);
			}
		});

		const products = elementsToNormalize
			.reduce((groups, el) => {
				return [
					...groups,
					...el.products.filter((group) =>
						group.some((pg) => productsToSort.includes(pg))
					)
				];
			}, [])
			.sort((a, b) => a[0].begin.x - b[0].begin.x);

		const sortedProducts = products.map(
				(group) =>
					group.sort(
						(a, b) => b.begin.z - a.begin.z || a.begin.y - b.begin.y
					)[0]
			),
			prevPositions = sortedProducts.map((pg) => pg.begin.x);
		sortedProducts.sort(Utils.sortBy(option.by, option.type));
		sortedProducts.forEach((pg, idx) => (pg.begin.x = prevPositions[idx]));
		sortedProducts.reduce(
			PlanogramEditComponent.zipProducts,
			prevPositions[0]
		);

		products.forEach((group) =>
			group.forEach((pg) => (pg.begin.x = group[0].begin.x))
		);

		this.activity.normalizeElements(this.stands[0].stand, elementsToNormalize);

		this.historyGenerate();
		this.activity.requestCanvasUpdate();
	}

	sortProducts(property: string, order: boolean): void {
		this.sortProductsParams = {
			order: order,
			property: property
		};
		this.rebuildProductsTable();
	}

	static isNeedUpdatePlacePredictCache(cache = {}, current_ids = []) {
		return !Utils.equalLists(Object.keys(cache), current_ids);
	}

	async setProductsPercents() {
		if (this.planogramId) {
			if (!this.products.length) return;

			const products: Array<number> = this.products.map((p: Product) => p.id);

			if (PlanogramEditComponent.isNeedUpdatePlacePredictCache(this.placePredictCache, products)) {
				this.blockHistoryButtons = true;
				const { date_from, date_to } = this.filtersService.Filters
				this.placePredictCache = await this.productsService.getProductsPercent(
					Object.assign({}, this.reportParams || {}, { date_from: date_from, date_to: date_to }),
					products,
					this.planogramId,
					this.standId
				);
				this.blockHistoryButtons = false;
			}

			this.products.forEach((product) => {
				product.avgSalesPerDay = this.placePredictCache[String(product.id)]?.avg_qty_per_day;
				product.planedPercent = this.placePredictCache[String(product.id)]?.percent;
			});
		} else {
			this.products.forEach((product) => {
				product.planedPercent = 100 / this.products.length;
			});
		}
	}

	setBrandsInfoVisibility() {
		if (this.activity.activeBlocks().length) {
			const block_brands = Utils.to1DArray(
				this.activity.activeBlocks().map((b) =>
					b.products.map((pg) => pg.product.brandsUniqueId)
				)
			);
			this.brandsInfo = this.brandsInfoCopy.filter((brand) =>
				block_brands.includes(brand.id)
			);
		} else {
			this.brandsInfo = [...this.brandsInfoCopy];
		}
	}

	setCategoriesInfoVisibility() {
		if (this.activity.activeBlocks().length) {
			const blockCategories = Utils.to1DArray(
					this.activity.activeBlocks().map((b) =>
						b.products.map((pg) => pg.product.categoriesUniqueId)
					)
				),
				blockBrands = Utils.to1DArray(
					this.activity.activeBlocks().map((b) =>
						b.products.map((pg) => pg.product.brandsUniqueId)
					)
				);
			this.categoriesInfo = this.categoriesInfoCopy.filter((category) =>
				blockCategories.includes(category.id)
			);
			this.categoriesInfo.forEach((item) => {
				if (item.childrens && item.childrens.length) {
					item.childrens = item.childrens.filter((brand) =>
						blockBrands.includes(brand.id)
					);
				}
			});
		} else {
			this.categoriesInfo = Utils.deepCopy(this.categoriesInfoCopy) as any;
		}
	}

	setRealProductPercents() {
		const withoutBrandsId = '-1',
			brandsObj = {},
			categoryObj = {},
			linearMeters: number = this.stands[0].stand.LinearMeters,
			planogramProducts: Array<ProductGroup> = this.stands
				.map((planogram: IPlanogram) => planogram.stand.products)
				.flat();

		this.products
			.forEach((product) => {
				const brandId: string = product.brandsUniqueId || withoutBrandsId,
					categoryId: string = product.categoriesUniqueId || withoutBrandsId,
					productMeters: number = planogramProducts
						.filter((pg: ProductGroup) => pg.product.id === product.id && pg.availability)
						.reduce((total: number, pg: ProductGroup) => total + pg.Width, 0),
					realProductPercent: number = linearMeters ? Utils.round(productMeters / linearMeters * 100, 3) : 0;

				if (!brandsObj.hasOwnProperty(brandId)) {
					brandsObj[brandId] = {
						id: brandId,
						name: product.brandsUniqueName || 'NO_BRAND',
						real: realProductPercent,
						plan: product.planedPercent || 0,
						recom: this.brandsRecommendedPercent[brandId] || 0
					};
				} else {
					brandsObj[brandId].real = Utils.round(brandsObj[brandId].real + realProductPercent, 3);
					brandsObj[brandId].plan = Utils.round(brandsObj[brandId].plan + (product.planedPercent || 0), 3);
				}

				const newBrand = {
					id: brandId,
					name: product.brandsUniqueName || 'NO_BRAND',
					real: realProductPercent,
					plan: product.planedPercent || 0,
					recom: this.categoriesRecommendedPercent[categoryId]?.childrens[brandId] || 0
				};

				if (!categoryObj.hasOwnProperty(categoryId)) {
					categoryObj[categoryId] = {
						id: categoryId,
						name: product.categoriesUniqueName || 'WITHOUT_CATEGORY',
						recom: this.categoriesRecommendedPercent[categoryId]?.recom || 0,
						childrens: [newBrand]
					};
				} else {
					if (categoryObj[categoryId].childrens.every((item) => item.id !== newBrand.id)) {
						categoryObj[categoryId].childrens = [...categoryObj[categoryId].childrens, newBrand];
					} else {
						const children = categoryObj[categoryId].childrens.find((item) => item.id == newBrand.id);
						children.real = Utils.round(children.real + realProductPercent, 3);
						children.plan = Utils.round(children.plan + (product.planedPercent || 0), 3);
					}
				}
			});

		this.brandsInfoCopy = Object.values(brandsObj);
		this.categoriesInfoCopy = Object.values(categoryObj);
		this.brandsInfoCopy.sort((a, b) => a.plan - b.plan);
		this.brandsInfoCopy.forEach((brand) => {
			brand.plan = brand.plan.toFixed(2);
			brand.real = brand.real.toFixed(2);
			if (brand.recom > 0) brand.recom = Utils.round(Number(brand.recom), 2);
			this.brandsRecommendedPercent[brand.id] = brand.recom;
		});
		this.categoriesInfoCopy.forEach((category) => {
			category.real = Utils.sum(category.childrens.map((b) => Number(b.real)));
			category.plan = Utils.sum(category.childrens.map((b) => Number(b.plan)));
			category.recom = Utils.round(category.recom, 2);
			this.categoriesRecommendedPercent[category.id] = {
				recom: category.recom || 0,
				childrens: {}
			};

			if (category.childrens.length) {
				category.childrens.forEach((brand) => {
					brand.plan = Number(brand.plan).toFixed(2);
					brand.real = category.real ? (Number(brand.real) / category.real) * 100 : Number(brand.real);
					brand.real = brand.real.toFixed(2);
					brand.recom = Utils.round(Number(brand.recom), 2);
				});
				category.childrens.forEach((child) => {
					this.categoriesRecommendedPercent[category.id].childrens[child.id] = child.recom || 0;
				});
			}
		});

		this.setBrandsInfoVisibility();
		this.setCategoriesInfoVisibility();
	}

	private setElementToDetail(element: Element): void {
		if (element) {
			const isOpened: boolean = this.detailedElementWidgetOverlayService.isOpened(element);
			this.elementToDetail = element.is(ShelfElement, PalletElement, HookElement) && !isOpened ? element : null;
		} else {
			this.elementToDetail = null;
		}
	}

	public openDetailedElementWidget(stand: IPlanogram, element: Element): void {
		if (this.detailedElementWidgetOverlayService.isOpened(element)) return;
		if (!element.is(ShelfElement, PalletElement, HookElement)) return;

		this.detailedElementWidgetOverlayService.open({
			overlay: this.overlay,
			parentInjector: this.injector,
			hostContainer: this.detailedOverlayHost?.nativeElement as HTMLElement | undefined,
			boundingRect: this.canvasContainer?.nativeElement.getBoundingClientRect(),
			element: element,
			planogram: stand,
			afterClose: () => {
				if (!this.activity.isCanvasFocused(this.mainCanvas.nativeElement)) {
					this.activity.setFocusedCanvas(stand.stand.id, null, this.mainCanvas.nativeElement);
					this.stands[0].stand.allElements.forEach((el: Element) => {
						el.shapes.forEach((shape: Figure) => (shape as any).representation = 'front');
					});
				}

				this.setElementToDetail(this.activity.targetElement());
			}
		});

		this.setElementToDetail(null);
	}

	public hasOpenedDetailedWidgets(): boolean {
		return this.detailedElementWidgetOverlayService.hasOpened();
	}

	updateProductsLayout(params: ILayoutConfigFormParams): void {
		const stand = this.activity.activeProducts()[0].element.stand;

		const updateCompressionParams: Partial<ICompressionParams> = {};

		this.activity.activeProducts().forEach((activeProduct: ProductGroup) => {
			const index: number = stand.products.indexOf(activeProduct),
				standProduct: ProductGroup = stand.products[index];

			const oldFacesQty: number = activeProduct.FacesQty;
			if (params.faces) standProduct.FacesQty = params.faces;
			if (params.depth) standProduct.placementDepth = params.depth;
			if (params.height) standProduct.placementHeight = params.height;
            if (!Utils.isNullOrUndefined(params.angle) && standProduct.angle !== params.angle) {
                standProduct.angle = params.angle;
                // TODO: need apply change element for new begin after rotate
            }
			if (params.hasOwnProperty('packing')) standProduct.selectPacking = params.packing;
			if (params.weight) standProduct.weight = params.weight;

			const prev = standProduct.applied_compressions.slice();
            let next: CompressionType[] = prev;
            if (!Utils.isNullOrUndefined(params.enableDepthCompression)) {
                next = next.filter(t => t !== CompressionType.Depth);
                if (params.enableDepthCompression) next.push(CompressionType.Depth);
            }

            if (!Utils.isNullOrUndefined(params.enableHeightCompression)) {
                next = next.filter(t => t !== CompressionType.Height);
                if (params.enableHeightCompression) next.push(CompressionType.Height);
            }
          
            if (!Utils.isNullOrUndefined(params.enableWidthCompression)) {
                next = next.filter(t => t !== CompressionType.Width);
                if (params.enableWidthCompression) next.push(CompressionType.Width);
            }
            standProduct.applied_compressions = next;

			if (!params.onlyLocalCompression) {
                standProduct.height_compression_coef = null;
                standProduct.width_compression_coef = null;
                standProduct.depth_compression_coef = null;
        
				if (!Utils.isNullOrUndefined(params.height_compression_coef)) {
					updateCompressionParams.height_compression_coef = params.height_compression_coef;
					standProduct.product.height_compression_coef = params.height_compression_coef;
				}

				if (!Utils.isNullOrUndefined(params.width_compression_coef)) {
					updateCompressionParams.width_compression_coef = params.width_compression_coef;
					standProduct.product.width_compression_coef = params.width_compression_coef;
				}

				if (!Utils.isNullOrUndefined(params.depth_compression_coef)) {
					updateCompressionParams.depth_compression_coef = params.depth_compression_coef;
					standProduct.product.depth_compression_coef = params.depth_compression_coef;
				}
			} else {
				standProduct.height_compression_coef = params.height_compression_coef 
                    ?? standProduct.height_compression_coef 
                    ?? standProduct.product.height_compression_coef;
                
                standProduct.width_compression_coef = params.width_compression_coef 
                    ?? standProduct.width_compression_coef 
                    ?? standProduct.product.width_compression_coef;

                standProduct.depth_compression_coef = params.depth_compression_coef 
                    ?? standProduct.depth_compression_coef 
                    ?? standProduct.product.depth_compression_coef;
			}

			if (params.allDepth) {
				standProduct.placementDepth = activeProduct.element.getMaxPlacementDepth(activeProduct);
			}

			standProduct.qty = activeProduct.placementDepth * (params.faces || oldFacesQty);
		});

		if (Object.keys(updateCompressionParams).length) {
			const productIds: Array<number> = Utils.unique(this.activity.activeProducts().map((pg: ProductGroup) => pg.product.id));
			this.productsService
				.setCompression(updateCompressionParams, { ids: productIds.join(',') })
				.subscribe(() => this.setProductsPercents());
		}

        const elementsToNormalize = Utils.unique(
            stand.products
                .filter((pg) =>
                    this.activity.activeProducts().some((p) => pg.product.id == p.product.id)
                )
                .map((pg) => pg.element)
        );

		elementsToNormalize.forEach((el) => {
			const groupsToShift = el.products.filter((group) =>
				group.some((pg) =>
					this.activity.activeProducts().some((p) => p.product.id == pg.product.id)
				)
			);
			groupsToShift.forEach((group) =>
				el.shiftProductGroups([group], Utils.to1DArray(group))
			);
		});
		const elements: Array<Element> = this.activity.normalizeElements(stand, elementsToNormalize);

		let dependedProducts = [];

		this.activity.activeProducts().forEach(
			(el) =>
				(dependedProducts = [
					...dependedProducts,
					...Utils.findDependProducts(el, this.stands[0].stand.products)
				])
		);
		this.stands.forEach((stand) => {
			stand.stand.products
				.sort((a, b) => a.getBoundingY().min - b.getBoundingY().min)
				.forEach((p) => {
					if (dependedProducts.includes(p)) {
						p.begin.y = p.dependProduct
							? p.dependProduct.getBoundingY().max
							: p.element.getBoundingY().max;
					}
				});
		});

		this.stands[0].stand.blocks.forEach((b) => {
			const blockProductErrors = b.setProducts(
				Utils.to1DArray(
					this.stands[0].stand.blocks.filter((block) => block != b).map((block) => block.products)
				)
			);

			if (blockProductErrors.length) this.activity.showDeleteMessage$.next({ block: b, errors: blockProductErrors });
		});

		this.activity.refreshActiveProducts();
		this.setRealProductPercents();
		this.historyGenerate();
		this.activity.requestCanvasUpdate(this.stands, elements.map((el: Element) => el.id));
	}

	blockUpdateConfig(params) {
		const idx = this.stands[0].stand.blocks.findIndex(
				(b) => b == this.activity.activeBlocks()[0] || b == this.activity.selectedBlock()
			),
			updatedBlock = this.stands[0].stand.blocks[idx].copy();
		updatedBlock.color = params.color;
		updatedBlock.updateConfig(params);

		const intersections = this.canDrawBlock(
			updatedBlock,
			this.stands[0].stand.blocks.filter((b) => b != this.activity.activeBlocks()[0])
		);

		if (!!intersections.length) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'BLOCK_OVER_BLOCKS_ERROR'
			});
			this.activity.refreshActiveBlocks();

			return;
		}

		this.stands[0].stand.blocks[idx] = updatedBlock;
		this.activity.setActiveBlocks([updatedBlock]);

		const elements: Array<Element> = this.activity.normalizeElements(this.stands[0].stand, updatedBlock.elements);
		this.rebuildProductsTable();
		this.activity.requestCanvasUpdate(this.stands, elements.map((el: Element) => el.id));
		this.historyGenerate();
	}

	POSTextMaterialConfigUpdate(label: Label): void {
		if (this.activity.activePosMaterials().length !== 1) return;
		let updateLabelIndex: number = this.stands[0].stand.POSMaterials.indexOf(this.activity.activePosMaterials()[0]);
		if (updateLabelIndex === -1) return;
		const material: POSTextMaterial = this.stands[0].stand.POSMaterials[updateLabelIndex] as POSTextMaterial;
		material.name = label.name;
		material.angle = label.angle;
		material.color = label.color;
		material.fontSize = label.fontSize;
		material.width = label.width;
		this.activity.setPOSMaterialActive(this.stands[0].stand.POSMaterials[updateLabelIndex]);
		this.activity.requestCanvasUpdate(this.stands);
		this.historyGenerate();
	}

	POSImageMaterialConfigUpdate(configData: POSImageMaterialConfigData): void {
		if (this.activity.activePosMaterials().length !== 1) return;
		let updateLabelIndex: number = this.stands[0].stand.POSMaterials.indexOf(this.activity.activePosMaterials()[0]);
		if (updateLabelIndex === -1) return;
		this.POSImageMaterialsUpdate(configData.material);
		this.activity.setPOSMaterialActive(this.stands[0].stand.POSMaterials[updateLabelIndex]);
		this.POSMaterialsService.update(configData.material.id, configData.dirtyData).subscribe({
			error: () => this.snackBarService.setErrorNotification()
		});
	}

	POSImageMaterialsUpdate(material: POSImageMaterial): void {
		this.stands[0].stand.POSMaterials
			.filter((m: POSMaterial) => m instanceof POSImageMaterial && m.id === material.id)
			.forEach((m: POSImageMaterial) => {
				m.name = material.name;
				m.image = material.image;
				m.width = material.width;
				m.height = material.height;
				m.description = material.description;
				m.opacity = material.opacity;
			});

		Utils.to1DArray(this.history.map((item: HistoryInstance) => item.POSMaterials))
			.filter((m: POSMaterial) => m instanceof POSImageMaterial && m.id === material.id)
			.forEach((m: POSImageMaterial) => {
				m.name = material.name;
				m.image = material.image;
				m.width = material.width;
				m.height = material.height;
				m.description = material.description;
				m.opacity = material.opacity;
			});

		this.activity.requestCanvasUpdate(this.stands);
	}

	redirectStand() {
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		return this.router.navigate(
			this.sessionService.getLink(['stand', 'edit', this.selectedStand]),
			{ relativeTo: this.route, queryParams: { planogram: this.planogramId }, queryParamsHandling: 'merge' }
		);
	}

	redirectPlanogram() {
		this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		this.router.onSameUrlNavigation = 'reload';
		return this.router.navigate(
			this.sessionService.getLink(['stand', 'edit', this.route.snapshot.params.id]),
			{ queryParams: { planogram: this.selectedPlanogram } }
		);
	}

	showUploadLayoutModal() {
		if (this.stands[0].stand.blocks.length) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'EXCEL_UPLOAD_BLOCKS_ERROR',
					twoAnswers: true,
					confirmLabel: 'YES',
					cancelLabel: 'NO'
				}
			});

			dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer) {
					this.activity.setActiveBlocks(this.stands[0].stand.blocks);
					this.deleteItems().then(() => this.activity.requestCanvasUpdateAsync()).then(() => this.uploadExcelModal());
				}
			});
		} else if (this.stands[0].stand.products.length) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'EXCEL_UPLOAD_WARNING_MESSAGE',
					confirmLabel: 'YES',
					cancelLabel: 'NO',
					twoAnswers: true
				}
			});

			dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer) {
					this.activity.setActiveProducts(this.stands[0].stand.products);
					this.deleteItems().then(() => this.activity.requestCanvasUpdateAsync()).then(() => this.uploadExcelModal());
				}
			});
		} else {
			this.uploadExcelModal();
		}
	}

	uploadExcelModal() {
		const dialogRef = this.dialog.open(ExcelToLayoutComponent, {
			width: '600px',
			height: 'fit-content',
			autoFocus: false,
			panelClass: 'upload-excel-modal',
			backdropClass: 'back-drop',
			data: {
				stand: this.stands[0],
				planogram: this.planogramId
			}
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			if (!result) return;
			this.loader = true;
			this.productAddStatus = false;

			this.products = result.origin_products;
			await this.setProductsPercents();
			this.setRealProductPercents();

			const conf: StandConf = this.planogramsService.getConf(this.stands[0], result.products, {
				blocks: [],
				conf: {
					reportParams: this.reportParams,
					productFilters: this.addProductsFilterService.filters,
					products: this.products.map(p => ({
						id: p.id,
						planedPercent: p.planedPercent
					}))
				}
			});

			await this.planogramsService.updatePlacementShelf(this.stands[0].id, conf);

			this.canvasClear();
			this.stands = await Promise.all(this.stands.map((s) => this.getStand(s.id)));
			this.activity.planogram = this.stands[0];

			const svg_conf = [];

			await Promise.all(
				this.stands.map((st: any) => {
					st.stand.allElements.forEach((el) => {
						if (NORMALIZED_ELEMENTS.includes(el.constructor.name)) {
							el.normalize(st.stand);
						}
					});

					const products: IProductConf[] = this.productsService.getProductsToConf(st.id, st.stand.products),
						conf: StandConf = this.planogramsService.getConf(st, products, {
							conf: {
								products: (st.conf?.products || []).map(p => ({
									id: p.id,
									planedPercent: p.planedPercent
								}))
							}
						});

					svg_conf.push({
						main_stand_id: st.main_stand,
						article_photo_mode: this.modes.articleMode(),
						sequence_number_mode: this.modes.sequenceNumberMode(),
						transparent_boxes: this.transparentBoxesMode,
						show_pos_materials: this.POSMaterialsMode,
						view_mode: this.activity.focusedCanvas().element ? 'top': 'direct',
						element_id: this.activity.focusedCanvas().element ?? undefined,
						planogram_id: st.planogram_id,
						show_stock_qty: this.activity.labelsState().stockQty,
						show_prices: this.activity.labelsState().price,
						show_abc_turnover: this.activity.labelsState().abcTurnover,
						show_abc_receipts: this.activity.labelsState().abcReceipts,
						abc_data: this.abcData
					});

					return this.planogramsService.updatePlacementShelf(st.id, conf);
				})
			);
			await this.planogramsService.makeSvg(svg_conf);

			this.historyGenerate();

			this.rebuildProductsTable();

			// reset zoom for correct image capture
			await this.activity.requestCanvasUpdateAsync();

			this.saveAble = false;
			this.loader = false;

			this.getAbcReport();

			setTimeout(() => {
				if (this.equipmentModel) void this.equipmentModel.updateScene();
			});
		});
	}

	// застосування нового масштабу
	async zoomApply(value: number) {
		this.zoom = value;
		void this.canvasUpdate();
	}

	async canDeactivate(): Promise<boolean> {
        if (this.saveObserver) {
			this.loader = true;
			await lastValueFrom(this.saveObserver);
			return true;
		}

		if (!this.saveAble || !this.ignoreOtherSave) return true;
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(
			ConfirmModalComponent,
			{
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'MUST_SAVE_CHANGES',
					cancelLabel: 'NO',
					confirmLabel: 'YES',
					twoAnswers: true
				}
			}
		);
		const result: IConfirmModalAnswer = await lastValueFrom(dialogRef.afterClosed());
		if (!result.answer) {
			const standIndex = this.standsList.findIndex(
				(item) => item.id == this.stands[0].main_stand
			);
			this.standFilters = [
				...this.changeIndex(this.standsList, standIndex, 0)
			];
		}
		return Boolean(result.answer);
	}

	changeIndex(array, fromIndex, toIndex) {
		const element = array[fromIndex];
		array.splice(fromIndex, 1);
		array.splice(toIndex, 0, element);

		return array;
	}

	static insertProductsOnStand(attrs: AutoLayoutParams) {
		const {
			stands,
			blocks,
			productOptions,
			percentDictionary,
			hierarchy,
			cachedPlacePredict,
			groupProperty,
			plannedBrands,
			plannedBrandsPercent,
			plannedCategories,
			plannedCategoriesPercent,
			hasReceipts,
			personalSettings
		} = attrs;

		return (group, idx) => {
			let group_positions = group.limits,
				target_shelves = [],
				target_hooks = [],
				target_holes = [],
				target_boxes = [];

			const sortShelves = () => {
				return (a, b) => {
					if (Math.abs(Utils.round(b.shapes[0].begin.y - a.shapes[0].begin.y)) < 0.01) {
						return Utils.round(a.shapes[0].begin.x - b.shapes[0].begin.x);
					}
					return Utils.round(b.shapes[0].begin.y - a.shapes[0].begin.y);
				};
			};

			if (blocks.length) {
				const block = blocks[idx];

				if (!block) return;

				target_shelves = block.elements
					.filter((el) => {
						if (['ShelfElement', 'PalletElement'].includes(el.constructor.name)) {
							const baseWithCentralPillar: boolean = el.relatedElements
								.some((e: Element) => e.parent.is(EquipmentElements.CentralPillar));

							return !el.depend?.parent.is(EquipmentElements.CentralPillar) && !baseWithCentralPillar;
						}

						return false;
					})
					.sort(sortShelves());

				target_hooks = block.elements
					.filter((el) => el.constructor.name == 'HookElement')
					.sort(sortShelves());

				target_holes = block.elements
					.filter((el) => el.constructor.name == 'HoleElement')
					.sort(sortShelves());

				target_boxes = block.elements
					.filter((el) => el.constructor.name == 'BoxElement')
					.sort(sortShelves());
			} else {
				target_shelves = stands[0].stand.availableElements
					.filter((i) => {
						if (['ShelfElement', 'PalletElement'].includes(i.constructor.name)) {
							const baseWithCentralPillar: boolean = i.relatedElements
								.some((e: Element) => e.parent.is(EquipmentElements.CentralPillar));
							const shelfOnCentralPillar: boolean = i.depend?.parent.is(EquipmentElements.CentralPillar);

							return !shelfOnCentralPillar && !baseWithCentralPillar && i.shapes[0].inRange(group_positions);
						}

						return false;
					})
					.sort(sortShelves());

				target_boxes = stands[0].stand.availableElements.filter(
					(el) =>
						el.constructor.name == 'BoxElement' &&
						el.shapes[0].inRange(group_positions) &&
						!el.products.length
				).sort(sortShelves());
			}

			if (
				!target_shelves.length &&
				!target_hooks.length &&
				!target_boxes.length &&
				!target_holes.length
			)
				return;

			target_shelves.forEach((s) => {
				if (s.constructor.name == 'ShelfElement') {
					s.setPlacementHeight(stands[0].stand);
				}
			});

			let target_shelves_boundings = Utils.to1DArray(
					target_shelves.map((s) => {
						const elementsOnShelf =
								s.constructor.name == 'ShelfElement'
									? s.getElementsOnShelf.filter((el) =>
										el.shapes.some((s) => s.inRange(group_positions))
									)
									: [],
							obj = s.getBoundingX(true),
							siblings = s.getSiblingElements;

						if (obj.min < group_positions.from) {
							obj.min = group_positions.from;
						}
						if (obj.max > group_positions.to) {
							obj.max = group_positions.to;
						}

						if (elementsOnShelf.length) {
							const shapes = Utils.to1DArray(
								elementsOnShelf.map((el) => el.shapes)
							)
								.filter(
									(el) =>
										el.isPointOn(null, s.getBoundingY().min) ||
										el.isPointOn(null, s.getBoundingY().max)
								)
								.filter((el) => el.inRange(group_positions))
								.map((s) => s.getBoundingX())
								.sort((a, b) => a.min - b.min);

							const shapes_boundings = shapes.map((shape, idx) => {
								if (!idx) {
									return {
										min: obj.min,
										max: shape.min,
										width: Utils.round(shape.min - obj.min),
										shelf: s
									};
								}

								return {
									min: shapes[idx - 1].max,
									max: shape.min,
									width: Utils.round(shape.min - shapes[idx - 1].max),
									shelf: s
								};
							});
							shapes_boundings.push({
								min: shapes[shapes.length - 1].max,
								max: obj.max,
								width: Utils.round(obj.max - shapes[shapes.length - 1].max),
								shelf: s
							});

							return shapes_boundings
								.filter((sh) => sh.width > 0)
								.map((sh) => {
									sh.rightSibling = siblings.find(
										(sib) =>
											Math.abs(Utils.round(sib.shapes[0].getBoundingX().min - sh.max)) < 0.01 &&
											target_shelves.includes(sib) &&
											!sib.isElementOnShelfStart
									);
									sh.leftSibling = siblings.find(
										(sib) =>
											Math.abs(Utils.round(sh.min - sib.shapes[0].getBoundingX().max)) < 0.01 &&
											target_shelves.includes(sib) &&
											!sib.isElementOnShelfEnd
									);
									return sh;
								});
						}

						obj.rightSibling = siblings.find(
							(sib) =>
								Math.abs(Utils.round(sib.shapes[0].getBoundingX().min - obj.max)) < 0.01 &&
								target_shelves.includes(sib) &&
								!sib.isElementOnShelfStart
						);
						obj.leftSibling = siblings.find(
							(sib) =>
								Math.abs(Utils.round(obj.min - sib.shapes[0].getBoundingX().max)) < 0.01 &&
								target_shelves.includes(sib) &&
								!sib.isElementOnShelfEnd
						);
						if (obj.max < obj.min) obj.max = obj.min;
						obj.width = Utils.round(obj.max - obj.min);

						return Object.assign({}, obj, { shelf: s });
					})
				),
				groupMeters = Utils.round(
					target_shelves_boundings.reduce((a, b) => a + Math.abs(b.width), 0)
				),
				groupedProducts = [],
				weightProducts = group.products.filter((product) => product.is_loose);

			if (groupProperty == 'none' && !blocks.length) {
				let totalPercent = 0;

				if (plannedBrands.length) {
					plannedBrands
						.sort((a, b) => plannedBrandsPercent[b] - plannedBrandsPercent[a])
						.forEach((brand) => {
							if (totalPercent >= 100) return;
							const realPercent =
								totalPercent + plannedBrandsPercent[brand] < 100
									? plannedBrandsPercent[brand]
									: 100 - totalPercent;
							groupedProducts.push({
								percent: realPercent,
								maximumWidth: (groupMeters * realPercent) / 100,
								realWidth: 0,
								products: group.products.filter(
									(product: Product) =>
										String(product.brandsUniqueId) == brand && !product.is_loose
								)
							});
							totalPercent = Utils.round(totalPercent + realPercent, 3);
						});
					if (totalPercent < 100) {
						const otherProducts = Utils.to1DArray(
								groupedProducts.map((g) => g.products)
							),
							restProducts = group.products.filter(
								(product) =>
									!otherProducts.includes(product) && !product.is_loose
							),
							grouped = Utils.groupBy(restProducts, this.autoLayoutGroupMapper['brand'])
								.map((brand) => {
									return {
										percent: productOptions.useSales
											? Utils.round(
												brand.reduce(
													(a, b) => a + percentDictionary[b.id],
													0
												) * 100,
												3
											)
											: Utils.round(
												(brand.length / restProducts.length) *
												(100 - totalPercent),
												3
											),
										products: brand
									};
								})
								.sort((a, b) => b.percent - a.percent),
							restPercent = Utils.round(
								grouped.reduce((a, b) => a + b.percent, 0),
								3
							),
							percentAddition =
								100 - totalPercent > restPercent
									? (100 - totalPercent - restPercent) / grouped.length
									: 0;
						grouped.forEach((brand) => {
							if (totalPercent >= 100) return;
							const realPercent =
								totalPercent + brand.percent + percentAddition < 100
									? brand.percent + percentAddition
									: 100 - totalPercent;
							groupedProducts.push({
								percent: realPercent,
								maximumWidth: (groupMeters * realPercent) / 100,
								realWidth: 0,
								products: brand.products
							});
							totalPercent = Utils.round(totalPercent + realPercent, 3);
						});
					}
				}
				if (plannedCategories.length) {
					plannedCategories
						.filter((cat) => plannedCategoriesPercent[cat].recom > 0)
						.sort(
							(a, b) =>
								plannedCategoriesPercent[b].recom -
								plannedCategoriesPercent[a].recom
						)
						.forEach((category) => {
							if (totalPercent >= 100) return;
							const categoryProducts = group.products.filter(
									(product: Product) =>
										product.categoriesUniqueId == category && !product.is_loose
								),
								realPercent =
									totalPercent + plannedCategoriesPercent[category].recom < 100
										? plannedCategoriesPercent[category].recom
										: 100 - totalPercent;

							const categoryGroups = [];
							let categoryPercent = 0;
							const brands = Utils.groupBy(categoryProducts, this.autoLayoutGroupMapper['brand'])
								.map((brand) => {
									return {
										percent: productOptions.useSales
											? realPercent *
											Utils.round(
												brand.reduce(
													(a, b) => a + percentDictionary[b.id],
													0
												),
												3
											)
											: realPercent *
											Utils.round(brand.length / categoryProducts.length, 3),
										plannedPercent: plannedCategories.includes(
											String(brand[0].categoriesUniqueId)
										)
											? (realPercent *
												(plannedCategoriesPercent[brand[0].categoriesUniqueId]
													.childrens[brand[0].brandsUniqueId] || 0)) /
											100
											: 0,
										products: brand
									};
								})
								.sort(
									(a, b) =>
										b.plannedPercent - a.plannedPercent || b.percent - a.percent
								);

							let totalBrandsPercent = Utils.round(
									brands.reduce(
										(a, b) => a + (b.plannedPercent || b.percent),
										0
									),
									3
								),
								brandPercentAddition =
									totalBrandsPercent < realPercent
										? (realPercent - totalBrandsPercent) /
										brands.filter((b) => !b.plannedPercent).length
										: 0;

							brands.forEach((brand) => {
								if (categoryPercent >= realPercent) return;
								let brandPercent =
									categoryPercent +
									(brand.plannedPercent ||
										brand.percent + brandPercentAddition) <
									realPercent
										? brand.plannedPercent ||
										brand.percent + brandPercentAddition
										: realPercent - categoryPercent;
								categoryGroups.push({
									percent: brandPercent,
									maximumWidth: (groupMeters * brandPercent) / 100,
									realWidth: 0,
									products: brand.products
								});
								categoryPercent = Utils.round(
									categoryPercent + brandPercent,
									3
								);
							});

							const otherProducts = Utils.to1DArray(
									categoryGroups.map((g) => g.products)
								),
								restProducts = categoryProducts.filter(
									(product) =>
										!otherProducts.includes(product) && !product.is_loose
								);

							if (restProducts.length) {
								categoryGroups.push({
									percent: realPercent - categoryPercent,
									maximumWidth:
										(groupMeters * (realPercent - categoryPercent)) / 100,
									realWidth: 0,
									products: restProducts
								});
							}

							groupedProducts = groupedProducts.concat(categoryGroups);

							totalPercent = Utils.round(totalPercent + realPercent, 3);
						});
					if (totalPercent < 100) {
						const otherProducts = Utils.to1DArray(
								groupedProducts.map((g) => g.products)
							),
							restProducts = group.products.filter(
								(product) =>
									!otherProducts.includes(product) && !product.is_loose
							),
							grouped = Utils.groupBy(restProducts, this.autoLayoutGroupMapper['category'])
								.map((group) => {
									return {
										percent: Utils.round(
											group.reduce((a, b) => a + percentDictionary[b.id], 0) *
											100,
											3
										),
										products: group
									};
								})
								.sort((a, b) => b.percent - a.percent),
							restPercent = Utils.round(
								grouped.reduce((a, b) => a + b.percent, 0),
								3
							),
							percentAddition =
								100 - totalPercent > restPercent
									? (100 - totalPercent - restPercent) / grouped.length
									: 0;
						grouped.forEach((group) => {
							if (totalPercent >= 100) return;
							const realPercent =
								totalPercent + group.percent + percentAddition < 100
									? group.percent + percentAddition
									: 100 - totalPercent;

							const categoryGroups = [];
							let categoryPercent = 0;
							const brands = Utils.groupBy(group.products, this.autoLayoutGroupMapper['brand'])
								.map((brand: Array<Product>) => {
									return {
										percent: productOptions.useSales
											? realPercent *
											Utils.round(
												brand.reduce(
													(a, b) => a + percentDictionary[b.id],
													0
												),
												3
											)
											: realPercent *
											Utils.round(brand.length / group.products.length, 3),
										plannedPercent: plannedCategories.includes(
											String(brand[0].categoriesUniqueId)
										)
											? (realPercent *
												(plannedCategoriesPercent[brand[0].categoriesUniqueId]
													.childrens[brand[0].brandsUniqueId] || 0)) /
											100
											: 0,
										products: brand
									};
								})
								.sort(
									(a, b) =>
										b.plannedPercent - a.plannedPercent || b.percent - a.percent
								);

							let totalBrandsPercent = Utils.round(
									brands.reduce(
										(a, b) => a + (b.plannedPercent || b.percent),
										0
									),
									3
								),
								brandPercentAddition =
									totalBrandsPercent < realPercent
										? (realPercent - totalBrandsPercent) /
										brands.filter((b) => !b.plannedPercent).length
										: 0;
							brands.forEach((brand) => {
								if (categoryPercent >= realPercent) return;
								const brandPercent =
									categoryPercent +
									(brand.plannedPercent ||
										brand.percent + brandPercentAddition) <
									realPercent
										? brand.plannedPercent ||
										brand.percent + brandPercentAddition
										: realPercent - categoryPercent;
								categoryGroups.push({
									percent: brandPercent,
									maximumWidth: (groupMeters * brandPercent) / 100,
									realWidth: 0,
									products: brand.products
								});
								categoryPercent = Utils.round(
									categoryPercent + brandPercent,
									3
								);
							});

							const otherProducts = Utils.to1DArray(
									categoryGroups.map((g) => g.products)
								),
								restProducts = group.products.filter(
									(product) => !otherProducts.includes(product)
								);

							if (restProducts.length) {
								categoryGroups.push({
									percent: realPercent - categoryPercent,
									maximumWidth:
										(groupMeters * (realPercent - categoryPercent)) / 100,
									realWidth: 0,
									products: restProducts
								});
							}

							groupedProducts = groupedProducts.concat(categoryGroups);

							totalPercent = Utils.round(totalPercent + realPercent, 3);
						});
					}
				}
				if (totalPercent < 100) {
					const otherProducts = Utils.to1DArray(
						groupedProducts.map((g) => g.products)
					);
					groupedProducts.push({
						percent: 100 - totalPercent,
						maximumWidth: (groupMeters * (100 - totalPercent)) / 100,
						realWidth: 0,
						products: group.products.filter(
							(product) => !otherProducts.includes(product) && !product.is_loose
						)
					});
				}
			} else if (groupProperty == 'category' && !blocks.length) {
				let groupPercent = 0,
					groupProducts = group.products.filter((product) => !product.is_loose);

				const brands = Utils.groupBy(groupProducts, PlanogramEditComponent.autoLayoutGroupMapper['brand'])
					.map((brand) => {
						return {
							percent: productOptions.useSales
								? Utils.round(
									brand.reduce((a, b) => a + percentDictionary[b.id], 0) *
									100,
									3
								)
								: Utils.round((brand.length / groupProducts.length) * 100, 3),
							plannedPercent: plannedCategories.includes(
								String(brand[0].categoriesUniqueId)
							)
								? plannedCategoriesPercent[brand[0].categoriesUniqueId].childrens[
								brand[0].brandsUniqueId
								] || 0
								: 0,
							products: brand
						};
					})
					.sort(
						(a, b) =>
							b.plannedPercent - a.plannedPercent || b.percent - a.percent
					);

				let totalBrandsPercent = Utils.round(
						brands.reduce((a, b) => a + (b.plannedPercent || b.percent), 0),
						3
					),
					brandPercentAddition =
						totalBrandsPercent < 100
							? (100 - totalBrandsPercent) /
							brands.filter((b) => !b.plannedPercent).length
							: 0;

				brands.forEach((brand) => {
					if (groupPercent >= 100) return;
					let brandPercent =
						groupPercent +
						(brand.plannedPercent || brand.percent + brandPercentAddition) <
						100
							? brand.plannedPercent || brand.percent + brandPercentAddition
							: 100 - groupPercent;
					if (brandPercent == 0) return;
					groupedProducts.push({
						percent: brandPercent,
						maximumWidth: (groupMeters * brandPercent) / 100,
						realWidth: 0,
						products: brand.products
					});
					groupPercent = Utils.round(groupPercent + brandPercent, 3);
				});

				if (groupPercent < 100) {
					const otherProducts = Utils.to1DArray(
						groupedProducts.map((g) => g.products)
					);
					groupedProducts.push({
						percent: 100 - groupPercent,
						maximumWidth: (groupMeters * (100 - groupPercent)) / 100,
						realWidth: 0,
						products: group.products.filter(
							(product) => !otherProducts.includes(product) && !product.is_loose
						)
					});
				}
			} else {
				groupedProducts.push({
					percent: 100,
					maximumWidth: groupMeters,
					realWidth: 0,
					products: group.products.filter((product) => !product.is_loose)
				});
			}

			let groupAccordance = {},
				pg_groups: any = groupedProducts.map((group) => {
					const recommendPercent = group.products.reduce((t, p) => t + percentDictionary[p.id], 0) * 100,
						percentMultiplier = recommendPercent ? group.percent / recommendPercent : 0;

					return Utils.shuffle<Product>(group.products)
						.filter(
							(product) =>
								product.width > 0 &&
								product.height > 0 &&
								product.depth > 0 &&
								product.width < Utils.round(group_positions.to - group_positions.from)
						)
						.map((product, idx, list) => {
							const availableWidth: number = productOptions.useSales && percentMultiplier
									? groupMeters * percentDictionary[product.id] * percentMultiplier
									: group.maximumWidth / list.length,
								facesQty: number = Math.floor(Utils.round(availableWidth / product.width)),
								realFaces: number = productOptions.maxFacesQty && facesQty > productOptions.maxFacesQty
									? productOptions.maxFacesQty
									: facesQty;

							group.realWidth = Utils.round(group.realWidth + realFaces * product.width);

							groupAccordance[product.id] = group;

							return {
								id: product.id,
								qty: realFaces,
								product: product
							};
						});
				});

			pg_groups = Utils.to1DArray(pg_groups).map((p) => {
				const product = p.product,
					pg = new ProductGroup({
						product,
						element: target_shelves[0] || target_hooks[0] || target_holes[0],
						begin: { x: 0, y: 0, z: 0 },
						applied_compressions: product.applied_compressions
					});

				pg.FacesQty = p.qty;
				return pg;
			});

			let weight_groups = Utils.to1DArray(weightProducts);

			if (target_hooks.length) {
				let targetProducts = Utils.to1DArray(groupedProducts.map((g) => g.products || []));

				switch (productOptions.sortBy) {
					case 'ItemHeight': targetProducts.sort(Utils.sortBy('height', productOptions.sortType)); break;
					case 'price': targetProducts.sort(Utils.sortBy('avgPrice', productOptions.sortType)); break;
					default: targetProducts.sort((a, b) => hierarchy.findIndex((h) => a.id.toString() == h) - hierarchy.findIndex((h) => b.id.toString() == h)); break;
				}

				targetProducts
					.forEach((product, idx) => {
						if (target_hooks.length <= idx) return;
						const pg = new ProductGroup({
							product,
							element: target_hooks[idx],
							begin: { x: 0, y: 0, z: 0 },
							applied_compressions: product.applied_compressions
						});
						if (!~personalSettings.productDepthHook) {
							pg.placementDepth = pg.element.getMaxPlacementDepth(pg);
						}
						pg.FacesQty = 1;
						stands[0].stand.products.push(pg);
					});
			}

			if (target_holes.length) {
				const targetProducts = Utils.to1DArray(groupedProducts.map((g) => g.products || []));

				switch (productOptions.sortBy) {
					case 'ItemHeight': targetProducts.sort(Utils.sortBy('height', productOptions.sortType)); break;
					case 'price': targetProducts.sort(Utils.sortBy('avgPrice', productOptions.sortType)); break;
					default: targetProducts.sort((a, b) => hierarchy.findIndex((h) => a.id.toString() == h) - hierarchy.findIndex((h) => b.id.toString() == h)); break;
				}

				targetProducts
					.forEach((product, idx) => {
						if (target_holes.length <= idx) return;
						const pg = new ProductGroup({
							product,
							element: target_holes[idx],
							begin: { x: 0, y: 0, z: 0 },
							applied_compressions: product.applied_compressions
						});
						pg.FacesQty = 1;
						stands[0].stand.products.push(pg);
					});
			}

			if (target_boxes.length && weight_groups.length) {
				weight_groups
					.sort(
						(a, b) =>
							hierarchy.findIndex((h) => a.id.toString() == h) -
							hierarchy.findIndex((h) => b.id.toString() == h)
					)
					.forEach((p, idx) => {
						if (target_boxes.length <= idx) return;
						const pg = new ProductGroup({
							product: p,
							element: target_boxes[idx],
							begin: { x: 0, y: 0, z: 0 },
							applied_compressions: p.applied_compressions
						});
						pg.FacesQty = 1;
						stands[0].stand.products.push(pg);
					});
			}

			PlanogramEditComponent.checkAutolayoutFreeSpace(
				pg_groups,
				groupMeters,
				groupAccordance,
				hierarchy,
				productOptions.maxFacesQty
			);

			if (hasReceipts) {
				if (productOptions.sortBy == 'golden_shelf') {
					pg_groups.sort(
						(a, b) =>
							cachedPlacePredict[b.product.id].turnover -
							cachedPlacePredict[a.product.id].turnover
					);
					target_shelves_boundings.sort((a, b) => {
						const goldenA = GoldenShelf.isPointOn(a.shelf.shapes[0].begin.y),
							goldenB = GoldenShelf.isPointOn(b.shelf.shapes[0].begin.y);

						return goldenA && !goldenB ? -1 : !goldenA && goldenB ? 1 : 0;
					});
					/*} else if (['ItemHeight', 'price', 'none'].includes(productOptions.sort_by) && groupProperty !== 'none') {
						pg_groups.sort(Utils.sortBy(productOptions.sort_by, productOptions.sort_type));*/
				} else {
					pg_groups = Utils.shuffle(pg_groups);
				}
			} else {
				pg_groups.sort(Utils.sortBy('ItemHeight'));
			}

			if (!target_shelves_boundings.length) return;

			let shelf_idx = 0,
				{ min: from_x } = target_shelves_boundings[0];

			const getMaxSiblingPoint = (idx) => {
				if (target_shelves_boundings[idx].rightSibling) {
					const sibIdx = target_shelves_boundings.findIndex(
						(t) => t.shelf == target_shelves_boundings[idx].rightSibling
					);
					if (sibIdx == -1) {
						return target_shelves_boundings[idx].max;
					}
					return getMaxSiblingPoint(sibIdx);
				}
				return target_shelves_boundings[idx].max;
			};

			const getMinSiblingPoint = (idx) => {
				if (target_shelves_boundings[idx].leftSibling) {
					const sibIdx = target_shelves_boundings.findIndex(
						(t) => t.shelf == target_shelves_boundings[idx].leftSibling
					);
					if (sibIdx == -1) {
						return target_shelves_boundings[idx].max;
					}
					return getMinSiblingPoint(sibIdx);
				}
				return target_shelves_boundings[idx].min;
			};

			pg_groups.forEach((pg) => {
				if (pg.qty == 0) return;
				let target_shelf = target_shelves_boundings[shelf_idx].shelf,
					pg_copy = pg.copy();

				pg_copy.Element = target_shelf;

				while (
					shelf_idx < target_shelves_boundings.length - 1 &&
					Utils.round(pg.Width + from_x) > getMaxSiblingPoint(shelf_idx)
					) {
					pg_copy = pg.copy();
					pg_copy.Element = target_shelf;

					let free_space = Utils.round(getMaxSiblingPoint(shelf_idx) - from_x),
						free_faces_qty = Math.floor(Utils.round(free_space / pg.ItemWidth));

					if (free_faces_qty) {
						const sliced_pg = pg.copy();

						if (!target_shelf) break;

						sliced_pg.Element = target_shelf;
						sliced_pg.begin.x = from_x;
						sliced_pg.begin.y = target_shelf.getBoundingY().max;
						sliced_pg.begin.z = target_shelf.getBoundingZ().max;

						if (
							productOptions.checkSizes &&
							target_shelf.constructor.name == 'ShelfElement' &&
							target_shelf.hasShelfIntersections(sliced_pg, from_x)
						)
							break;

						if (target_shelf.products.some(pr => pr.some(p => p.product.id == sliced_pg.product.id))) break;

						sliced_pg.placementDepth = target_shelf.getMaxPlacementDepth(sliced_pg, true);

						if (!sliced_pg.placementDepth || isNaN(sliced_pg.placementDepth)) break;

						if (!!~personalSettings.productDepthShelf) sliced_pg.placementDepth = 1;

						sliced_pg.FacesQty = blocks.length
							? free_faces_qty
							: Math.abs(free_faces_qty);
						sliced_pg.element = target_shelf;

						stands[0].stand.products.push(sliced_pg);

						pg.FacesQty -= free_faces_qty;

						while (
							Utils.round(sliced_pg.begin.x + sliced_pg.Width) >
							target_shelves_boundings[shelf_idx].max
							) {
							++shelf_idx;
							target_shelf = target_shelves_boundings[shelf_idx].shelf;
						}
						from_x = Utils.round(sliced_pg.begin.x + sliced_pg.Width);
					}

					if (shelf_idx != target_shelves_boundings.length - 1) {
						++shelf_idx;
						target_shelf = target_shelves_boundings[shelf_idx].shelf;
						from_x = target_shelves_boundings[shelf_idx].min;
					}
				}

				pg_copy = pg.copy();
				pg_copy.Element = target_shelf;
				pg_copy.begin.x = from_x;
				pg_copy.begin.y = target_shelf.getBoundingY().max;
				pg_copy.begin.z = target_shelf.getBoundingZ().max;

				if (
					productOptions.checkSizes &&
					target_shelf.constructor.name == 'ShelfElement' &&
					target_shelf.hasShelfIntersections(pg_copy, from_x)
				)
					return;

				if (target_shelf.products.some(pr => pr.some(p => p.product.id == pg_copy.product.id))) return;

				let pg_faces = pg.FacesQty;

				const shelf_max_point = getMaxSiblingPoint(shelf_idx);

				if (Math.abs(Utils.round(shelf_max_point - from_x)) < pg.Width) {
					pg_faces = Math.floor(
						Math.abs(Utils.round((shelf_max_point - from_x) / pg.ItemWidth))
					);
				}

				if (!pg_faces) return;
				if (!target_shelf) return;

				pg.Element = target_shelf;

				pg.begin.x = from_x;
				pg.begin.y = target_shelf.getBoundingY().max;
				pg.begin.z = target_shelf.getBoundingZ().max;
				pg.placementDepth = target_shelf.getMaxPlacementDepth(pg, true);

				if (!pg.placementDepth || isNaN(pg.placementDepth)) return;

				if (!!~personalSettings.productDepthShelf) pg.placementDepth = 1;

				pg.FacesQty = Math.abs(pg_faces);

				if (!from_x) {
					pg.begin.x = target_shelves_boundings[shelf_idx].min;
					from_x = Utils.round(from_x + pg.begin.x + pg.Width);
				} else if (
					from_x &&
					shelf_idx &&
					from_x == target_shelves_boundings[shelf_idx].min &&
					!blocks.length &&
					target_shelves_boundings[shelf_idx].leftSibling
				) {
					pg.begin.x = Utils.max(
						target_shelves_boundings[shelf_idx].leftSibling.products.map(
							(p) => p[0].getBoundingX().max
						)
					);
					from_x = Utils.round(from_x + pg.Width);
				} else {
					pg.begin.x = from_x;
					from_x = Utils.round(from_x + pg.Width);
				}

				while (
					from_x > target_shelves_boundings[shelf_idx].max &&
					shelf_idx < target_shelves_boundings.length - 1
					) {
					++shelf_idx;
					target_shelf = target_shelves_boundings[shelf_idx].shelf;
					if (!target_shelves_boundings[shelf_idx].leftSibling) {
						from_x = target_shelves_boundings[shelf_idx].min;
					}
				}

				stands[0].stand.products.push(pg);
			});

			groupedProducts.forEach((group) => {
				const ids = group.products.map((p) => p.article);
				group.realWidth = Utils.round(
					stands[0].stand.products.reduce((t, p) => {
						if (ids.includes(p.product.article)) {
							return Utils.round(t + p.Width);
						} else {
							return t;
						}
					}, 0)
				);
			});

			if (!blocks.length) {
				// перерахунок вільного місця на полицях
				const last_shelf_occupied_space =
						Utils.round(from_x - target_shelves_boundings[shelf_idx].min),
					empty_shelves = target_shelves_boundings.slice(
						shelf_idx,
						target_shelves_boundings.length
					),
					empty_meters =
						Utils.round(
							empty_shelves.reduce((a, b) => a + Math.abs(b.width), 0) - last_shelf_occupied_space
						),
					productsIds = stands[0].stand.products
						.sort((a, b) => b.plannedPercent - a.plannedPercent)
						.map((p) => p.product.id.toString());

				// визначення невикладених товарів та перерахунок фейсів для кожного з них
				let unlaid_pg_groups = pg_groups.filter(
					(pg) => !productsIds.includes(pg.product.id.toString())
				);
				unlaid_pg_groups.forEach((pg) => {
					const facesQty = productOptions.useSales
						? Math.floor(Utils.round((empty_meters * percentDictionary[pg.product.id]) / pg.product.width)) || 1
						: Math.floor(Utils.round(empty_meters / unlaid_pg_groups.length / pg.product.width)) || 1;
					pg.FacesQty =
						productOptions.maxFacesQty &&
						facesQty > productOptions.maxFacesQty
							? productOptions.maxFacesQty
							: facesQty;
				});

				PlanogramEditComponent.checkAutolayoutFreeSpace(
					unlaid_pg_groups,
					empty_meters,
					groupAccordance,
					hierarchy,
					productOptions.maxFacesQty
				);

				// докладання невиставленого товару на пусті полиці
				target_shelves_boundings.forEach((b, idx) => {
					const siblingMaxPoint = getMaxSiblingPoint(idx),
						siblingMinPoint = getMinSiblingPoint(idx),
						siblingProducts = [
							...b.shelf.products,
							...b.shelf.getSiblings.reduce(
								(p, s) => [...p, ...s.products],
								[]
							)
						].filter(
							(p) =>
								siblingMinPoint <= p[0].getBoundingX().min &&
								p[0].getBoundingX().max <= siblingMaxPoint
						);
					let productsMax = siblingProducts.length
							? Utils.max(siblingProducts.map((p) => p[0].getBoundingX().max))
							: b.min,
						diff = Utils.round(b.max - productsMax),
						min = unlaid_pg_groups.length
							? Math.min.apply(
								null,
								unlaid_pg_groups.map((pg) => pg.Width)
							)
							: 0,
						max_iterations = 10,
						iteration_count = 0,
						unlaid_hierarchy = hierarchy.filter(
							(p) => !productsIds.includes(p)
						);

					if (productsMax > b.max) return;
					while (diff > min) {
						iteration_count++;

						for (let product_id of unlaid_hierarchy) {
							const pg = unlaid_pg_groups.find(
								(pg) => pg.product.id == product_id
							);

							if (!pg) continue;

							const group = groupAccordance[pg.product.id],
								groupDiff = Utils.round(group.maximumWidth - group.realWidth);

							if (Utils.round(pg.ItemWidth * pg.FacesQty) > groupDiff) continue;

							let pg_faces = pg.FacesQty;
							if (Math.abs(Utils.round(siblingMaxPoint - productsMax)) < pg.Width) {
								pg_faces = Math.floor(
									Math.abs(Utils.round((siblingMaxPoint - productsMax) / pg.ItemWidth))
								);
							}
							if (!pg_faces) return;

							pg.begin.x = productsMax;
							pg.begin.y = b.shelf.getBoundingY().max;
							pg.begin.z = b.shelf.getBoundingZ().max;
							pg.placementDepth = b.shelf.getMaxPlacementDepth(pg, true);
							pg.element = b.shelf;

							if (!pg.placementDepth || isNaN(pg.placementDepth)) return;

							if (!!~personalSettings.productDepthShelf) pg.placementDepth = 1;

							pg.FacesQty = Math.abs(pg_faces);

							if (
								productOptions.checkSizes &&
								b.shelf.constructor.name == 'ShelfElement' &&
								b.shelf.hasShelfIntersections(pg)
							)
								continue;

							if (pg.getBoundingX().max > siblingMaxPoint) continue;

							stands[0].stand.products.push(pg);
							unlaid_pg_groups = unlaid_pg_groups.filter(
								(p) => p.product.id !== pg.product.id
							);
							productsIds.push(product_id);
							productsMax = Utils.round(productsMax + pg.Width);
							diff = Utils.round(diff - pg.Width);
							group.realWidth = Utils.round(group.realWidth + pg.Width);

							if (diff > min) continue;
							break;
						}

						if (iteration_count === max_iterations) break;
					}
				});
				// додавання фейсів товарам, якщо на полиці залишилось вільне місце
				target_shelves_boundings.forEach((b, idx) => {
					if (b.leftSibling) return;
					const siblingMaxPoint = getMaxSiblingPoint(idx),
						siblingMinPoint = getMinSiblingPoint(idx),
						siblingProducts = [
							...b.shelf.products,
							...b.shelf.getSiblings.reduce(
								(p, s) => [...p, ...s.products],
								[]
							)
						].filter(
							(p) =>
								siblingMinPoint <= p[0].getBoundingX().min &&
								p[0].getBoundingX().max <= siblingMaxPoint
						);
					let productsWidth = siblingProducts.length
							? siblingProducts.reduce((a, b) => a + b[0].Width, 0)
							: 0,
						diff = Utils.round(siblingMaxPoint - siblingMinPoint - productsWidth),
						min = siblingProducts.length
							? Math.min.apply(
								null,
								b.shelf.products.map((i) => i[0].ItemWidth)
							)
							: 0,
						max_iterations = 10,
						iteration_count = 0;

					while (diff > min) {
						iteration_count++;

						for (let product_id of hierarchy) {
							const product = siblingProducts.find(
								(pg) => pg[0].product.id == product_id
							);

							if (!product) continue;

							const group = groupAccordance[product[0].product.id],
								groupDiff = Utils.round(group.maximumWidth - group.realWidth);

							if (product[0].ItemWidth > diff) continue;

							if (product[0].ItemWidth > groupDiff) continue;

							if (
								productOptions.maxFacesQty &&
								product[0].FacesQty >= productOptions.maxFacesQty
							)
								continue;

							product[0].FacesQty += 1;
							diff = Utils.round(diff - product[0].ItemWidth);
							group.realWidth = Utils.round(group.realWidth + product[0].ItemWidth);

							if (diff > min) continue;
							break;
						}

						if (iteration_count === max_iterations) break;
					}
				});
			}

			if (!['none', 'golden_shelf'].includes(productOptions.sortBy)) {
				PlanogramEditComponent.updateProductsPosition(
					target_shelves_boundings,
					productOptions,
					productOptions.sortBy
				);
			}
		};
	}

	static updateProductsPosition(boundings, productOptions: IAutoLayoutParams, type) {
		const sorted_groups =
			PlanogramEditComponent.getSortedGroups(boundings);

		sorted_groups.forEach((group) => {
			const min_x = Math.min.apply(
				null,
				group.map((pg) => pg.begin.x)
			);
			let products;
			switch (type) {
				case 'ItemHeight':
					const sortedProducts = group.sort(
							Utils.sortBy(productOptions.sortBy, productOptions.sortType)
						),
						groupedProducts = Utils.groupBy(sortedProducts, type, false);

					groupedProducts.forEach((g) => {
						if (products) {
							products = [...products, ...Utils.shuffle(g)];
						} else {
							products = [...Utils.shuffle(g)];
						}
					});
					break;
				case 'price':
					const priceSortedProducts = group.sort(
							Utils.sortBy('avgPrice', productOptions.sortType)
						),
						priceGroupedProducts = Utils.groupBy(priceSortedProducts, type, false);

					priceGroupedProducts.forEach((g) => {
						if (products) {
							products = [...products, ...Utils.shuffle(g)];
						} else {
							products = [...Utils.shuffle(g)];
						}
					});
					break;
				case 'shuffle':
					products = Utils.shuffle(group);
					break;
			}
			products.reduce((a, b) => {
				b.begin.x = a;
				return b.getBoundingX().max;
			}, min_x);
		});
	}

	static getSortedGroups(boundings): Array<Array<ProductGroup>> {
		const sibling_boundings = [];
		let sibling_boundings_index = 0;
		boundings.forEach((b) => {
			if (!sibling_boundings[sibling_boundings_index]) {
				sibling_boundings.push({
					min: b.min,
					max: b.max,
					shelves: []
				});
			}
			sibling_boundings[sibling_boundings_index].shelves.push(b.shelf);
			if (!b.rightSibling) {
				sibling_boundings[sibling_boundings_index].max = b.max;
				sibling_boundings_index++;
			}
		});
		const sorted_groups = [];
		sibling_boundings.forEach((b, idx) => {
			if (!sorted_groups[idx]) sorted_groups[idx] = [];
			b.shelves.forEach((s) => {
				sorted_groups[idx].push(
					...s.products
						.filter(
							(pg) =>
								pg[0].getBoundingX().min >= b.min - EPS_3 &&
								pg[0].getBoundingX().max <= b.max + EPS_3
						)
						.map((p) => p[0])
				);
			});
		});
		return sorted_groups.filter((g) => g.length);
	}

	static zipProducts(a, b) {
		if (typeof a === 'object') {
			b.begin.x = a.begin.x + a.Width;

			return b;
		}

		if (typeof a === 'number') {
			b.begin.x = a;

			return b.begin.x + b.Width;
		}
	}

	async autoLayout(data?: IAutoLayoutParams) {
		this.blockAutolayout = true;
		this.loader = true;

		if (this.stands[0].stand.availableElements.some((el: Element) => el.is(HangBarElement))) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'AUTO_LAYOUT_HANG_BAR_WARNING'
			});
		}

		if (this.stands[0].stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'AUTO_LAYOUT_ISLAND_RACK_WARNING'
			});
		}

		this.mixPanelService.trackPage(MixpanelEvents.AutoLayout);

		let selectedBlocks: Array<Block> = [];
		if (this.activity.activeBlocks().length) selectedBlocks = this.activity.activeBlocks();
		if (this.activity.selectedBlock()) selectedBlocks = [this.activity.selectedBlock()];
		if (!selectedBlocks.length) selectedBlocks = this.stands[0].stand.blocks;

		let isDateChanged: boolean = false;
		if (data) {
			if (data.useSales) {
				this.reportParams = {
					qty: data.coefficientSalesQty,
					profit: data.coefficientProfit,
					receipts_qty: data.coefficientReceiptsQty,
					turnover: data.coefficientTurnover
				};
				isDateChanged = !this.cachedAutolayoutParams.period || !Utils.equalObjects(data.period, this.cachedAutolayoutParams.period);
			}
			Utils.assignObject(this.cachedAutolayoutParams, data);
			this.cachedAutolayoutParams = { ...this.cachedAutolayoutParams };
		}

		const percentDictionary: any = this.cachedReportData || {},
			productOptions: IAutoLayoutParams = this.cachedAutolayoutParams,
			productsIds: Array<number> = this.products.map((product: Product) => product.id),
			reportParams = Object.assign({}, this.reportParams, { products: productsIds.join(',') }),
			finalProducts: Array<{ products: Array<Product>, width: number, limits?: Range }> = [];

		if (!Utils.equalObjects(this.cachedReportParams, reportParams) || isDateChanged) {
			const { date_from, date_to } = this.filtersService.Filters,
				response: any = await this.productsService.getProductsPercent(
					Object.assign({}, this.reportParams || {}, { date_from, date_to }),
					productsIds,
					this.planogramId,
					this.standId
				);

			for (let key in response) {
				if (response.hasOwnProperty(key)) percentDictionary[key] = (response[key].percent || 0) / 100;
			}

			this.placePredictCache = response;
			this.cachedPlacePredict = response;
			this.cachedReportData = percentDictionary;
			this.cachedReportParams = reportParams;
		}

		const hierarchy: Array<string> = Object.keys(percentDictionary)
				.sort((a, b) => percentDictionary[b] - percentDictionary[a]),
			isBrandsPlanned: boolean = !this.brandsPanelDataGrouped &&
				Object.values(this.brandsRecommendedPercent).some((el: number) => el > 0),
			plannedBrands = isBrandsPlanned
				? Object.keys(this.brandsRecommendedPercent)
					.filter((key: string) => this.brandsRecommendedPercent[key] > 0)
				: [],
			isCategoriesPlanned: boolean = this.brandsPanelDataGrouped &&
				Object.values(this.categoriesRecommendedPercent)
					.some((el: any) => el.recom > 0 || Object.values(el.childrens).some((ch: number) => ch > 0)),
			plannedCategories: Array<string> = isCategoriesPlanned
				? Object.keys(this.categoriesRecommendedPercent)
					.filter((key: string) => this.categoriesRecommendedPercent[key].recom > 0 ||
						Object.values(this.categoriesRecommendedPercent[key].childrens).some((ch: number) => ch > 0))
				: [];

		let useProducts: Array<Product> = [...this.products];
		if (this.sessionService.hasFeature(ServiceFeatures.MasterPositions) && !productOptions.useMasterPositions) {
			useProducts = useProducts.filter((product: Product) => !product.is_master_position);
		}

		if (selectedBlocks.length) {
			const activeBlocksProducts: Array<ProductGroup> = Utils.to1DArray(
				selectedBlocks.map((b: Block) => b.products)
			);

			this.stands[0].stand.products = this.stands[0].stand.products
				.filter((pg: ProductGroup) => !activeBlocksProducts.includes(pg));

			selectedBlocks.forEach((b: Block) => {
				const groupBy: any = b.getGroupByOption(this.autoLayoutHierachy);
				let output: Array<Product> = useProducts.map((product: Product) => product.copy());

				if (!b.hasEmptySettings) {
					output = output.filter((p: Product) => b.isProductSuitable(p))
				} else {
					switch (groupBy) {
						case 'category': {
							output = output.filter((p: Product) => Utils.to1DArray(p.categories).some(i => b.categories.includes(i.id)));
							break;
						}
						case 'brand': {
							output = output.filter((p: Product) => p.brands.some(i => b.brands.includes(i.id)));
							break;
						}
						case 'supplier': {
							output = output.filter((p: Product) => p.suppliers.some(i => b.suppliers.includes(i.id)));
							break;
						}
					}
				}

				output = Utils.to1DArray(Utils.groupBy(output, PlanogramEditComponent.autoLayoutGroupMapper[this.autoLayoutExtraMapper[groupBy]]));

				finalProducts.push({
					products: Utils.shuffle(output),
					width: b.width,
					limits: { from: b.boundingX.min, to: b.boundingX.max }
				});
			});
		} else {
			this.stands[0].stand.products = [];

			const groupProperty = productOptions.groupBy,
				groupedProducts = Utils.groupBy(useProducts, PlanogramEditComponent.autoLayoutGroupMapper[groupProperty])
					.map((group) =>
						Utils.to1DArray(
							Utils.groupBy(group, PlanogramEditComponent.autoLayoutGroupMapper[this.autoLayoutExtraMapper[groupProperty]])
						)
					),
				isProductsGrouped: boolean = ['category', 'brand'].includes(groupProperty) &&
					(isBrandsPlanned || isCategoriesPlanned);

			if (isProductsGrouped) {
				const groupPercentDictionary =
						groupProperty == 'category'
							? this.categoriesRecommendedPercent
							: this.brandsRecommendedPercent,
					recommendedPercents = groupedProducts.reduce((p, group: Array<Product>) => {
						const id =
							groupProperty == 'category'
								? group[0].categoriesUniqueId || '-1'
								: group[0].brandsUniqueId || '-1';
						p[id] = group.reduce((a, b) => a + percentDictionary[b.id], 0);
						return p;
					}, {}),
					panelIds = groupProperty == 'category'
						? this.categoriesInfoCopy.map((c) => c.id)
						: this.brandsInfoCopy.map((b) => b.id),
					sortedIds = Object.keys(groupPercentDictionary)
						.filter((item) => panelIds.includes(String(item)))
						.sort((a, b) => {
							if (groupProperty == 'category') {
								return groupPercentDictionary[b].recom - groupPercentDictionary[a].recom ||
									recommendedPercents[b] - recommendedPercents[a];
							} else {
								return groupPercentDictionary[b] - groupPercentDictionary[a] ||
									recommendedPercents[b] - recommendedPercents[a];
							}
						}),
					groupedProductsSorted = [];

				groupedProducts.forEach((group: Array<Product>) => {
					const id = groupProperty == 'category' ? group[0].categoriesUniqueId || '-1' : group[0].brandsUniqueId || '-1',
						index = sortedIds.indexOf(String(id));

					groupedProductsSorted[index] = { id, products: group };
				});

				finalProducts.push(
					...this.setFragmentsForGroupedProducts(
						groupedProductsSorted,
						recommendedPercents,
						groupProperty,
						groupPercentDictionary,
						productOptions.useSales
					)
				);
			} else {
				const standWidth = this.stands[0].stand.Width;
				groupedProducts.forEach((group) => {
					if (productOptions.useSales) {
						finalProducts.push({
							products: group,
							width: Utils.round(
								group.reduce((a, b) => Utils.round(a + percentDictionary[b.id]), 0) *
								standWidth
							)
						});
					} else {
						finalProducts.push({
							products: group,
							width: Utils.round((group.length / useProducts.length) * standWidth)
						});
					}
				});
			}

			const reindexProducts = [...finalProducts],
				reindexValues = Utils.shuffle(Object.entries(finalProducts)).reduce(
					(a, b, idx) => {
						a[b[0]] = idx;
						return a;
					},
					{}
				);

			for (let idx in reindexValues) {
				if (reindexValues.hasOwnProperty(idx))
					finalProducts[reindexValues[idx]] = reindexProducts[idx];
			}

			finalProducts.forEach((group, index) => {
				const from = index ? finalProducts[index - 1].limits.to : 0;
				group.limits = { from, to: Utils.round(from + group.width) };
			});
		}

		finalProducts.forEach(
			PlanogramEditComponent.insertProductsOnStand({
				stands: this.stands,
				blocks: selectedBlocks,
				productOptions,
				percentDictionary,
				hierarchy,
				cachedPlacePredict: this.cachedPlacePredict,
				dataGrouped: this.brandsPanelDataGrouped,
				groupProperty: productOptions.groupBy,
				plannedBrands,
				plannedBrandsPercent: this.brandsRecommendedPercent,
				plannedCategories,
				plannedCategoriesPercent: this.categoriesRecommendedPercent,
				hasReceipts: this.sessionService.hasFeature(ServiceFeatures.Receipts),
				personalSettings: this.sessionService.personalSettings
			})
		);

		this.activity.normalizeElements(this.stands[0].stand);

		this.getAbcReport();
		await this.setProductsPercents();
		this.setRealProductPercents();
		this.activity.setProductActive();
		this.buildProductsTable(false);
		this.historyGenerate();
		this.activity.requestCanvasUpdateAsync().finally(() => {
			this.loader = false;
			this.blockAutolayout = false;
		});
	}

	applyLayoutRule({ data, rule, stand }) {
		this.changeCanvasListening(false);

		this.activity.setLayoutRulesProducts(this.layoutRules ? data : {});

		this.activity.normalizeElements(stand, [], true);

		this.renderBehavior.next({ rule, stand });
	}

	saveLayoutRule(id: number) {
		this.activeRule.saved = true;
		if (!this.activeRule.id) {
			this.activeRule.id = id;
			this.history.forEach((historyObj: HistoryInstance) => historyObj.rule.id = id);
		}
		this.mixPanelService.trackPage(MixpanelEvents.LayoutRuleSave, {
			ruleName: this.activeRule.name
		});
	}

	setFragmentsForGroupedProducts(
		groupedProducts,
		recommendedPercents,
		groupProperty,
		groupPercentDictionary,
		takeIntoPrice
	) {
		const fragments = [],
			standWidth = this.stands[0].stand.Width,
			zeroGroupsLength = groupedProducts
				.filter((group) =>
					groupProperty == 'category'
						? groupPercentDictionary[group.id].recom == 0
						: groupPercentDictionary[group.id] == 0
				)
				.reduce((a, b) => a + b.products.length, 0),
			totalPlannedPercent =
				Object.values<any>(groupPercentDictionary)
					.map((prop) => (groupProperty == 'category' ? prop.recom : prop))
					.reduce((a, b) => Utils.round(a + b), 0) / 100,
			totalRecommendedPercent = Object.keys(groupPercentDictionary)
				.filter((key) =>
					groupProperty == 'category'
						? groupPercentDictionary[key].recom == 0
						: groupPercentDictionary[key] == 0
				)
				.reduce((p, key) => recommendedPercents[key] + p, 0);

		let previousElementsSum = 0;

		for (let group of groupedProducts) {
			if (previousElementsSum >= standWidth) break;

			const productItemValue =
				groupProperty == 'category'
					? groupPercentDictionary[group.id].recom
					: groupPercentDictionary[group.id];

			if (groupProperty == 'category') {
				const groupedData = Utils.groupBy(group.products, PlanogramEditComponent.autoLayoutGroupMapper['brand']);
				groupedData.forEach((g, idx) =>
					g.forEach(
						(p) =>
							(p.plannedPercent = this.getBrandPlannedPercent(
								p,
								groupedData[idx].length
							))
					)
				);
			}

			if (productItemValue) {
				fragments.push({
					products: group.products,
					width: Utils.round(
						Math.min(
							standWidth - previousElementsSum,
							(productItemValue / 100) * standWidth
						)
					)
				});
			} else {
				let recommendPercent,
					restPercent = totalPlannedPercent < 1 ? 1 - totalPlannedPercent : 0;

				if (takeIntoPrice) {
					recommendPercent = recommendedPercents[group.id];
					if (restPercent > totalRecommendedPercent) {
						recommendPercent =
							(recommendPercent / totalRecommendedPercent) * restPercent;
					}
				} else {
					recommendPercent =
						(restPercent * group.products.length) / zeroGroupsLength;
				}

				const width = Utils.round(
					Math.min(
						standWidth - previousElementsSum,
						recommendPercent * standWidth
					)
				);

				fragments.push({
					products: group.products,
					width: isNaN(width) ? 0 : width
				});
			}
			previousElementsSum += fragments[fragments.length - 1].width;
		}

		return fragments;
	}

	getBrandPlannedPercent(product: Product, length) {
		const categoryId = product.categoriesUniqueId || '-1',
			brandId = product.brandsUniqueId || '-1',
			productCategory = this.categoriesRecommendedPercent[categoryId];

		if (productCategory) {
			return productCategory.childrens[brandId] / 100 / length;
		}
		return 0;
	}

	getChangedProducts(n_id, s_id, p_id) {
		return this.layoutService.getNotificationStandById(n_id, s_id, p_id)
			.then((data) => {
				this.changedProducts = data.notification_conf.data.find(
					(i) => i.id == Number(s_id)
				);
			})
			.then(() => {
				this.changedProducts.conf.data.forEach((p) => {
					this.activity.addRotatedProduct(p.product_rotate_to.id);
				});
			});
	}

	openReportConfigs() {
		this.activity.openPanel('autolayout');
	}

	showEditInstrumentWarning() {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'EDIT_INSTRUMENT_WARNING_MESSAGE',
				confirmLabel: 'YES',
				cancelLabel: 'NO',
				twoAnswers: true
			}
		});

		dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
			if (result.answer) {
				this.mixPanelService.trackPage(MixpanelEvents.EquipmentEditFromPlanogram);
				return this.router.navigate(
					this.sessionService.getLink(['equipment', 'edit', this.stands[0].stand.id]),
					{
						queryParams: {
							stand_id: this.stands[0].id,
							planogram_id: this.planogramId,
							from: 'stand'
						}
					}
				);
			}
		});
	}

	revertStand(id: number): void {
		if (!this.sessionService.hasFeature(ServiceFeatures.PlanogramRevert)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.RevertPlanogram);
			return;
		}

		const revert = (id: number) => {
			this.loader = true;
			this.planogramsService.revertStand(id).subscribe(() => {
				this.mixPanelService.trackPage(MixpanelEvents.PlanogramRevert);
				window.location.reload();
			});
		};

		if (this.saveAble && this.ignoreOtherSave) {
			const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'SAVING_BEFORE_REVERT_WARNING',
					confirmLabel: 'YES',
					cancelLabel: 'NO',
					twoAnswers: true
				}
			});

			dialogRef.afterClosed().subscribe(async (result: IConfirmModalAnswer) => {
				if (result.answer === null) return;
				if (result.answer) await this.saveAction(false, true);
				revert(id);
			});
		} else {
			revert(id);
		}
	}

	clearToolLayer() {
		this.toolsLayer.destroyChildren();
		this.toolsLayer.draw();
	}

	openLayout() {
		this.layoutOpen = !this.layoutOpen;
	}

	async openEfficiencyAnalysis() {
		this.analysisOpen = !this.analysisOpen;
	}

	async getProductsEfficiencyAnalysisData() {
		const stand: any = this.stands[0],
			defaults: any = this.sessionService.defaults,
			date_filters = {
				date_from: moment(defaults.date_to)
					.subtract('30', 'day')
					.format('YYYY-MM-DD'),
				date_to: defaults.date_to
			};

		return await this.productsService.getProductsForEfficiencyAnalysis({
			planogram: Number(stand.planogram_id),
			stand: stand.id,
			products: this.products.map((p) => p.id),
			percents: [
				{ a: 80, b: 15, c: 5 },
				{ a: 80, b: 15, c: 5 },
				{ a: 80, b: 15, c: 5 },
				{ a: 80, b: 15, c: 5 }
			],
			date_from: date_filters.date_from,
			date_to: date_filters.date_to,
			turnover_coef: this.reportParams.turnover,
			profit_coef: this.reportParams.profit,
			qty_coef: this.reportParams.qty,
			receipts_qty_coef: this.reportParams.receipts_qty
		});
	}

	async setNewPlannedPercent(event: any) {
		this.categoriesRecommendedPercent = {};
		this.brandsRecommendedPercent = {};

		event.brands.forEach(
			(b) => (this.brandsRecommendedPercent[b.id] = b.recom)
		);
		event.categories.forEach((c) => {
			this.categoriesRecommendedPercent[c.id] = {
				recom: c.recom || 0,
				childrens: {}
			};
			if (c.childrens.length) {
				c.childrens.forEach(
					(child) =>
						(this.categoriesRecommendedPercent[c.id].childrens[child.id] =
							child.recom || 0)
				);
			}
		});

		this.brandsPanelDataGrouped = event.brandsPanelDataGrouped;

		this.stands.forEach((s) => {
			const conf: any = {
				...s.conf,
				products: this.products.map((p) => {
					return {
						id: p.id,
						planedPercent: p.planedPercent
					};
				}),
				reportParams: this.reportParams,
				productFilters: this.addProductsFilterService.filters,
				brandsRecommendedPercent: this.brandsRecommendedPercent,
				categoriesRecommendedPercent: this.categoriesRecommendedPercent,
				brandsPanelDataGrouped: this.brandsPanelDataGrouped,
				layout_meters: s.stand.layoutMeters
			}
			this.planogramsService.updateStandField(s.id, { conf: conf });
		});
	}

	applyLabelsConfig(values: ILabelsState) {
		this.activity.updateLabelsState(values);
		this.showTags();
		this.activity.closePanel();
	}

	refreshBlockElements(block: Block) {
		block.cache.elements = null;
		return block.elements;
	}

	getProductSizes() {
		return Utils.getProductSizes(this.activity.previewProduct(), 120);
	}

	private updateDragOutsidePreviews(): void {
		if (!this.mainStage || !this.canvasContainer?.nativeElement) return;

		this.dragOutsideService.update({
			items: this.activity.activeProducts().map((pg: ProductGroup) => ({
				key: pg,
				canvasProduct: this.canvasDrawerService.getCanvasProduct(pg)
			})),
			stage: this.mainStage,
			container: this.canvasContainer.nativeElement,
			overCanvasTargets: Array.from(document.querySelectorAll('detailed-element-widget'))
				.filter((el) => el instanceof HTMLElement) as Array<HTMLElement>
		});
	}

	private clearDragOutsidePreviews(): void {
		this.dragOutsideService.clear();
	}

	private getCorrectPoint(behavior: PlanogramViewBehavior, pg: ProductGroup, dragOutside: boolean = false): Point {
		let point: Point = null;

		if (dragOutside) {
			point = behavior.convertCanvasPointToPlanogram(this.dragOutsideService.getDropPoint(pg, this.mainStage));
		}

		if (!point) {
			point = behavior.getPoint(pg, this.canvasDrawerService.getCanvasProduct(pg));
		}

		return point;
	}

	private handleProductGroupDrop(behavior: PlanogramViewBehavior, planogram: IPlanogram, dragOutside: boolean = false): void {
		if (!this.activity.activeProducts().length) {
			this.stopProductMove();
			this.clearDragOutsidePreviews();
			return;
		}
		this.changeCanvasListening(false);
		const activeProductGroups: Array<Array<ProductGroup>> = behavior.getActiveProductGroups(this.activity.activeProducts());

		if (!planogram) return this.dragOutsideService.redrawCanvas$.next();

		for (let productGroup of activeProductGroups) {
			const point: Point = this.getCorrectPoint(behavior, productGroup[0], dragOutside);

			const element: Element = behavior.getAnchorElement(productGroup[0], point);

			if (!element) return this.dragOutsideService.redrawCanvas$.next();

			point.z = behavior.getCorrectZCoordinate(element, point.z, productGroup[0]);
			const result: ICanPutProductsResult = element.canPutProductGroups(productGroup, point);
			if (!result.success) {
				if (result.message) this.snackBarService.setSnackBarNotification(result.message);
				return this.dragOutsideService.redrawCanvas$.next();
			}
		}

		const normalizeList: Array<Element> = activeProductGroups.map((g: Array<ProductGroup>) => g[0].element);

		for (let productGroup of activeProductGroups) {
			const point: Point = this.getCorrectPoint(behavior, productGroup[0], dragOutside);

			const element: Element = behavior.getAnchorElement(productGroup[0], point);

			if (!element) return this.dragOutsideService.redrawCanvas$.next();

			point.z = behavior.getCorrectZCoordinate(element, point.z, productGroup[0]);
			element.putProductGroups(productGroup, activeProductGroups, { ...point });

			let block: Block = planogram.stand.findBlock(point.x, point.y, productGroup[0].Width);

			if (this.activity.selectedBlock() !== block) this.activity.setSelectedBlock(block);

			productGroup.forEach((product: ProductGroup) => normalizeList.push(product.element));
		}

		planogram.stand.shiftProducts(activeProductGroups);

		const changedBlocks: Array<Block> = planogram.stand.blocks
			.filter((block: Block) => block.products.some((pg: ProductGroup) => this.activity.isProductActive(pg)));

		if (changedBlocks.length) {
			changedBlocks.forEach((block: Block) => {
				const blockIndex: number = planogram.stand.blocks.indexOf(block);
				planogram.stand.blocks[blockIndex].updateBlockProducts(
					this.activity.getDependBlockProducts(planogram.stand.blocks[blockIndex])
				);
			});
		}

		planogram.stand.blocks.forEach((block: Block) => block.clearCache());

		const elements: Array<Element> = this.activity.normalizeElements(planogram.stand, Utils.unique(normalizeList));
		this.activity.setNumbersForPerforation$.next(elements);

		const activeElements: Array<ProductGroup> = [...this.activity.activeProducts()];
		activeElements.forEach((pg: ProductGroup, idx: number) => this.activity.setProductActive(pg, !!idx));

		if (this.activity.activeProducts().length > 1 && this.activity.settingsPanel().differentElements) {
			this.activity.closePanel();
		}

		this.activity.requestCanvasUpdateAsync(this.stands, elements.map((el: Element) => el.id)).finally(() => {
			this.stopProductMove(true);
		});
		this.historyGenerate();
		this.setRealProductPercents();
		this.activity.setFocusedCanvas(this.stands[0].stand.id, null, this.mainCanvas.nativeElement);
	}

	isNeedCloseRightPanel() {
		if (this.activity.panelType() == 'layoutrules') return;
		if (this.activity.panelType() == 'brand') return;
		if (this.activity.activeBlocks().length) return;
		if (this.activity.dragProducts().length) return;
		if (this.activity.settingsPanelDisabled()) this.activity.closePanel();
	}

	openInitOnboardingModal(): void {
		if (
			!this.sessionService.hasFeature(ServiceFeatures.Onboarding)
			|| this.sessionService.settings.demoOnboardingPlanogramOpened
		) return;

		this.dialog
			.open<OnboardingModalComponent, IOnboardingModalData>(OnboardingModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					doneLabel: 'ONBOARDING_MODAL.OPENED_PLANOGRAM',
					titleLabel: 'ONBOARDING_MODAL.LAYOUT_PRODUCTS',
					descriptionLabel: 'ONBOARDING_MODAL.LAYOUT_PRODUCTS_DESCRIPTION',
					imageUrl: '/static/img/onboarding/layoutProducts.jpg',
					mainButtonLabel: 'COMMON.ROGER_THAT',
					mixpanelEvent: MixpanelEvents.OnboardingPlacePlanogram
				}
			})
			.afterClosed().subscribe(() => {
				this.sessionService.settings.demoOnboardingPlanogramOpened = true;
				this.sessionService.updateSettings();
			});
	}

	openSaveOnboardingModal(): void {
		if (
			!this.sessionService.hasFeature(ServiceFeatures.Onboarding)
			|| this.sessionService.settings.demoOnboardingPlanogramSaved
		) return;

		this.dialog
			.open<OnboardingModalComponent, IOnboardingModalData>(OnboardingModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					doneLabel: 'ONBOARDING_MODAL.CREATED_PLANOGRAM',
					titleLabel: 'ONBOARDING_MODAL.EXPORT_PLANOGRAM',
					descriptionLabel: 'ONBOARDING_MODAL.EXPORT_PLANOGRAM_DESCRIPTION',
					imageUrl: '/static/img/onboarding/exportPlanogram.jpg',
					secondaryButtonLabel: 'COMMON.DO_IT_LATER',
					mainButtonLabel: 'COMMON.GO_TO_VIEW_MODE',
					mixpanelEvent: MixpanelEvents.OnboardingCreatePlanogram
				}
			})
			.afterClosed().subscribe((result: boolean) => {
				this.sessionService.settings.demoOnboardingPlanogramSaved = true;
				this.sessionService.updateSettings();
				if (result) {
					this.router
						.navigate(
							this.sessionService.getLink(['stand', 'view', this.standId]),
							{ queryParams: { planogram: this.planogramId } }
						)
						.finally();
				}
			});
	}

	showTags() {
		if (this.activity.labelsState().abcTurnover || this.activity.labelsState().abcReceipts) {
			this.getAbcReport();
		} else {
			this.activity.requestCanvasUpdate();
		}
	}

	reverseLayout(): void {
		if (!this.sessionService.hasFeature(ServiceFeatures.LayoutReverse)) {
			this.blockedFunctionalService.openBlockedFunctionalModal(FunctionalType.ReversedLayout);
			return;
		}
		this.isRenderingReversed = true;
		this.stands[0].stand = this.planogramLayoutService.getReversed(this.stands[0].stand);

		this.activity.normalizeElements(this.stands[0].stand, []);
		this.activity.planogramUpdate$.next(this.stands[0]);

		this.historyGenerate();

		this.activity.requestCanvasUpdateAsync().finally(() => {
			if (this.activity.activeProducts().length) this.activity.setProductActive();
			this.changeCanvasListening(true);
			timer(0).subscribe(() => {
				this.isRenderingReversed = false;
			});
		});

		this.mixPanelService.trackPage(MixpanelEvents.MirrorLayoutFromPlanogramEdit);
	}

	getFreeHoles(element: Element): Array<Element> {
		const elementDepend = element.parent.type == 'Hole' ? element.depend : element;
		let sortedHoles = this.stands[0].stand.allElements
			.filter((el) => el.depend == elementDepend && !el.products.length)
			.sort((a, b) =>
				b.shapes[0].begin.y - a.shapes[0].begin.y ||
				a.shapes[0].begin.x - b.shapes[0].begin.x
			);

		if (element.parent.type == 'Hole') {
			const thisHoleIndex: number = sortedHoles.findIndex((el: Element) => el === element);
			sortedHoles = sortedHoles.filter((element, index) => index >= thisHoleIndex);
		}

		return sortedHoles;
	}

	async changeHighlightOption(value: HighlightOptionsValue) {
		this.closePanel();

		this.activity.setHighlightValue(value);
		this.highlightProductsService.setColors(this.products);

		const highlightFunc = this.highlightProductsService.applyHighlight(
			this.products,
			this.standId,
			this.planogramId
		);
		if (highlightFunc[this.activity.highlightValue()]) {
			this.activity.setHighlightedProducts(await highlightFunc[this.activity.highlightValue()]());
			this.mixPanelService.trackPage(
				MixpanelEvents.HighlightProductsOnPlanogramEdit,
				{ type: this.activity.highlightValue() }
			);
		} else {
			this.activity.clearHighlightedProducts();
		}
		setTimeout(async () => await this.activity.requestCanvasUpdateAsync());
	}

	// resize left panel block start
	onResizeStart(event: MouseEvent): void {
		this.isResizing = true;
		this.startX = event.clientX;
		this.startWidth = event.target['parentElement'].clientWidth;

		window.addEventListener('mousemove', this.onResizing);
		window.addEventListener('mouseup', this.onResizeEnd);
	}

	private onResizing = (event: MouseEvent): void => {
		event.preventDefault();

		if (this.isResizing) {
			const movementX: number = event.clientX - this.startX,
				newWidth: number = Math.min(Math.max(this.startWidth + movementX, 420), 1000); // min(), max() need for add products panel
			this.leftResizablePanel.nativeElement.style.width = `${newWidth}px`;
			this.leftPanelSavedWidth = newWidth;
		}
	}

	private onResizeEnd = () => {
		this.recountStandLayoutWidth();
		this.sessionService.defaults.settings.planogramEditLeftPanelWidth = this.leftPanelSavedWidth;
		this.sessionService.updateSettings();
		this.isResizing = false;
		window.removeEventListener('mousemove', this.onResizing);
		window.removeEventListener('mouseup', this.onResizeEnd);
	}
	// resize left panel block end

	arbitraryLayoutHandler(planogram: EquipmentBase): void {
		if (planogram.arbitraryLayout && !this.sessionService.defaults.settings.hideArbitraryWarning) {
			this.dialog
				.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
					width: '700px',
					height: 'auto',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: 'PLANOGRAM_EDIT.ARBITRARY_LAYOUT_CONFIRM',
						twoAnswers: true,
						confirmLabel: 'COMMON.CONFIRM',
						cancelLabel: 'COMMON.CANCEL',
						checkBox: true,
						checkBoxLabel: 'NOT_SHOW_ANYMORE'
					}
				})
				.afterClosed()
				.subscribe((result: IConfirmModalAnswer) => {
					if (!result.answer) return;

					if (result.checkBoxState) {
						this.sessionService.defaults.settings.hideArbitraryWarning = result.answer;
						this.sessionService.updateSettings();
					}

					this.setArbitraryLayout(planogram);
				});
		} else {
			this.setArbitraryLayout(planogram);
		}
	}

	setArbitraryLayout(planogram: EquipmentBase): void {
		planogram.arbitraryLayout = !planogram.arbitraryLayout;

		this.mixPanelService.trackPage(MixpanelEvents.ArbitraryLayout, { status: planogram.arbitraryLayout });

		if (planogram.arbitraryLayout) planogram.alignment = AlignmentTypes.Default;

		this.activity.normalizeElements(planogram);
		this.historyGenerate();
		this.activity.requestCanvasUpdate();
		setTimeout(() => {
			if (this.equipmentModel) void this.equipmentModel.updateScene();
		});
		this.closePanel();
	}

	async saveNewEquipment(): Promise<void> {
		if (!this.saveAble) return;

		const id: number = await this.createEquipment();

		if (!id) {
			this.saveAble = true;
			return;
		}

        this.socketService.deleteSubscription(SocketGroup.EquipmentUsersActivity, { equipment_id: Number(this.stands[0].stand?.id )});
		this.stands[0].stand.id = id;
        this.socketService.addSubscription(SocketGroup.EquipmentUsersActivity, { equipment_id: Number(this.stands[0].stand?.id )});
    
		await this.planogramsService.partialUpdate(this.getSaveConfiguration(), this.planogramId);
		await this.saveAction(true);
		this.equipmentUsage = [this.stands[0].id];
		this.beginState = this.history[this.historyIndex];
		this.toggleEquipmentEditMode();
	}

	async saveCurrentEquipment(): Promise<void> {
		if (!this.saveAble || !this.ignoreOtherEquipmentSave) return;

		const confirmLabel: string = await lastValueFrom<string>(this.translate.get('PLANOGRAMS_LIST.QUANTITY'));

		this.dialog
			.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'EQUIPMENT_EDIT.SAVE_CURRENT_EQUIPMENT_CONFIRM',
					secondLabel: `${confirmLabel}: ${this.equipmentUsage.length}`,
					cancelLabel: 'COMMON.CANCEL',
					confirmLabel: 'REPLACE',
					twoAnswers: true
				}
			})
			.afterClosed()
			.subscribe(async (result: IConfirmModalAnswer) => {
				if (!result.answer) {
					this.saveAble = true;
					return;
				}

				this.loader = true;
				this.mixPanelService.trackPage(MixpanelEvents.EquipmentSaveCurrentFromPlanogram);
				this.saveAble = false;

				await this.saveEquipment(this.stands[0].stand);

				const elementsBefore: { [key: string]: Point } = {};
				const elementsDifference: { [key: string]: Point } = {};

				this.beginState.stands[this.standId].allElements.forEach((el: Element) => {
					elementsBefore[el.id] = el.shapes[0].begin;
				});
				this.stands[0].stand.allElements.forEach((el: Element) => {
					elementsDifference[el.id] = {
						x: Utils.round(el.shapes[0].begin.x - elementsBefore[el.id].x),
						y: Utils.round(el.shapes[0].begin.y - elementsBefore[el.id].y),
						z: Utils.round(el.shapes[0].begin.z - elementsBefore[el.id].z),
					}
				});

				const planograms: Array<IPlanogram> = await lastValueFrom(
					this.planogramsService
						.bulkFetch(
							this.equipmentUsage
								.filter((id: number) => id != this.standId)
								.map((id: number) => {
									return { planogram_id: id, plan_id: null };
								})
						)
						.pipe(
							map((data: Array<IPlanogramData>): Array<IPlanogram> => {
								return data.map((item: IPlanogramData) => {
									return this.planogramLayoutService.getPlanogramObject(
										item,
										[],
										item.planogram_stand[0]?.planogram_id,
										false
									);
								});
							})
						)
				);

				const configurations: Array<StandConf> = planograms.map((planogram: IPlanogram) => {
					planogram.stand.products.forEach((pg: ProductGroup) => {
						if (elementsDifference[pg.element.id].x) {
							pg.begin.x = Utils.round(pg.begin.x + elementsDifference[pg.element.id].x);
						}
						if (elementsDifference[pg.element.id].y) {
							pg.begin.y = Utils.round(pg.begin.y + elementsDifference[pg.element.id].y);
						}
						if (elementsDifference[pg.element.id].z) {
							pg.begin.z = Utils.round(pg.begin.z + elementsDifference[pg.element.id].z);
						}
					});
					planogram.stand.allElements.forEach((el: Element) => {
						if (NORMALIZED_ELEMENTS.includes(el.constructor.name)) el.normalize(planogram.stand);
					});

					const products: Array<IProductConf> = this.productsService.getProductsToConf(
						planogram.id,
						planogram.stand.products
					);

					return this.planogramsService.getConf(planogram, products, {
						id: planogram.id,
						main_stand: planogram.main_stand,
						conf: {
							products: (planogram.conf?.products || []).map((p: any) => ({
								id: p.id,
								planedPercent: p.planedPercent
							}))
						}
					});
				});

				const currentPlanogramProducts: Array<IProductConf> = this.productsService.getProductsToConf(
					this.stands[0].id,
					this.stands[0].stand.products
				);

				const currentPlanogramConfiguration: StandConf = this.planogramsService.getConf(
					this.stands[0],
					currentPlanogramProducts,
					{
						id: this.stands[0].id,
						main_stand: this.stands[0].main_stand,
						conf: {
							products: this.products.map((p: Product) => ({
								id: p.id,
								planedPercent: p.planedPercent
							}))
						}
					}
				);

				configurations.push(currentPlanogramConfiguration);

				await lastValueFrom(this.planogramsService.bulkUpdate(configurations));
				await this.planogramsService.makeSvg(
					this.planogramsService.getDefaultSvgConf([...planograms, this.stands[0]])
				);

				const planogramsWithErrors: Array<IPlanogram> = [...planograms, this.stands[0]]
					.filter((planogram: IPlanogram) => planogram.stand.products.find((pg: ProductGroup) => pg.error))
					.filter((planogram: IPlanogram) => planogram.planogram_stand?.length);

				if (planogramsWithErrors.length) {
					const errorIndicators: Array<any> = planogramsWithErrors
						.filter((planogram: IPlanogram) => planogram.id != this.stands[0].id)
						.map((planogram: IPlanogram) => {
							return {
								planogram: planogram.id,
								name: 'EQUIPMENT_CHANGED',
								type: 'ERROR'
							};
						});

					if (errorIndicators.length) {
						this.planogramsService.createIndicators(errorIndicators).subscribe();
					}

					this.notificationsService.createEquipmentEditNotification({
						equipment_id: this.stands[0].stand.id,
						planograms: planogramsWithErrors.map((item: IPlanogram) => ({
							plan_id: item.planogram_id,
							planogram_id: item.id
						}))
					}).subscribe();
				}

				this.beginState = this.history[this.historyIndex];
				this.loader = false;
				this.toggleEquipmentEditMode();
			});
	}

	cancelEquipmentChanges(): void {
		this.mixPanelService.trackPage(MixpanelEvents.EquipmentEditCancel);
		this.historyApply(this.beginState).then(() => this.toggleEquipmentEditMode());
	}

	async createEquipment(): Promise<number> {
		const dialogRef = this.dialog.open(EquipmentCreateNameModalComponent, {
			width: '450px',
			autoFocus: true,
			backdropClass: 'back-drop'
		});

		const result = await lastValueFrom(dialogRef.afterClosed());

		if (!result) return null;

		this.mixPanelService.trackPage(MixpanelEvents.EquipmentSaveAsNewFromPlanogram);
		await this.saveTemplate.updateScene();
		this.saveTemplate.render();

		const configurations: IEquipmentConfigurations = EquipmentService
			.getEquipmentConfigurations(this.stands[0].stand);

		const image = this.saveTemplate.getImage();
		const payload: any = {
			name: result.name,
			code: this.stands[0].stand.code,
			producer: this.stands[0].stand.producer,
			empty: false,
			linear_conf: configurations.linearConf,
			conf: configurations.conf,
			new_conf: configurations.newConf,
			type: this.stands[0].stand.type
		};

		if (image) {
			payload.image = image;
		}

		this.loader = true;
		const newEquipment: IEquipment = await lastValueFrom(this.equipmentService.create(payload));
		this.loader = false;

		return newEquipment.id;
	}

	async saveEquipment(equipment: EquipmentBase): Promise<void> {
		const configurations: IEquipmentConfigurations = EquipmentService.getEquipmentConfigurations(equipment);

		await this.saveTemplate.updateScene();
		this.saveTemplate.render();

		const equipmentImage = this.saveTemplate.getImage();
		
		const updateData: any = {
			empty: false,
			linear_conf: configurations.linearConf,
			conf: configurations.conf,
			new_conf: configurations.newConf
		};

		if (equipmentImage) {
			updateData.image = equipmentImage;
		}

		await lastValueFrom(this.equipmentService.update(equipment.id, updateData));
	}

	getSaveConfiguration(): StandConf {
		const productsConfiguration: Array<IProductConf> = this.productsService
			.getProductsToConf(this.stands[0].id, this.stands[0].stand.products);

		return this.planogramsService.getConf(this.stands[0], productsConfiguration, {
			id: this.stands[0].id,
			main_stand: this.stands[0].main_stand,
			blocks: this.stands[0].stand.blocks.map((block: Block) => block.toJSON()),
			view_mode: this.activity.focusedCanvas().element ? 'top': 'direct',
			element_id: this.activity.focusedCanvas().element ?? undefined,
			size_mode: this.modes.sizesMode(),
			conf: {
				products: this.products.map((p: Product) => ({ id: p.id, planedPercent: p.planedPercent })),
				reportParams: this.reportParams,
				productFilters: this.addProductsFilterService.filters,
				selectedColumns: this.stands[0].conf.selectedColumns || [],
				brandsRecommendedPercent: this.brandsRecommendedPercent,
				categoriesRecommendedPercent: this.categoriesRecommendedPercent
			}
		});
	}

	@HostListener('window:touchend', ['$event']) canvasTouchEnd(event: TouchEvent) {
		this.canvasUp(event.changedTouches[0]);
	}

	@HostListener('window:focus') onTabOpen(): void {
		if (this.mainStage) this.mainStage.batchDraw();
	}

	@HostListener('window:resize') windowResize() {
		this.recountStandLayoutWidth();
	}

	// обробка руху мишки по вікні
	@HostListener('window:mousemove', ['$event']) canvasMouseMove(event: MouseEvent) {
		this.canvasMove(event);
	}

	@HostListener('window:touchmove', ['$event']) canvasMouseDrag(event: TouchEvent) {
		this.canvasMove(event.changedTouches[0]);
	}

	@HostListener('window:keyup', ['$event']) onKeyboardMoveEnd(event: KeyboardEvent): void {
		if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
		if (!this.needCanvasResizeAfterKeyboardMove) return;

		this.needCanvasResizeAfterKeyboardMove = false;
		void this.canvasUpdate();
	}
}
